<script>
  import Vue from 'vue';
  import conciliacaoProjetoServico from '../servico';
  import moment from 'moment';
  import DatePicker from 'vue2-datepicker';
  import VueSweetAlert from 'vue-sweetalert';
  import SelecaoBusca from './SelecaoBusca.vue';
  import RegularizarModal from './RegularizarModal.vue';
  import RegularizarTransitoriaModal from './RegularizarTransitoriaModal.vue';
  import ExtratosContaModal from './ExtratosContaModal.vue';
  import LogConciliacaoModal from './LogConciliacaoModal.vue';
  import permissoesConciliacao from '~js/permissoes-conciliacao';

  Vue.use(VueSweetAlert);

  const TOLERANCIA = 0.005;
  const LIMITE_AJUSTE_DIFERENCA = 3;
  const BLOCO_LINHAS = 250;

  const texto = v => (v || '').toString().toUpperCase();
  const COLUNAS_GRADE = [
    { chave: 'id', texto: 'ID', valor: l => (l.idExtrato > 0 ? l.idExtrato : l.idLancamento) || 0 },
    { chave: 'data', texto: 'Data', valor: l => +l.dataInt || 0 },
    { chave: 'historico', texto: 'Histórico', valor: l => texto(l.historico) },
    { chave: 'valor', texto: 'Valor', valor: l => +l.valor || 0, classe: 'tr' },
    { chave: 'cpfcnpj', texto: 'CPF/CNPJ', valor: l => texto(l.cpfcnpj).replace(/[^A-Z0-9]/g, '') },
    { chave: 'cliente', texto: 'Cliente / Fornecedor', valor: l => texto(l.nomePessoa) },
    { chave: 'forma', texto: 'Forma', valor: l => texto(l.formaPagBanco) },
    { chave: 'bordero', texto: 'Borderô', valor: l => +l.idBordero || 0 },
    { chave: 'documento', texto: 'Documento', valor: l => texto(l.documento || l.documentoReceberPagar || l.notaFiscal) },
    { chave: 'docBanco', texto: 'Doc. banco', valor: l => texto(l.documentoBanco) },
    { chave: 'complemento', texto: 'Complemento', valor: l => texto(l.complemento) },
    { chave: 'rubrica', texto: 'Rubrica', valor: l => texto(l.nomeRubrica) },
    { chave: 'subrubrica', texto: 'Subrubrica', valor: l => texto(l.nomeSubrubrica) },
    { chave: 'conta', texto: 'Conta', valor: l => texto(l.nomeConta) },
    { chave: 'projeto', texto: 'Projeto', valor: l => texto(l.nomeProjeto), soProjeto: true },
    { chave: 'observacao', texto: 'Observação', valor: l => texto(l.observacao) },
  ];

  const CORES_GRUPO_EXTRATO = ['#c47f00', '#0e7c86', '#7a4fc4', '#c2410c', '#2f6fb2', '#4d7c0f'];

  const chaveCambioLinha = l => {
    const d = (l.documento || '').replace(/\s/g, '').toUpperCase();
    return d.indexOf('GDC') === 0 && d.length > 3 ? d : '';
  };
  const chaveBlocoExtrato = l => (l.documentoBanco || '').trim();

  const GradeConciliacao = {
    props: {
      linhas: { type: Array, default: () => [] },
      lado: { type: String, required: true },
      titulo: { type: String, default: '' },
      candidatoValor: { type: Number, default: 0 },
      candidatoChaves: { type: Array, default: () => [] },
      vinculosAtivos: { type: Array, default: () => [] },
      grupoAtivo: { type: Number, default: 0 },
      mostrarProjeto: { type: Boolean, default: false },
      historicoComComplemento: { type: Boolean, default: false },
      exigirConferido: { type: Boolean, default: false },
      permitirBuscaOutras: { type: Boolean, default: false },
      permitirRegularizar: { type: Boolean, default: false },
      regularizarDebito: { type: Boolean, default: false },
      filtroValor: { type: Number, default: null },
      lupa: { type: Object, default: null },
      restanteServidor: { type: Number, default: 0 },
      carregandoServidor: { type: Boolean, default: false },
    },
    data() {
      return { limiteVisivel: BLOCO_LINHAS, ordem: null };
    },
    computed: {
      colunas() { return COLUNAS_GRADE.filter(c => !c.soProjeto || this.mostrarProjeto); },
      linhasOrdenadas() {
        const coluna = this.ordem && COLUNAS_GRADE.find(c => c.chave === this.ordem.coluna);
        if (!coluna) return this.linhas;
        const sentido = this.ordem.crescente ? 1 : -1;
        return this.linhas.slice().sort((a, b) => {
          const va = coluna.valor(a);
          const vb = coluna.valor(b);
          const r = typeof va === 'number' ? va - vb : va.localeCompare(vb, 'pt-BR');
          return (r || (+a.dataInt || 0) - (+b.dataInt || 0) || (a.idLancamento || 0) - (b.idLancamento || 0)) * sentido;
        });
      },
      linhasVisiveis() { return this.linhasOrdenadas.length > this.limiteVisivel ? this.linhasOrdenadas.slice(0, this.limiteVisivel) : this.linhasOrdenadas; },
      ocultas() { return Math.max(0, this.linhas.length - this.limiteVisivel); },
      total() { return this.linhas.reduce((s, l) => s + l.valor, 0); },
      qtde() { return this.linhas.length; },
      pendentes() { return this.linhas.filter(l => l.conciliado !== 1).length; },
      todosMarcados() {
        const livres = this.linhas.filter(l => l.conciliado !== 1 && !this.bloqueada(l));
        return livres.length > 0 && livres.every(l => l.selecionado);
      },
      docLabel() { return this.lado === 'direita' ? 'DOC. BANCO' : 'DOCUMENTO'; },
    },
    methods: {
      formatar(v) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
      formatarDoc(v) {
        const d = (v || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (d.length === 14) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12);
        if (d.length === 11) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9);
        return v || '';
      },
      raizDoc(v) {
        const d = (v || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (d.length === 14) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8);
        return this.formatarDoc(v);
      },
      dicaBuscaDoc(v) {
        const d = (v || '').replace(/[^A-Za-z0-9]/g, '');
        return d.length === 14 ? 'Filtrar pela raiz do CNPJ — ' + this.raizDoc(v) + ' (matriz e filiais)' : 'Filtrar por este CPF/CNPJ';
      },
      dicaBuscaOutras(l) {
        return 'Buscar contas a receber em aberto de outras contas com este ' + (l.cpfcnpj ? 'CPF/CNPJ' : 'nome');
      },
      rotuloSituacao(l) {
        if (l.crOutraConta === 1) return 'Conta a receber de outra conta — ' + (l.nomeConta || 'conta de origem') + '. Ao conciliar, a baixa transfere o lançamento para esta conta.';
        if (l.executado !== 1) return l.creditoDebito === 1 ? 'Conta a receber — em aberto' : 'Conta a pagar — em aberto';
        if (this.exigirConferido) {
          return l.conferido === 1
            ? 'Baixado e conciliado no banco'
            : 'Baixado, mas ainda não conciliado no banco';
        }
        return 'Baixado';
      },
      classeSituacao(l) {
        if (l.crOutraConta === 1) return 'sit-outra';
        if (l.executado !== 1) return 'sit-aberto';
        if (this.exigirConferido) return l.conferido === 1 ? 'sit-conf' : 'sit-naoconf';
        return 'sit-exec';
      },
      bloqueada(l) { return this.exigirConferido && this.lado === 'direita' && l.conferido !== 1; },
      docValor(l) { return this.lado === 'direita' ? (l.documentoBanco || l.documento || '') : (l.documento || ''); },
      dicaDocumento(l) {
        const partes = [];
        if (l.documentoReceberPagar) partes.push('Doc. a receber/pagar: ' + l.documentoReceberPagar);
        if (l.notaFiscal) partes.push('Nota fiscal: ' + l.notaFiscal);
        return partes.join(' · ');
      },
      dicaBanco(l) {
        if (!l.concBancoGrupo) return '';
        const qtde = l.concBancoQtdeExtrato || 0;
        const quando = l.concBancoData ? ' em ' + l.concBancoData : '';
        if (qtde === 1) return 'Já conciliado no banco' + quando + (l.concBancoDocumento ? ' · documento ' + l.concBancoDocumento : '') + '.';
        if (qtde === 0) return 'Já conciliado no banco' + quando + ' · sem documento do banco (pago em outra conta).';
        return 'Já conciliado no banco' + quando + ' · ' + qtde + ' documentos do banco no mesmo grupo — confira antes de conciliar no projeto.';
      },
      estado(l) {
        if (this.grupoAtivo !== 0 && l.idGrupo === this.grupoAtivo && l.conciliado === 1) return 'vinculo';
        if (l.conciliado === 1) return l.tipoConc === 1 ? 'auto' : (l.tipoConc === 4 ? 'just' : 'manual');
        if (l.selecionado) return 'sel';
        if (l.idLancamento > 0 && this.vinculosAtivos.indexOf(l.idLancamento) >= 0) return 'cand';
        if (this.candidatoValor !== 0 && Math.abs(l.valor - this.candidatoValor) <= 0.005 && this.noBlocoCandidato(l)) return 'cand';
        if (l.sugestao === 1) return 'sug';
        return 'pend';
      },
      dicaSugestao(l) {
        if (l.sugestao !== 1) return '';
        if (l.sugestaoGrupo > 0) {
          return 'Grupo sugerido · ' + (l.sugestaoGrupoDescricao || '')
               + '\n' + l.sugestaoGrupoQtdeEsq + ' lançamento(s) da conta somam ' + l.sugestaoGrupoQtdeDir + ' linha(s) do extrato.'
               + '\nClique para marcar o grupo inteiro dos dois lados. Nada é gravado até você conciliar.';
        }
        const nivel = { alta: 'confiança alta', media: 'confiança média', baixa: 'confiança baixa' }[l.sugestaoNivel] || 'confiança baixa';
        const criterio = { conta_receber: 'conta a receber', llm: 'histórico (IA)', historico: 'histórico',
                           equivalente: 'único lançamento equivalente' }[l.sugestaoCriterio] || l.sugestaoCriterio;
        const disputa = l.sugestaoConcorrentes > 1
          ? '\n' + l.sugestaoConcorrentes + ' linhas do extrato apontam este mesmo lançamento — só uma pode conciliar.'
          : '';
        const herdada = l.sugestaoCriterio === 'equivalente'
          ? '\nA IA apontou um lançamento que já foi conciliado — este é o único pendente com mesma data e valor.'
          : '';
        return 'Sugestão da IA · ' + nivel + (criterio ? ' · ' + criterio : '') + disputa + herdada
             + '\nClique para marcar o par dos dois lados. Nada é gravado até você conciliar.';
      },
      ordenarPor(chave) {
        if (this.ordem && this.ordem.coluna === chave) this.ordem = { coluna: chave, crescente: !this.ordem.crescente };
        else this.ordem = { coluna: chave, crescente: true };
        this.limiteVisivel = BLOCO_LINHAS;
      },
      iconeOrdem(chave) {
        if (!this.ordem || this.ordem.coluna !== chave) return 'fa fa-sort ord-inativo';
        return this.ordem.crescente ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
      },
      dicaOrdem(c) {
        if (!this.ordem || this.ordem.coluna !== c.chave) return 'Ordenar por ' + c.texto.toLowerCase();
        return 'Ordenado por ' + c.texto.toLowerCase() + (this.ordem.crescente ? ' (crescente)' : ' (decrescente)') + ' — clique para inverter';
      },
      mostrarMais(quantidade = BLOCO_LINHAS) { this.limiteVisivel += quantidade; },
      mostrarTodas() { this.limiteVisivel = this.linhas.length; },
      garantirVisivel(indice) {
        if (indice >= this.limiteVisivel) this.limiteVisivel = indice + BLOCO_LINHAS;
      },
      sugerir(l) { this.$emit('sugestao', { lado: this.lado, linha: l }); },
      temCheck(l) {
        const e = this.estado(l);
        return e === 'sel' || e === 'auto' || e === 'manual' || e === 'just' || e === 'cand' || e === 'vinculo';
      },
      toggle(l) {
        if (l.conciliado === 1) return;
        if (this.bloqueada(l)) this.$emit('bloqueada', l);
        this.$emit('toggle', { lado: this.lado, linha: l });
      },
      ativar(l) { this.$emit('ativar', { lado: this.lado, linha: l }); },
      buscar(texto, linha) { this.$emit('buscar', { texto, creditoDebito: linha.creditoDebito }); },
      valorFiltrado(l) { return this.filtroValor != null && Math.abs(l.valor - this.filtroValor) <= TOLERANCIA; },
      alternarFiltroValor(l) { this.$emit('filtrar-valor', { valor: l.valor, lado: this.lado }); },
      dicaFiltroValor(l) {
        return this.valorFiltrado(l)
          ? 'Filtro por valor ativo — clique para voltar a mostrar todos os lançamentos'
          : 'Filtrar os dois lados por este valor';
      },
      buscarOutras(texto, linha) { this.$emit('buscar-outras', { texto, creditoDebito: linha.creditoDebito }); },
      lupaAtiva(tipo, texto) { return !!this.lupa && this.lupa.tipo === tipo && this.lupa.texto === texto; },
      iconeLupa(tipo, texto) { return this.lupaAtiva(tipo, texto) ? 'fa-times' : (tipo === 'outras' ? 'fa-search-plus' : 'fa-search'); },
      dicaLupa(tipo, texto, dica) { return this.lupaAtiva(tipo, texto) ? 'Filtro desta lupa ativo — clique para desfazer' : dica; },
      info(l) { this.$emit('info', l); },
      vinculos(l) { this.$emit('vinculos', l); },
      dicaVinculo(l) {
        return l.vinculoTransitoria === 1
          ? 'Em transição de conciliação — ver os lançamentos vinculados'
          : 'Vínculo concluído — ver os lançamentos vinculados';
      },
      extrasHistorico(l) {
        if (!this.historicoComComplemento) return this.documentoDoExtrato(l);
        const documento = l.documento || l.documentoReceberPagar;
        return [
          (l.complemento || '').trim(),
          documento ? 'Doc ' + documento : '',
          l.notaFiscal ? 'NF ' + l.notaFiscal : '',
        ].filter(x => x).join(' · ');
      },
      documentoDoExtrato(l) {
        const documento = l.idExtrato > 0 ? (l.documento || '').trim() : '';
        return documento && (l.historico || '').indexOf(documento) < 0 ? 'Doc ' + documento : '';
      },
      historicoCompleto(l) {
        const extras = this.extrasHistorico(l);
        return extras ? (l.historico || '') + ' · ' + extras : l.historico;
      },
      podeRegularizar(l) {
        if (this.regularizarDebito) return l.creditoDebito === 2 && l.conciliado !== 1;
        return this.permitirRegularizar && l.idExtrato > 0 && l.conciliado !== 1;
      },
      corGrupoExtrato(l) {
        return l.grupoExtrato > 0 && l.conciliado !== 1 ? CORES_GRUPO_EXTRATO[(l.grupoExtrato - 1) % CORES_GRUPO_EXTRATO.length] : '';
      },
      estiloLinhaGrupo(l) {
        const cor = this.corGrupoExtrato(l);
        return cor ? { boxShadow: 'inset 4px 0 0 ' + cor } : null;
      },
      estiloTagGrupo(l) {
        const cor = this.corGrupoExtrato(l);
        return { color: cor, borderColor: cor, background: cor + '14' };
      },
      dicaRegularizar(l) {
        if (this.regularizarDebito) return 'Regularizar este débito — contas a receber, transferência ou os dois';
        if (l.grupoExtrato > 0)
          return 'Regularizar o grupo: ' + l.grupoExtratoDescricao + ' · R$ ' + this.formatar(l.grupoExtratoValor) + ' — um lançamento para todas as linhas';
        return 'Regularizar esta linha — transferência, aplicação, resgate ou crédito a identificar';
      },
      regularizar(l) { this.$emit('regularizar', l); },
      desfazer(l) { this.$emit('desfazer', l); },
      observar(l) { this.$emit('observar', { lado: this.lado, linha: l }); },
      marcarTodos(e) { this.$emit('marcar-todos', { lado: this.lado, marca: e.target.checked }); },
      noBlocoCandidato(l, chaves = this.candidatoChaves) {
        if (!chaves || chaves.length === 0) return true;
        const chave = this.lado === 'direita' ? chaveBlocoExtrato(l) : chaveCambioLinha(l);
        return chaves.indexOf(chave) >= 0;
      },
      scrollToValor(valor, chaves = []) {
        if (!valor) return;
        this.scrollParaIndice(this.linhasOrdenadas.findIndex(l => l.conciliado !== 1 && Math.abs(l.valor - valor) <= 0.005 && this.noBlocoCandidato(l, chaves)));
      },
      scrollToConciliado(idGrupo, valor) {
        let idx = -1;
        if (idGrupo) idx = this.linhasOrdenadas.findIndex(l => l.conciliado === 1 && l.idGrupo === idGrupo);
        if (idx < 0 && valor) idx = this.linhasOrdenadas.findIndex(l => l.conciliado === 1 && Math.abs(l.valor - valor) <= 0.005);
        this.scrollParaIndice(idx);
      },
      scrollToSelecionado() {
        this.scrollParaIndice(this.linhasOrdenadas.findIndex(l => l.selecionado));
      },
      scrollParaIndice(idx) {
        if (idx < 0) return;
        this.garantirVisivel(idx);
        this.$nextTick(() => {
          const cont = this.$refs.scroll;
          if (!cont) return;
          const rows = cont.querySelectorAll('.grid-row');
          const row = rows[idx];
          if (row) cont.scrollTop = Math.max(0, row.offsetTop - 34);
        });
      },
      emitirScrollX() {
        if (this._ignorarX) return;
        const cont = this.$refs.scroll;
        if (!cont) return;
        this.$emit('scroll-x', { lado: this.lado, x: cont.scrollLeft });
      },
      sincronizarX(x) {
        const cont = this.$refs.scroll;
        if (!cont) return;
        const alvo = Math.min(x, Math.max(0, cont.scrollWidth - cont.clientWidth));
        if (Math.abs(cont.scrollLeft - alvo) < 1) return;
        this._ignorarX = true;
        clearTimeout(this._timerX);
        this._timerX = setTimeout(() => { this._ignorarX = false; }, 80);
        cont.scrollLeft = alvo;
      },
    },
    watch: {
      linhas() { this.limiteVisivel = BLOCO_LINHAS; },
    },
    mounted() {
      this._ignorarX = false;
      this._timerX = null;
      if (this.$refs.scroll) this.$refs.scroll.addEventListener('scroll', this.emitirScrollX, { passive: true });
    },
    beforeDestroy() {
      clearTimeout(this._timerX);
      if (this.$refs.scroll) this.$refs.scroll.removeEventListener('scroll', this.emitirScrollX);
    },
    template: `
      <div class="painel">
        <div class="painel-head">
          <div>
            <div class="painel-tit">{{ titulo }}</div>
            <div class="painel-res">{{ qtde }} lançamentos <span v-if="pendentes > 0">· <span class="pend">{{ pendentes }} pendente<span v-if="pendentes > 1">s</span></span> </span>· R$ {{ formatar(total) }}</div>
          </div>
          <label class="sel-todos"><input type="checkbox" :checked="todosMarcados" @change="marcarTodos"> Selecionar todos</label>
        </div>
        <div class="painel-scroll" ref="scroll">
          <div class="grid-conc grid-head" :class="{ 'grid-proj': mostrarProjeto }">
            <div></div><div></div><div></div><div v-for="c in colunas" :key="c.chave" class="col-ord" :class="c.classe" :title="dicaOrdem(c)" @click="ordenarPor(c.chave)">{{ c.texto }}<i :class="iconeOrdem(c.chave)"></i></div>
          </div>
          <div class="grid-conc grid-row" :class="['st-' + estado(l), { 'grid-proj': mostrarProjeto, 'row-bloq': bloqueada(l) && l.conciliado !== 1, 'row-cr-outra': l.crOutraConta === 1 }]" v-for="l in linhasVisiveis" :key="lado + '-' + l.idLancamento + '-' + l.idLancRateio + '-' + l.idExtrato"
               @click="ativar(l)" @dblclick="toggle(l)" :title="l.conciliado === 1 ? '' : (bloqueada(l) ? rotuloSituacao(l) : 'Duplo-clique para selecionar')">
            <div class="c-ind" :style="estiloLinhaGrupo(l)"><span class="circ" :class="'circ-' + estado(l)"><svg v-if="temCheck(l)" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
            <div class="c-info">
              <a v-if="l.vinculoTransitoria > 0" href="#" class="ic ic-vinc" :title="dicaVinculo(l)" @click.prevent.stop="vinculos(l)"><i class="fa fa-usd"></i></a>
              <a href="#" class="ic" title="Informações do lançamento" @click.prevent.stop="info(l)"><i class="fa fa-info-circle"></i></a>
              <a v-if="l.sugestao === 1" href="#" class="ic ic-sug" :class="'sug-' + (l.sugestaoNivel || 'baixa')" :title="dicaSugestao(l)" @click.prevent.stop="sugerir(l)"><i class="fa fa-magic"></i></a>
              <a v-if="podeRegularizar(l)" href="#" class="ic ic-reg" :class="{ 'ic-reg-grupo': l.grupoExtrato > 0 }"
                 :title="dicaRegularizar(l)"
                 @click.prevent.stop="regularizar(l)"><i class="fa fa-random"></i></a>
            </div>
            <div class="c-sit"><span class="sit-mk" :class="classeSituacao(l)" :title="rotuloSituacao(l)"></span></div>
            <div class="mono muted">{{ l.idExtrato > 0 ? l.idExtrato : l.idLancamento }}</div>
            <div class="mono nowrap">{{ l.dataStr }}<i v-if="l.dataMovimentoBancoStr" class="fa fa-history data-retro" :title="'Lançamento retroativo: o banco o mostrou no extrato de ' + l.dataMovimentoBancoStr + ', com data de balancete ' + l.dataStr + '.'"></i></div>
            <div class="clamp2" :title="historicoCompleto(l)"><div class="clamp2-in"><span v-if="l.grupoExtrato > 0 && l.conciliado !== 1" class="tag-grupo-ext" :style="estiloTagGrupo(l)" :title="'Grupo ' + l.grupoExtrato + ': ' + l.grupoExtratoDescricao + ' · R$ ' + formatar(l.grupoExtratoValor)"><i class="fa fa-object-group"></i> G{{ l.grupoExtrato }} · {{ l.grupoExtratoQtde }}</span>{{ l.historico }}<span v-if="extrasHistorico(l)" class="hist-compl"> · {{ extrasHistorico(l) }}</span></div></div>
            <div class="mono valor tr cel-valor" :class="[l.valor < 0 ? 'neg' : 'pos', { 'cel-valor-on': valorFiltrado(l) }]">
              <span class="cel-txt">R$ {{ formatar(l.valor) }}</span>
              <a href="#" class="ic-valor" :class="{ ativo: valorFiltrado(l) }" :title="dicaFiltroValor(l)" @click.prevent.stop="alternarFiltroValor(l)"><i class="fa" :class="valorFiltrado(l) ? 'fa-times' : 'fa-filter'"></i></a>
            </div>
            <div class="mono muted nowrap cel-busca cel-busca-ini">
              <a v-if="l.cpfcnpj" href="#" class="ic-busca" :class="{ ativo: lupaAtiva('busca', raizDoc(l.cpfcnpj)) }" :title="dicaLupa('busca', raizDoc(l.cpfcnpj), dicaBuscaDoc(l.cpfcnpj))" @click.prevent.stop="buscar(raizDoc(l.cpfcnpj), l)"><i class="fa" :class="iconeLupa('busca', raizDoc(l.cpfcnpj))"></i></a>
              <a v-if="l.cpfcnpj && permitirBuscaOutras" href="#" class="ic-busca ic-busca-mais" :class="{ ativo: lupaAtiva('outras', raizDoc(l.cpfcnpj)) }" :title="dicaLupa('outras', raizDoc(l.cpfcnpj), dicaBuscaOutras(l))" @click.prevent.stop="buscarOutras(raizDoc(l.cpfcnpj), l)"><i class="fa" :class="iconeLupa('outras', raizDoc(l.cpfcnpj))"></i></a>
              <span class="cel-txt">{{ formatarDoc(l.cpfcnpj) }}</span>
            </div>
            <div class="clamp2 cel-busca cel-busca-ini" :title="l.nomePessoa">
              <a v-if="l.nomePessoa" href="#" class="ic-busca" :class="{ ativo: lupaAtiva('busca', l.nomePessoa) }" :title="dicaLupa('busca', l.nomePessoa, 'Filtrar por este cliente / fornecedor')" @click.prevent.stop="buscar(l.nomePessoa, l)"><i class="fa" :class="iconeLupa('busca', l.nomePessoa)"></i></a>
              <a v-if="l.nomePessoa && permitirBuscaOutras" href="#" class="ic-busca ic-busca-mais" :class="{ ativo: lupaAtiva('outras', l.nomePessoa) }" :title="dicaLupa('outras', l.nomePessoa, 'Buscar contas a receber em aberto de outras contas deste cliente / fornecedor')" @click.prevent.stop="buscarOutras(l.nomePessoa, l)"><i class="fa" :class="iconeLupa('outras', l.nomePessoa)"></i></a>
              <div class="clamp2-in">{{ l.nomePessoa }}</div>
            </div>
            <div class="muted">{{ l.formaPagBanco }}</div>
            <div class="mono muted" :title="l.idBordero > 0 ? 'Borderô ' + l.idBordero : ''">{{ l.idBordero > 0 ? l.idBordero : '' }}</div>
            <div class="mono" :class="{ muted: !l.documento }" :title="dicaDocumento(l)">{{ l.documento || l.documentoReceberPagar || l.notaFiscal }}</div>
            <div class="mono doc-banco">
              <span>{{ l.documentoBanco }}</span>
              <i v-if="l.concBancoGrupo > 0" class="fa fa-university dica-banco" :class="{ 'dica-parcial': l.concBancoQtdeExtrato !== 1 }" :title="dicaBanco(l)"></i>
            </div>
            <div class="clamp2 muted" :title="l.complemento"><div class="clamp2-in">{{ l.complemento }}</div></div>
            <div class="clamp2 muted" :title="l.nomeRubrica"><div class="clamp2-in">{{ l.nomeRubrica }}</div></div>
            <div class="clamp2 muted" :title="l.nomeSubrubrica"><div class="clamp2-in">{{ l.nomeSubrubrica }}</div></div>
            <div class="clamp2 muted" :title="l.nomeConta"><div class="clamp2-in">{{ l.nomeConta }}</div></div>
            <div v-if="mostrarProjeto" class="clamp2 muted" :title="l.nomeProjeto"><div class="clamp2-in">{{ l.nomeProjeto }}</div></div>
            <div class="obs-cell">
              <span class="obs-txt" :title="l.observacao">{{ l.observacao }}</span>
            </div>
          </div>
          <div v-if="linhas.length === 0" class="grid-vazio">Nenhum lançamento no período</div>
        </div>
        <div v-if="ocultas > 0 || restanteServidor > 0" class="grid-mais">
            <template v-if="ocultas > 0">
              Mostrando {{ limiteVisivel }} de {{ qtde }} lançamentos
              <button type="button" @click="mostrarMais()">Mostrar mais {{ Math.min(ocultas, 250) }}</button>
              <button type="button" @click="mostrarTodas()">Mostrar todos ({{ ocultas }} restantes)</button>
            </template>
            <template v-else>Mostrando os {{ qtde }} lançamentos carregados</template>
            <button v-if="restanteServidor > 0" type="button" class="btn-servidor" :disabled="carregandoServidor" @click="$emit('carregar-servidor')">
              <i class="fa" :class="carregandoServidor ? 'fa-spinner fa-pulse' : 'fa-download'"></i>
              Buscar mais 5.000 no servidor ({{ restanteServidor.toLocaleString('pt-BR') }} fora)
            </button>
        </div>
      </div>`,
  };

  const RodapeVinculo = {
    props: {
      qtde: { type: Number, default: 0 },
      total: { type: Number, default: 0 },
      qtdeMarcados: { type: Number, default: 0 },
      somaMarcados: { type: Number, default: 0 },
      extrato: { type: Number, default: null },
      colunasAntes: { type: Number, required: true },
      colunasDepois: { type: Number, default: 0 },
    },
    methods: {
      formatar(v) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
      classe(v) { return v < 0 ? 'neg' : 'pos'; },
    },
    template: `
      <tfoot class="vinc-calc">
        <tr>
          <td :colspan="colunasAntes" class="tr">{{ qtde > 1 ? 'Total (' + qtde + ' linhas)' : 'Total' }}</td>
          <td class="tr mono" :class="classe(total)">R$ {{ formatar(total) }}</td>
          <td v-if="colunasDepois" :colspan="colunasDepois"></td>
        </tr>
        <template v-if="qtde > 1">
          <tr v-if="qtdeMarcados === 0">
            <td :colspan="colunasAntes + 1 + colunasDepois" class="muted vinc-calc-dica">Marque as linhas para somar.</td>
          </tr>
          <template v-else>
            <tr class="vinc-calc-marc">
              <td :colspan="colunasAntes" class="tr">Soma dos marcados ({{ qtdeMarcados }})</td>
              <td class="tr mono" :class="classe(somaMarcados)">R$ {{ formatar(somaMarcados) }}</td>
              <td v-if="colunasDepois" :colspan="colunasDepois"></td>
            </tr>
            <tr v-if="extrato !== null" class="vinc-calc-dif">
              <td :colspan="colunasAntes" class="tr">Diferença (extrato − marcados)</td>
              <td class="tr mono" :class="Math.abs(extrato - somaMarcados) < 0.005 ? 'pos' : 'neg'">R$ {{ formatar(extrato - somaMarcados) }}</td>
              <td v-if="colunasDepois" :colspan="colunasDepois"></td>
            </tr>
          </template>
        </template>
      </tfoot>`,
  };

  export default {
    components: { DatePicker, GradeConciliacao, RodapeVinculo, SelecaoBusca, RegularizarModal, RegularizarTransitoriaModal, ExtratosContaModal, LogConciliacaoModal },
    data() {
      return {
        mostrarExtratosConta: false,
        logConciliacao: null,
        idProjetoCript: this.$route.params.idContaCript || this.$route.params.idProjetoCript,
        nomeProjeto: '',
        nomeContaPadrao: '',
        contaPadraoId: 0,
        totalConta: 0,
        conciliadoConta: 0,
        numeroBanco: '',
        nomeBanco: '',
        numeroAgencia: '',
        numeroConta: '',
        saldoTesouraria: null,
        dataReferenciaTesouraria: '',
        dataConciliadora: '',
        tesourariaRecemLida: false,
        vigenciaInicio: '',
        vigenciaFim: '',
        encerrado: false,
        contaLivre: false,
        semPrestacaoExterna: false,
        dataIni: null,
        dataFim: null,
        esquerda: [],
        direita: [],
        processado: false,
        carregando: true,
        desfazendo: false,
        salvando: false,
        timerProcessar: null,
        ultimaAtualizacao: '',
        soNaoConciliados: false,
        ocultarAplicacao: false,
        filtroForma: '',
        filtroSituacao: '',
        filtroTipo: '',
        filtroValor: null,
        lupa: null,
        filtroLancamento: (this.$route.meta.modo === 'banco') ? 'exec' : '',
        transitoria: false,
        periodoTrAplicado: false,
        periodoPadraoTr: null,
        debitoParaRegularizar: null,
        periodoTocado: false,
        modoSugestao: false,
        buscaTexto: '',
        buscaAplicada: '',
        timerBusca: null,
        carregandoCROutras: false,
        crContaCarregado: false,
        carregandoCRConta: false,
        pedidoCRConta: 0,
        pedidoDados: 0,
        buscaCROutras: '',
        grupoAtivo: 0,
        linhaAtiva: null,
        vinculoIds: [],
        vinculoPedido: 0,
        lancamentoInfo: null,
        criacaoInfo: null,
        rateiosInfo: [],
        carregandoRateios: false,
        vinculoLinha: null,
        vinculoDados: null,
        carregandoVinculo: false,
        vinculoMarcados: [],
        mostrarBaixa: false,
        baixaData: null,
        baixaErro: false,
        baixaObs: '',
        baixaComTaxa: false,
        mostrarCheckTaxa: false,
        mostrarConferencia: false,
        modoConf: 'todos',
        buscaConf: '',
        mostrarJustificativa: false,
        mostrarAjuste: false,
        ajuste: { carregando: false, gravando: false, previa: null, escolhido: 0, idsEsq: [], idsDir: [] },
        salvandoJustificativa: false,
        validarJustificativa: false,
        justificativa: { lado: '', linhas: [], texto: '', edicao: false },
        linhaRegularizar: null,
        detectando: false,
        mostrarOperacao: false,
        deteccao: null,
        truncado: false,
        retroativosForaPeriodo: 0,
        retroativoDataInicial: '',
        limiteLinhas: 5000,
        linhasEsquerdaCarregadas: 0,
        totalLinhasEsquerda: 0,
        totalLinhasDireita: 0,
      }
    },
    computed: {
      categoriaInfo() {
        if (this.carregandoRateios) return 'Carregando...';
        const comCategoria = this.rateiosInfo.find(r => r.nomeCategoriaFinanceira);
        return comCategoria ? comCategoria.nomeCategoriaFinanceira : '—';
      },
      modo() { return this.$route.meta.modo || 'projeto'; },
      api() {
        if (this.modo === 'banco') {
          return { dados: conciliacaoProjetoServico.dadosBanco, conciliar: conciliacaoProjetoServico.conciliarBanco, desfazer: conciliacaoProjetoServico.desfazerBanco, observacao: conciliacaoProjetoServico.observacaoBanco };
        }
        return { dados: conciliacaoProjetoServico.dados, conciliar: conciliacaoProjetoServico.conciliar, desfazer: conciliacaoProjetoServico.desfazer, observacao: conciliacaoProjetoServico.observacao };
      },
      tituloEsq() {
        if (this.transitoria) return 'Créditos e contas a receber';
        return this.modo === 'banco' ? 'Lançamentos da conta' : 'Lançamentos do projeto';
      },
      tituloDir() {
        if (this.transitoria) return 'Lançamentos da conta transitória';
        return this.modo === 'banco' ? 'Extrato do banco' : 'Lançamentos da conta bancária';
      },
      rotuloEsq() {
        if (this.transitoria) return 'Total crédito';
        return this.modo === 'banco' ? 'Conta' : 'Projeto';
      },
      rotuloDir() {
        if (this.transitoria) return 'Total débito';
        return this.modo === 'banco' ? 'Extrato' : 'Banco';
      },
      mostrarProjetoDir() { return this.modo === 'projeto' || this.transitoria; },
      mostrarProjetoEsq() { return this.modo === 'banco'; },
      exigirConferido() { return this.modo !== 'banco'; },
      podeAlterarModulo() { return permissoesConciliacao.alterar(this.modo); },
      podeApagarModulo() { return permissoesConciliacao.apagar(this.modo); },
      podeVerLog() { return this.modo === 'banco' && permissoesConciliacao.configurar('banco'); },
      alvoLog() {
        if (!this.podeVerLog) return null;
        const lanc = new Set();
        const ext = new Set();
        const somar = l => {
          if (!l) return;
          if (l.idExtrato > 0) ext.add(l.idExtrato);
          else if (l.idLancamento > 0) lanc.add(l.idLancamento);
        };
        const sel = this.selEsq.concat(this.selDir);
        let grupo = 0;
        if (sel.length) {
          sel.forEach(somar);
        } else if (this.linhaAtivaVisivel && this.linhaAtiva) {
          grupo = this.linhaAtiva.idGrupo > 0 ? this.linhaAtiva.idGrupo : 0;
          somar(this.linhaAtiva);
          if (grupo > 0) this.esquerda.concat(this.direita).filter(l => l.idGrupo === grupo && l.conciliado === 1).forEach(somar);
        }
        if (!grupo && !lanc.size && !ext.size) return null;
        return { grupo, lancamentos: Array.from(lanc), extratos: Array.from(ext), qtde: lanc.size + ext.size };
      },
      rotuloLog() {
        const a = this.alvoLog;
        if (!a) return '';
        if (a.grupo > 0) return 'Log do grupo';
        return a.qtde > 1 ? 'Log dos ' + a.qtde + ' selecionados' : 'Log do selecionado';
      },
      veioDaTesouraria() { return this.modo === 'banco' && this.$route.query.origem === 'tesouraria' && permissoesConciliacao.consultar('tesouraria'); },
      bloqueadoContexto() { return this.modo !== 'banco' && (this.encerrado || this.contaLivre || this.semPrestacaoExterna); },
      somenteLeitura() { return this.bloqueadoContexto || !this.podeAlterarModulo; },
      motivoSomenteLeitura() {
        if (!this.podeAlterarModulo) return { chip: 'SEM PERMISSÃO · SÓ CONSULTA', titulo: 'Sem permissão para alterar', dica: 'Seu usuário tem acesso apenas de consulta nesta conciliação. Para conciliar, desfazer ou justificar, solicite a permissão no cadastro de usuários.' };
        if (this.contaLivre) return { chip: 'CONTA LIVRE · SÓ CONSULTA', titulo: 'Conta padrão livre', dica: 'A conta padrão deste projeto está marcada como conta livre, então ele fica fora da conciliação por projeto. A tela abre só para consulta.' };
        if (this.semPrestacaoExterna) return { chip: 'SEM PRESTAÇÃO EXTERNA · SÓ CONSULTA', titulo: 'Projeto sem prestação de conta externa', dica: 'A conciliação por projeto atende à prestação de contas externa. Como este projeto não presta contas para fora, a tela abre só para consulta.' };
        return { chip: 'ENCERRADO · SÓ CONSULTA', titulo: 'Projeto encerrado', dica: 'A conciliação fica disponível só para consulta: dá para filtrar, conferir e exportar, mas não conciliar, desfazer ou justificar.' };
      },
      formasDisponiveis() {
        const set = {};
        this.esquerda.concat(this.direita).forEach(l => { if (l.formaPagBanco) set[l.formaPagBanco] = true; });
        return Object.keys(set).sort();
      },
      esquerdaVisivel() {
        const linhas = this.filtrar(this.esquerda, true, 'esquerda');
        const antigasPrimeiro = (a, b) => (a.dataInt - b.dataInt) || (a.idLancamento - b.idLancamento);
        if (this.transitoria) return linhas.slice().sort(antigasPrimeiro);
        if (this.modo === 'banco' && (this.filtroLancamento === 'cr' || this.filtroLancamento === 'cr-outras')) return linhas.slice().sort(antigasPrimeiro);
        return linhas;
      },
      direitaVisivel() { return this.filtrar(this.direita, this.modo !== 'banco', 'direita'); },
      totalSugestoes() { return this.direita.filter(l => l.sugestao === 1 && l.conciliado !== 1).length; },
      grupoSugeridoSelecionado() {
        if (this.selDir.length === 0 || this.selEsq.length === 0) return null;
        const grupo = this.selDir[0].sugestaoGrupo;
        if (!grupo) return null;
        if (!this.selDir.every(l => l.sugestaoGrupo === grupo)) return null;
        if (!this.selEsq.every(l => l.sugestaoGrupo === grupo)) return null;
        return this.selDir[0];
      },
      sugestaoSelecionada() {
        if (this.selDir.length !== 1 || this.selEsq.length !== 1) return null;
        const extrato = this.selDir[0];
        if (extrato.sugestao !== 1 || extrato.sugestaoPar !== this.selEsq[0].idLancamento) return null;
        return extrato;
      },
      selEsq() { return this.esquerda.filter(l => l.selecionado); },
      crOutrasSelecionadas() { return this.selEsq.filter(l => l.crOutraConta === 1); },
      selDir() { return this.direita.filter(l => l.selecionado); },
      somaEsq() { return this.selEsq.reduce((s, l) => s + l.valor, 0); },
      somaDir() { return this.selDir.reduce((s, l) => s + l.valor, 0); },
      debitoSelecionadoTr() {
        if (!this.transitoria || this.somenteLeitura) return null;
        if (this.selEsq.length > 0 || this.selDir.length !== 1) return null;
        const linha = this.selDir[0];
        if (linha.creditoDebito !== 2 || linha.conciliado === 1) return null;
        return linha;
      },
      crAbertoSelecionadoTr() { return this.transitoria && this.selEsq.some(l => l.executado !== 1); },
      diferenca() { return this.transitoria ? this.somaEsq + this.somaDir : this.somaEsq - this.somaDir; },
      filtroValorEsq() { return this.valorFiltroDoLado('esquerda'); },
      filtroValorDir() { return this.valorFiltroDoLado('direita'); },
      dicaChipLupa() {
        if (!this.lupa) return '';
        const rotulos = {
          exec: 'Executados',
          cr: this.transitoria ? 'Executados + contas a receber' : 'Executados + CR desta conta',
          'cr-outras': 'Executados + CR de outras contas',
        };
        let texto = 'A lupa preencheu a busca com "' + this.lupa.texto + '"';
        if (this.lupa.lancamento !== this.lupa.anterior.lancamento) texto += ' e trocou Lançamento para "' + (rotulos[this.lupa.lancamento] || this.lupa.lancamento) + '"';
        return texto + '. Clique no X para desfazer e voltar ao que estava antes.';
      },
      dicaChipValor() {
        if (this.transitoria) {
          return 'Na conta transitória os lados são contrários: R$ ' + this.formatar(this.filtroValorEsq)
               + ' à esquerda e R$ ' + this.formatar(this.filtroValorDir)
               + ' à direita. Clique no X para voltar a mostrar todos os lançamentos.';
        }
        return 'Os dois lados estão filtrados por este valor, com o mesmo sinal — crédito não aparece junto com débito. Clique no X para voltar a mostrar todos os lançamentos.';
      },
      candidatoEsq() { return this.transitoria ? -this.somaDir : this.somaDir; },
      candidatoDir() { return this.transitoria ? -this.somaEsq : this.somaEsq; },
      candidatoChavesEsq() { return this.selDir.length > 0 ? this.chavesCandidatas('direita', this.selDir, this.candidatoEsq) : []; },
      candidatoChavesDir() { return this.selEsq.length > 0 ? this.chavesCandidatas('esquerda', this.selEsq, this.candidatoDir) : []; },
      podeConciliar() {
        if (this.somenteLeitura) return false;
        if (this.selEsq.length === 0 || this.selDir.length === 0) return false;
        if (Math.abs(this.diferenca) > TOLERANCIA) return false;
        if (this.exigirConferido && this.selDir.some(l => l.conferido !== 1)) return false;
        return true;
      },
      podeJustificar() {
        if (this.somenteLeitura) return false;
        return (this.selEsq.length > 0 && this.selDir.length === 0) || (this.selDir.length > 0 && this.selEsq.length === 0);
      },
      podeAjustarDiferenca() {
        if (this.modo !== 'banco' || this.transitoria || this.somenteLeitura || !this.podeAlterarModulo) return false;
        if (this.selEsq.length === 0 || this.selDir.length === 0) return false;
        const dif = Math.abs(this.diferenca);
        return dif > TOLERANCIA && dif <= LIMITE_AJUSTE_DIFERENCA + TOLERANCIA;
      },
      ajusteEscolhido() {
        const previa = this.ajuste.previa;
        return previa ? previa.lancamentos.filter(l => l.idLancamento === this.ajuste.escolhido)[0] || null : null;
      },
      totalJustificativa() { return (this.justificativa.linhas || []).reduce((s, l) => s + l.valor, 0); },
      qtdeJustificar() { return this.selEsq.length > 0 ? this.selEsq.length : this.selDir.length; },
      linhaAtivaVisivel() {
        if (!this.linhaAtiva) return false;
        return this.esquerdaVisivel.indexOf(this.linhaAtiva) !== -1 || this.direitaVisivel.indexOf(this.linhaAtiva) !== -1;
      },
      cortouEsquerda() { return this.totalLinhasEsquerda > this.linhasEsquerdaCarregadas; },
      cortouDireita() { return this.totalLinhasDireita > this.direita.length; },
      restanteEsquerda() { return Math.max(0, this.totalLinhasEsquerda - this.linhasEsquerdaCarregadas); },
      podeDesfazer() { return !this.bloqueadoContexto && this.podeApagarModulo && this.linhaAtivaVisivel && this.linhaAtiva.idGrupo > 0 && this.selEsq.length === 0 && this.selDir.length === 0; },
      statusMsg() {
        const ne = this.selEsq.length, nd = this.selDir.length;
        if (this.transitoria) {
          if (ne === 0 && nd === 0) return 'Selecione créditos ou contas a receber e lançamentos da transitória';
          if (nd === 0) return 'Selecione lançamentos da transitória';
          if (ne === 0) return 'Selecione créditos ou contas a receber';
          if (Math.abs(this.diferenca) > TOLERANCIA) return 'Créditos e débitos não fecham';
          return 'Pronto para conciliar';
        }
        if (ne === 0 && nd === 0) return 'Selecione lançamentos nos dois lados';
        if (nd === 0) return 'Selecione uma linha do outro lado';
        if (ne === 0) return 'Selecione lançamentos do projeto';
        if (Math.abs(this.diferenca) > TOLERANCIA) return 'Valores não conferem';
        if (this.exigirConferido && this.selDir.some(l => l.conferido !== 1)) return 'Falta conciliar no banco';
        return 'Pronto para conciliar';
      },
      esquerdaPeriodo() {
        return this.modo === 'banco' ? this.esquerda.filter(l => l.executado === 1) : this.esquerda;
      },
      progresso() {
        const total = this.esquerdaPeriodo.length + this.direita.length;
        const conc = this.esquerdaPeriodo.filter(l => l.conciliado === 1).length + this.direita.filter(l => l.conciliado === 1).length;
        return { conc, total };
      },
      pctProgresso() { return this.progresso.total > 0 ? (this.progresso.conc >= this.progresso.total ? 100 : Math.floor(this.progresso.conc / this.progresso.total * 100)) : 0; },
      cardConc() { return this.totalConta > 0 ? this.conciliadoConta : this.progresso.conc; },
      cardTotal() { return this.totalConta > 0 ? this.totalConta : this.progresso.total; },
      cardPend() { return Math.max(0, this.cardTotal - this.cardConc); },
      cardPct() { return this.cardTotal > 0 ? (this.cardConc >= this.cardTotal ? 100 : Math.floor(this.cardConc / this.cardTotal * 100)) : 0; },
      pendTotal() { return this.pendEsq + this.pendDir; },
      pendEsq() { return this.esquerdaPeriodo.filter(l => l.conciliado !== 1).length; },
      pendDir() { return this.direita.filter(l => l.conciliado !== 1).length; },
      lancamentoPadrao() { return this.modo === 'banco' ? 'exec' : ''; },
      ocultarAplicacaoPadrao() { return false; },
      temFiltroTela() { return this.filtroValor !== null || !!this.filtroForma || !!this.filtroSituacao || !!this.filtroTipo || !!this.buscaTexto || (!this.transitoria && this.filtroLancamento !== this.lancamentoPadrao) || this.ocultarAplicacao !== this.ocultarAplicacaoPadrao || this.modoSugestao; },
      conferencia() {
        const doc = l => (l.documentoBanco && l.documentoBanco.trim()) ? l.documentoBanco : l.documento;
        const grupos = {};
        this.esquerdaPeriodo.concat(this.direita).forEach(l => { if (l.conciliado === 1) grupos[l.idGrupo] = true; });
        const gsorted = Object.keys(grupos).map(k => +k).sort((a, b) => a - b);
        const rows = [];
        let qtdGrupos = 0, qtdDiv = 0, semPar = 0, somaDiv = 0;
        gsorted.forEach(g => {
          const le = this.esquerdaPeriodo.filter(l => l.conciliado === 1 && l.idGrupo === g);
          const ld = this.direita.filter(l => l.conciliado === 1 && l.idGrupo === g);
          const tipo = (le.length ? le[0].tipoConc : 0) || (ld.length ? ld[0].tipoConc : 0);
          const somaE = le.reduce((s, l) => s + l.valor, 0);
          const somaD = ld.reduce((s, l) => s + l.valor, 0);
          const dif = this.transitoria ? somaE + somaD : somaE - somaD;
          const justif = (tipo === 3 || tipo === 4);
          const divg = justif ? 0 : (Math.abs(dif) > 0.005 ? 1 : 0);
          qtdGrupos++;
          if (divg) { qtdDiv++; somaDiv += Math.abs(dif); }
          const mx = Math.max(le.length, ld.length);
          for (let k = 0; k < mx; k++) {
            const e = le[k], d = ld[k];
            const semE = !e && divg === 1;
            const semD = !d && divg === 1;
            if (semE || semD) semPar++;
            rows.push({
              grupoKey: 'g' + g, grupo: g, tipoDesc: this.descTipo(tipo), justif, divergente: divg, primeira: k === 0,
              dataEsq: e ? e.dataStr : '', docEsq: e ? doc(e) : '', docIntEsq: e ? (e.documento || '') : '', docBancoEsq: e ? (e.documentoBanco || '') : '', histEsq: e ? e.historico : '', valorEsq: e ? e.valor : null, semCorrEsq: semE,
              dataDir: d ? d.dataStr : '', docDir: d ? doc(d) : '', docIntDir: d ? (d.documento || '') : '', docBancoDir: d ? (d.documentoBanco || '') : '', histDir: d ? d.historico : '', valorDir: d ? d.valor : null, semCorrDir: semD,
              dif: k === 0 ? (justif ? null : dif) : null,
            });
          }
        });
        this.esquerdaPeriodo.filter(l => l.conciliado !== 1).forEach((l, i) => {
          rows.push({ grupoKey: 'ne' + i, grupo: 0, tipoDesc: 'Não conciliado', justif: false, divergente: 1, primeira: true, dataEsq: l.dataStr, docEsq: doc(l), docIntEsq: l.documento || '', docBancoEsq: l.documentoBanco || '', histEsq: l.historico, valorEsq: l.valor, semCorrEsq: false, dataDir: '', docDir: '', docIntDir: '', docBancoDir: '', histDir: '', valorDir: null, semCorrDir: true, dif: l.valor });
          semPar++; qtdDiv++; somaDiv += Math.abs(l.valor);
        });
        this.direita.filter(l => l.conciliado !== 1).forEach((l, i) => {
          rows.push({ grupoKey: 'nd' + i, grupo: 0, tipoDesc: 'Não conciliado', justif: false, divergente: 1, primeira: true, dataEsq: '', docEsq: '', docIntEsq: '', docBancoEsq: '', histEsq: '', valorEsq: null, semCorrEsq: true, dataDir: l.dataStr, docDir: doc(l), docIntDir: l.documento || '', docBancoDir: l.documentoBanco || '', histDir: l.historico, valorDir: l.valor, semCorrDir: false, dif: -l.valor });
          semPar++; qtdDiv++; somaDiv += Math.abs(l.valor);
        });
        const totEsq = this.esquerdaPeriodo.reduce((s, l) => s + l.valor, 0);
        const totDir = this.direita.reduce((s, l) => s + l.valor, 0);
        return { rows, totEsq, totDir, diferenca: this.transitoria ? totEsq + totDir : totEsq - totDir, qtdGrupos, qtdConf: qtdGrupos - qtdDiv, qtdDiv, semPar, somaDiv };
      },
      confRows() {
        const modo = this.modoConf;
        const q = (this.buscaConf || '').trim().toLowerCase();
        return this.conferencia.rows.filter(r => {
          if (modo === 'div' && r.divergente !== 1) return false;
          if (modo === 'ok' && r.divergente === 1) return false;
          if (q) {
            const alvo = [r.grupo || '', r.docEsq, r.docDir, r.histEsq, r.histDir].join(' ').toLowerCase();
            if (alvo.indexOf(q) === -1) return false;
          }
          return true;
        });
      },
      confSomaVisiveis() {
        return this.confRows.reduce((s, r) => s + (r.divergente === 1 && r.dif != null ? Math.abs(r.dif) : 0), 0);
      },
      confGruposVisiveis() {
        const set = {};
        this.confRows.forEach(r => { set[r.grupoKey] = true; });
        return Object.keys(set).length;
      },
      confGruposTotal() {
        const set = {};
        this.conferencia.rows.forEach(r => { set[r.grupoKey] = true; });
        return Object.keys(set).length;
      },
      confPctConf() {
        const c = this.conferencia;
        return c.qtdGrupos > 0 ? Math.round(c.qtdConf / c.qtdGrupos * 100) : 100;
      },
      confPeriodoLabel() {
        const f = d => d ? moment(d).format('DD/MM') : '';
        const fy = d => d ? moment(d).format('DD/MM/YYYY') : '';
        return this.dataIni && this.dataFim ? (f(this.dataIni) + ' a ' + fy(this.dataFim)) : '';
      },
      temDif() { return Math.abs(this.conferencia.diferenca) > 0.005; },
      linhaRegularizavel() {
        if (this.modo !== 'banco') return null;
        if (this.selEsq.length > 0 || this.selDir.length !== 1) return null;
        const linha = this.selDir[0];
        return (linha.idExtrato > 0 && linha.conciliado !== 1) ? linha : null;
      },
      linhasRegularizaveis() {
        if (this.modo !== 'banco' || this.selEsq.length > 0 || this.selDir.length === 0) return [];
        const linhas = this.selDir;
        const primeira = linhas[0];
        const validas = linhas.every(l => l.idExtrato > 0 && l.conciliado !== 1
          && l.creditoDebito === primeira.creditoDebito && l.dataInt === primeira.dataInt);
        return validas ? linhas : [];
      },
      boletosSelecionados() {
        if (this.modo !== 'banco' || this.selDir.length === 0) return [];
        const validos = this.selDir.every(l => l.idExtrato > 0 && l.conciliado !== 1 && l.creditoDebito === 1
          && /^\s*LIQUIDACAO BOLETO/i.test(l.historico || ''));
        return validos ? this.selDir : [];
      },
      lancamentosSaldo() {
        if (this.modo !== 'banco' || this.transitoria || this.selEsq.length === 0) return [];
        if (this.selDir.length > 0 && this.boletosSelecionados.length === 0) return [];
        const validos = this.selEsq.every(l => l.idLancamento > 0 && l.executado === 1 && l.conciliado !== 1 && l.crOutraConta !== 1);
        return validos ? this.selEsq : [];
      },
      complementoRegularizavel() {
        if (this.modo !== 'banco' || this.transitoria || this.selEsq.length === 0 || this.selDir.length === 0) return false;
        if (this.boletosSelecionados.length) return false;
        const primeira = this.selDir[0];
        const extratoOk = this.selDir.every(l => l.idExtrato > 0 && l.conciliado !== 1
          && l.creditoDebito === primeira.creditoDebito && l.dataInt === primeira.dataInt);
        const lancadosOk = this.selEsq.every(l => l.idLancamento > 0 && l.executado === 1 && l.conciliado !== 1 && l.crOutraConta !== 1);
        return extratoOk && lancadosOk && Math.abs(this.diferenca) > TOLERANCIA;
      },
      podeRegularizar() { return this.linhasRegularizaveis.length > 0 || this.lancamentosSaldo.length > 0 || this.complementoRegularizavel; },
      dicaBotaoRegularizar() {
        if (this.complementoRegularizavel)
          return 'Lançar a diferença de R$ ' + (-this.diferenca).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            + ' e conciliar tudo junto — os lançamentos já feitos não são alterados';
        if (this.lancamentosSaldo.length) {
          if (Math.abs(this.somaEsq) <= TOLERANCIA) return 'A soma dos lançamentos selecionados dá zero — use Conciliar com justificativa';
          if (this.somaEsq < 0) return 'A soma dos lançamentos selecionados é negativa — só dá para transferir quando sobra dinheiro na conta';
          if (this.boletosSelecionados.length)
            return 'Transferir o saldo dos ' + this.lancamentosSaldo.length + ' lançamentos selecionados — cada um é conferido pelo nosso número com os '
              + this.boletosSelecionados.length + ' boletos do relatório e a transferência zera a soma';
          return 'Transferir o saldo dos ' + this.lancamentosSaldo.length + ' lançamentos selecionados para outra conta — a transferência zera a soma e tudo é conciliado junto';
        }
        if (this.selEsq.length > 0 && this.selDir.length === 0)
          return 'Para transferir o saldo, selecione só lançamentos executados e ainda não conciliados desta conta';
        const qtd = this.linhasRegularizaveis.length;
        if (qtd > 1) return 'Regularizar as ' + qtd + ' linhas selecionadas juntas — o total tem que fechar nos lançamentos criados';
        if (this.selDir.length > 1) return 'As linhas selecionadas precisam ser do mesmo dia e todas créditos ou todas débitos';
        return 'Selecione uma ou mais linhas do extrato — o sistema identifica se é transferência, aplicação, resgate ou crédito a identificar';
      },
      creditoRegularizavel() {
        const linha = this.linhaRegularizavel;
        return linha && linha.creditoDebito === 1 ? linha : null;
      },
    },
    watch: {
      '$route': 'reiniciar',
      buscaTexto(valor) {
        if (this.lupa && (valor || '').trim() !== this.lupa.texto) this.lupa = null;
        clearTimeout(this.timerBusca);
        this.timerBusca = setTimeout(() => { this.buscaAplicada = (valor || '').trim(); }, 350);
      },
      linhaAtivaVisivel(visivel) {
        if (!visivel && this.linhaAtiva) { this.grupoAtivo = 0; this.linhaAtiva = null; }
      },
      filtroLancamento(valor) {
        if (this.lupa && valor !== this.lupa.lancamento) this.lupa = null;
        if (valor === 'cr' && this.modo === 'banco' && !this.transitoria && this.processado && !this.crContaCarregado) {
          this.carregarCRConta();
          return;
        }
        if (valor !== 'cr-outras' || this.transitoria) return;
        if (this.esquerda.some(l => l.crOutraConta === 1)) return;
        this.carregarCROutras(this.buscaTexto || '');
      },
    },
    mounted() {
      this.$root.tituloDaPagina = 'Conciliação';
      this._onKeydown = e => { if (e.key === 'Escape' && this.mostrarConferencia) this.fecharConferencia(); };
      document.addEventListener('keydown', this._onKeydown);
      if (this.modo === 'banco' && !this.dataIni && !this.dataFim) {
        const diaUtil = conciliacaoProjetoServico.diaUtilAnterior().catch(() => null);
        this.carregarContextoBanco(diaUtil).then(() => this.processar(this.autoAoAbrir()));
        return;
      }
      this.carregarContexto().then(() => this.processar());
    },
    beforeDestroy() { if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown); },
    methods: {
      autoAoAbrir() {
        return this.modo === 'banco' && !this.transitoria && !!this.dataIni && !!this.dataFim;
      },
      carregarContexto() {
        if (this.modo === 'banco') return Promise.resolve();

        const di = this.dataIni ? moment(this.dataIni).format('YYYY-MM-DD') : '';
        const df = this.dataFim ? moment(this.dataFim).format('YYYY-MM-DD') : '';

        return conciliacaoProjetoServico.contexto(this.idProjetoCript, di, df)
          .then(dados => {
            this.nomeProjeto = dados.nomeProjeto;
            this.nomeContaPadrao = dados.nomeContaPadrao;
            this.contaPadraoId = dados.idContaPadrao;
            this.encerrado = !!dados.encerrado;
            this.contaLivre = !!dados.contaLivre;
            this.semPrestacaoExterna = !!dados.semPrestacaoExterna;
            this.numeroBanco = dados.numeroBanco || ''; this.nomeBanco = dados.nomeBanco || '';
            this.numeroAgencia = dados.numeroAgencia || ''; this.numeroConta = dados.numeroConta || '';
            this.vigenciaInicio = dados.vigenciaInicio; this.vigenciaFim = dados.vigenciaFim;
            this.totalConta = dados.totalConta || 0; this.conciliadoConta = dados.conciliadoConta || 0;
            this.aplicarPeriodoRetornado(dados);
          })
          .catch(() => { });
      },
      carregarContextoBanco(diaUtil) {
        return conciliacaoProjetoServico.contextoBanco(this.idProjetoCript)
          .then(dados => {
            this.nomeProjeto = dados.nomeProjeto;
            this.nomeContaPadrao = dados.nomeContaPadrao;
            this.contaPadraoId = dados.idContaPadrao;
            this.numeroBanco = dados.numeroBanco || ''; this.nomeBanco = dados.nomeBanco || '';
            this.numeroAgencia = dados.numeroAgencia || ''; this.numeroConta = dados.numeroConta || '';
            this.transitoria = !!dados.transitoria;
            this.saldoTesouraria = dados.tesouraria || null;
            this.dataReferenciaTesouraria = dados.dataReferenciaTesourariaCurta || '';
            this.dataConciliadora = dados.dataReferenciaTesouraria || '';
            this.tesourariaRecemLida = true;

            if (!this.transitoria) {
              return this.definirDiaUtilBanco(diaUtil).then(() => {
                if (this.dataFim && moment(this.dataFim).format('DD/MM/YYYY') !== dados.dataReferenciaTesouraria) {
                  this.tesourariaRecemLida = false;
                  return this.atualizarSaldoTesouraria().then(() => { this.tesourariaRecemLida = true; });
                }
              });
            }

            this.filtroLancamento = 'exec';
            this.modoSugestao = false;
            this.periodoTrAplicado = true;
            this.periodoPadraoTr = {
              ini: dados.dataIni ? moment(dados.dataIni, 'YYYY-MM-DD').toDate() : null,
              fim: dados.dataFim ? moment(dados.dataFim, 'YYYY-MM-DD').toDate() : null,
            };
            this.aplicarPeriodoPadraoTr();
          })
          .catch(() => this.definirDiaUtilBanco(diaUtil));
      },
      atualizarSaldoTesouraria() {
        if (this.tesourariaRecemLida) { this.tesourariaRecemLida = false; return Promise.resolve(); }
        const df = this.dataFim ? moment(this.dataFim).format('YYYY-MM-DD') : '';
        return conciliacaoProjetoServico.contextoBanco(this.idProjetoCript, df)
          .then(dados => {
            this.saldoTesouraria = dados.tesouraria || null;
            this.dataReferenciaTesouraria = dados.dataReferenciaTesourariaCurta || '';
          })
          .catch(() => { });
      },
      aplicarPeriodoPadraoTr() {
        this.dataIni = this.periodoPadraoTr ? this.periodoPadraoTr.ini : null;
        this.dataFim = this.periodoPadraoTr ? this.periodoPadraoTr.fim : null;
      },
      definirHoje() {
        const base = moment();
        this.dataIni = base.toDate();
        this.dataFim = base.toDate();
      },
      definirMesAtual() {
        this.dataIni = moment().startOf('month').toDate();
        this.dataFim = moment().endOf('month').toDate();
      },
      aplicarPeriodoRetornado(dados) {
        if (this.dataIni && this.dataFim) return;

        const ini = dados.dataIni ? moment(dados.dataIni, 'YYYY-MM-DD') : null;
        const fim = dados.dataFim ? moment(dados.dataFim, 'YYYY-MM-DD') : null;

        if (ini && ini.isValid() && fim && fim.isValid()) {
          this.dataIni = ini.toDate();
          this.dataFim = fim.toDate();
          return;
        }

        this.definirMesAtual();
      },
      diaUtilAnteriorLocal() {
        const d = moment().subtract(1, 'day');
        while (d.day() === 0 || d.day() === 6) d.subtract(1, 'day');
        return d.toDate();
      },
      definirDiaUtilBanco(diaUtil) {
        return (diaUtil || conciliacaoProjetoServico.diaUtilAnterior())
          .then(r => {
            if (r && r.data) {
              this.dataIni = moment(r.data, 'YYYY-MM-DD').toDate();
              this.dataFim = moment(r.data, 'YYYY-MM-DD').toDate();
            } else {
              this.dataIni = this.diaUtilAnteriorLocal();
              this.dataFim = this.diaUtilAnteriorLocal();
            }
          })
          .catch(() => {
            this.dataIni = this.diaUtilAnteriorLocal();
            this.dataFim = this.diaUtilAnteriorLocal();
          });
      },
      formatar(v) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
      numeroInt(v) { return (v || 0).toLocaleString('pt-BR'); },
      moeda(v) {
        const n = v || 0;
        return (n < -0.004 ? '−R$ ' : 'R$ ') + this.formatar(Math.abs(n));
      },
      moedaSinal(v) {
        const n = v || 0;
        return (n > 0.004 ? '+' : '') + this.moeda(n);
      },
      temBancoTesouraria() {
        const t = this.saldoTesouraria;
        return !!t && t.saldoBanco !== null && t.saldoBanco !== undefined && t.dataSaldoBanco > 0;
      },
      tituloDiferencaTesouraria() {
        const t = this.saldoTesouraria;
        if (!t) return '';
        if (t.conciliacaoPendente) {
          const partes = [];
          if (t.lancamentosPorConciliar) partes.push(t.lancamentosPorConciliar + (t.lancamentosPorConciliar === 1 ? ' lançamento não conferido' : ' lançamentos não conferidos'));
          if (t.extratosPorConciliar) partes.push(t.extratosPorConciliar + (t.extratosPorConciliar === 1 ? ' linha de extrato' : ' linhas de extrato') + ' por conciliar');
          if (!partes.length && t.extratosPendentes) partes.push(t.extratosPendentes + ' linha(s) de extrato com lançamento ainda não conferido');
          return 'Conciliação pendente: ' + partes.join(' e ');
        }
        if (t.tipoDivergencia === 'baixa pendente') return 'Conta conciliada, mas há borderô com arquivo enviado e títulos sem baixa';
        if (t.tipoDivergencia === 'estrutural') return 'Conta conciliada e ainda assim diferente do banco: diferença a investigar';
        return 'Saldo do banco igual ao conciliado no Manager na data do saldo do banco';
      },
      tituloOperacaoTesouraria() {
        const t = this.saldoTesouraria;
        if (!t) return '';
        if (!t.divergenciaOperacao) return 'Saldo por operação (todos os lançamentos, conferidos ou não) igual ao conciliado: nada lançado sem conciliar desde a data de corte';
        const partes = ['Saldo por operação diferente do conciliado em ' + this.moedaSinal(t.diferencaOperacao)];
        if (t.lancamentosNaoConferidos) partes.push(t.lancamentosNaoConferidos + (t.lancamentosNaoConferidos === 1 ? ' lançamento não conferido' : ' lançamentos não conferidos') + ' desde a data de corte');
        return partes.join(': ');
      },
      subOperacaoTesouraria() {
        const t = this.saldoTesouraria;
        if (!t || !t.divergenciaOperacao) return 'bate com o conciliado';
        const n = t.lancamentosNaoConferidos || 0;
        if (n) return n + (n === 1 ? ' lançamento não conferido' : ' lançamentos não conferidos');
        return 'diferença ' + this.moedaSinal(t.diferencaOperacao);
      },
      classeDiferencaTesouraria() {
        const t = this.saldoTesouraria;
        if (!t || !this.temBancoTesouraria()) return 'mudo';
        if (t.conciliacaoPendente) return 'pend-val';
        if (t.divergente) return 'neg';
        return 'ok';
      },
      valCls(v) { return v == null ? '' : (v < 0 ? 'cf-neg' : 'cf-pos'); },
      descTipo(tipo) { switch (tipo) { case 1: return 'Automática'; case 2: return 'Manual'; case 3: return 'Pago outra conta'; case 4: return 'Justificado'; default: return ''; } },
      abrirConferencia() {
        try {
          void this.conferencia.rows.length;
          this.modoConf = 'todos';
          this.buscaConf = '';
          this.mostrarConferencia = true;
        } catch (e) {
          console.error('Conferência:', e);
          toastr.error('Erro ao abrir a conferência: ' + (e && e.message ? e.message : e));
        }
      },
      fecharConferencia() { this.mostrarConferencia = false; },
      setModoConf(m) { this.modoConf = m; },
      reiniciar() { this.idProjetoCript = this.$route.params.idContaCript || this.$route.params.idProjetoCript; this.esquerda = []; this.direita = []; this.processado = false; this.grupoAtivo = 0; this.linhaAtiva = null; this.processar(this.autoAoAbrir()); },
      casaBusca(l) {
        if (!this.buscaAplicada) return true;
        const alvo = l._busca || this.textoBusca(l);
        const busca = this.semAcento(this.buscaAplicada);
        const buscaAlnum = this.soAlnum(this.buscaAplicada);
        if (alvo.indexOf(busca) !== -1) return true;
        return !!buscaAlnum && alvo.indexOf(buscaAlnum) !== -1;
      },
      valorFiltroDoLado(lado) {
        if (this.filtroValor === null) return null;
        return this.transitoria && lado === 'direita' ? -this.filtroValor : this.filtroValor;
      },
      casaValor(l, lado) {
        const alvo = this.valorFiltroDoLado(lado);
        if (alvo === null) return true;
        return Math.abs(l.valor - alvo) <= TOLERANCIA;
      },
      onFiltrarValor(dados) {
        const bruto = dados && typeof dados === 'object' ? dados.valor : dados;
        const lado = dados && typeof dados === 'object' ? dados.lado : 'esquerda';
        const valor = +bruto || 0;
        const alvo = this.transitoria && lado === 'direita' ? -valor : valor;
        if (this.filtroValor !== null && Math.abs(this.filtroValor - alvo) <= TOLERANCIA) {
          this.limparFiltroValor();
          return;
        }
        this.filtroValor = alvo;
      },
      limparFiltroValor() { this.filtroValor = null; },
      filtrar(linhas, aplicarForma = true, lado = 'esquerda') {
        return linhas.filter(l => {
          if (this.modoSugestao) return l.sugestao === 1 && l.conciliado !== 1 && this.casaValor(l, lado);
          if (l.selecionado === true) return true;
          if (!this.casaValor(l, lado)) return false;
          if (this.soNaoConciliados && l.conciliado === 1) return false;
          if (this.ocultarAplicacao && l.aplicacao !== 0) return false;
          if (aplicarForma && this.filtroForma && l.formaPagBanco !== this.filtroForma) return false;
          if (this.filtroTipo && l.creditoDebito !== +this.filtroTipo) return false;
          if (!this.casaBusca(l)) return false;
          if (this.modo !== 'banco') {
            if (this.filtroLancamento === 'conf' && !(l.executado === 1 && l.conferido === 1)) return false;
            if (this.filtroLancamento === 'naoconf' && !(l.executado === 1 && l.conferido !== 1)) return false;
            if (this.filtroLancamento === 'aberto' && l.executado === 1) return false;
          }
          if (this.transitoria && lado === 'esquerda') {
            if (this.filtroLancamento === 'exec' && l.executado !== 1) return false;
          }
          if (this.modo === 'banco' && !this.transitoria && l.executado !== 1) {
            if (lado !== 'esquerda') return false;
            if (this.filtroLancamento === 'cr') {
              if (!(l.creditoDebito === 1 && l.crOutraConta !== 1)) return false;
            } else if (this.filtroLancamento === 'cr-outras') {
              if (l.crOutraConta !== 1) return false;
            } else {
              return false;
            }
          }
          if (this.filtroSituacao === '1' && l.tipoConc !== 1) return false;
          if (this.filtroSituacao === '2' && l.tipoConc !== 2) return false;
          if (this.filtroSituacao === '4' && l.tipoConc !== 4) return false;
          if (this.filtroSituacao === '3' && !(l.idConta !== 0 && l.idConta !== this.contaPadraoId)) return false;
          return true;
        });
      },
      limparFiltrosDaGrade() {
        this.ocultarAplicacao = this.ocultarAplicacaoPadrao; this.filtroForma = ''; this.filtroSituacao = ''; this.filtroTipo = '';
        if (!this.transitoria) this.filtroLancamento = this.lancamentoPadrao;
        this.modoSugestao = false;
        clearTimeout(this.timerBusca);
        this.buscaTexto = ''; this.buscaAplicada = '';
        this.filtroValor = null;
        this.lupa = null;
        this.limparCROutras();
        this.limparSelecao();
      },
      limparFiltros() {
        this.limparFiltrosDaGrade();
        this.processar();
      },
      limparSelecao() {
        this.esquerda.forEach(l => { l.selecionado = false; });
        this.direita.forEach(l => { l.selecionado = false; });
        this.grupoAtivo = 0;
        this.linhaAtiva = null;
      },
      voltar() {
        if (this.veioDaTesouraria) { this.$router.push({ name: 'posicao-tesouraria', query: { voltar: 1 } }); return; }
        this.$router.push({ name: this.modo === 'banco' ? 'conciliacao-banco' : 'conciliacao-projeto' });
      },
      onDataChange() {
        this.periodoTocado = true;

        if (this.modo !== 'banco' && (!this.dataIni || !this.dataFim)) {
          const referencia = moment(this.dataIni || this.dataFim || new Date());
          if (!this.dataIni) this.dataIni = referencia.clone().startOf('month').toDate();
          if (!this.dataFim) this.dataFim = referencia.clone().endOf('month').toDate();
          toastr.info('A conciliação do projeto precisa de um período. Ajustamos para o mês da data informada.');
        }

        this.limparFiltrosDaGrade();
        clearTimeout(this.timerProcessar);
        this.timerProcessar = setTimeout(() => this.processar(true), 300);

        if (this.modo === 'banco' && !this.transitoria) {
          this.tesourariaRecemLida = false;
          this.atualizarSaldoTesouraria();
        }
      },
      incluirRetroativos() {
        if (!this.retroativoDataInicial) return;
        this.dataIni = moment(this.retroativoDataInicial, 'DD/MM/YYYY').toDate();
        this.onDataChange();
      },
      atualizar() {
        if (this.carregando) return;
        clearTimeout(this.timerProcessar);
        this.processar(true);
      },
      semAcento(v) {
        return (v == null ? '' : String(v)).toLowerCase()
          .replace(/[áàâäã]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[íìîï]/g, 'i')
          .replace(/[óòôöõ]/g, 'o').replace(/[úùûü]/g, 'u').replace(/ç/g, 'c');
      },
      soAlnum(v) {
        return (v == null ? '' : String(v)).toLowerCase().replace(/[^a-z0-9]/g, '');
      },
      textoBusca(l) {
        return this.semAcento(l.historico) + ' ' + this.semAcento(l.nomePessoa)
          + ' ' + this.semAcento(l.complemento) + ' ' + this.semAcento(l.documento)
          + ' ' + this.semAcento(l.documentoBanco) + ' ' + this.semAcento(l.documentoReceberPagar)
          + ' ' + this.semAcento(l.notaFiscal)
          + ' ' + this.semAcento(l.cpfcnpj) + ' ' + this.soAlnum(l.cpfcnpj);
      },
      carregarMais() {
        if (this.carregando) return;

        const antes = this.linhasEsquerdaCarregadas;
        this.limiteLinhas += 5000;

        this.processar().then(() => {
          const novas = this.linhasEsquerdaCarregadas - antes;
          if (novas > 0) toastr.success(novas.toLocaleString('pt-BR') + ' lançamentos carregados.');
          else toastr.info('Não há mais lançamentos para carregar neste período.');
        });
      },
      processar(comAuto = false) {
        const t0 = performance.now();
        this.carregando = true; this.grupoAtivo = 0; this.linhaAtiva = null; this.filtroValor = null;
        this.vinculoPedido++; this.vinculoIds = [];
        const di = this.dataIni ? moment(this.dataIni).format('YYYY-MM-DD') : '';
        const df = this.dataFim ? moment(this.dataFim).format('YYYY-MM-DD') : '';
        const comCR = this.filtroLancamento === 'cr';
        this.pedidoCRConta++; this.carregandoCRConta = false;
        const pedido = ++this.pedidoDados;
        return this.api.dados(this.idProjetoCript, di, df, comAuto === true && !this.encerrado, this.limiteLinhas, comCR)
          .then(dados => {
            if (pedido !== this.pedidoDados) return;
            this.carregando = false; this.processado = true;
            this.crContaCarregado = comCR;
            this.ultimaAtualizacao = moment().format('HH:mm');
            this.encerrado = !!dados.encerrado;
            this.contaLivre = !!dados.contaLivre;
            this.semPrestacaoExterna = !!dados.semPrestacaoExterna;
            const estreouTransitoria = !!dados.transitoria && !this.transitoria;
            this.transitoria = !!dados.transitoria;
            if (estreouTransitoria) {
              this.filtroLancamento = 'exec';
              this.modoSugestao = false;
            }
            if (this.transitoria && !this.periodoTrAplicado && !this.periodoTocado) {
              this.periodoTrAplicado = true;
              const hoje = moment().startOf('day');
              if (!this.dataIni || !this.dataFim || !hoje.isSame(this.dataIni, 'day') || !hoje.isSame(this.dataFim, 'day')) {
                this.definirHoje();
                return this.processar(comAuto);
              }
            }
            this.nomeProjeto = dados.nomeProjeto; this.nomeContaPadrao = dados.nomeContaPadrao; this.contaPadraoId = dados.idContaPadrao;
            this.totalConta = dados.totalConta || 0; this.conciliadoConta = dados.conciliadoConta || 0;
            this.numeroBanco = dados.numeroBanco || ''; this.nomeBanco = dados.nomeBanco || ''; this.numeroAgencia = dados.numeroAgencia || ''; this.numeroConta = dados.numeroConta || '';
            this.vigenciaInicio = dados.vigenciaInicio; this.vigenciaFim = dados.vigenciaFim;
            if (this.modo !== 'banco') this.aplicarPeriodoRetornado(dados);
            if (this.modo === 'banco' && !this.periodoTocado && !this.dataIni && dados.vigenciaInicio) this.dataIni = moment(dados.vigenciaInicio, 'DD/MM/YYYY').toDate();
            if (this.modo === 'banco' && !this.periodoTocado && !this.dataFim && dados.vigenciaFim) this.dataFim = moment(dados.vigenciaFim, 'DD/MM/YYYY').toDate();
            this.esquerda = (dados.esquerda || []).map(l => Object.assign({ selecionado: false }, l, { _busca: this.textoBusca(l) }));
            this.direita = (dados.direita || []).map(l => Object.assign({ selecionado: false }, l, { _busca: this.textoBusca(l) }));
            this.truncado = dados.truncado === true;
            this.retroativosForaPeriodo = dados.retroativosForaPeriodo || 0;
            this.retroativoDataInicial = dados.retroativoDataInicial || '';
            this.linhasEsquerdaCarregadas = dados.linhasEsquerdaCarregadas || this.esquerda.length;
            this.totalLinhasEsquerda = dados.totalLinhasEsquerda || this.esquerda.length;
            this.totalLinhasDireita = dados.totalLinhasDireita || this.direita.length;
            if (this.modoSugestao && this.totalSugestoes === 0) this.modoSugestao = false;
            if (this.modo === 'banco' && !this.transitoria && this.filtroLancamento === 'cr-outras') this.carregarCROutras(this.buscaCROutras || '');
            if (this.modo === 'banco' && !this.transitoria && this.filtroLancamento === 'cr' && !this.crContaCarregado) this.carregarCRConta();
            if ((dados.paresDesfeitos || []).length > 0) {
              toastr.warning(dados.paresDesfeitos.map(a => $('<div>').text(a).html()).join('<br>'), 'Conciliações desfeitas porque foram alteradas fora do módulo', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
            }
            if (this.modo === 'banco' && !this.transitoria && (dados.conciliadosAutomatica || 0) > 0) {
              this.tesourariaRecemLida = false;
              this.atualizarSaldoTesouraria();
            }
            const tResposta = performance.now();
            this.$nextTick(() => console.log('[conc] http=' + Math.round(tResposta - t0) + 'ms render='
              + Math.round(performance.now() - tResposta) + 'ms esq=' + this.esquerda.length + ' dir=' + this.direita.length));
          })
          .catch(() => {
            if (pedido !== this.pedidoDados) return;
            this.carregando = false; toastr.error('Não foi possível carregar os lançamentos.');
          });
      },
      onToggle({ lado, linha }) {
        linha.selecionado = !linha.selecionado;
        this.grupoAtivo = 0;
        this.linhaAtiva = null;
        if (linha.selecionado) this.trazerCandidato(lado, linha.valor, linha);
      },
      marcarVinculos(linha) {
        const pedido = ++this.vinculoPedido;
        this.vinculoIds = [];
        if (!this.transitoria || !(linha.vinculoTransitoria > 0) || !(linha.idLancamento > 0)) return;

        conciliacaoProjetoServico.vinculosTransitoria(this.idProjetoCript, linha.idLancamento)
          .then(r => {
            if (pedido !== this.vinculoPedido || !r) return;
            const ids = (r.itens || []).map(i => +i.idLancamento).concat([+r.idLancamentoDebito])
              .filter((id, i, a) => id > 0 && id !== linha.idLancamento && a.indexOf(id) === i);
            this.vinculoIds = ids;
          })
          .catch(() => {});
      },
      onAtivar({ lado, linha }) {
        this.marcarVinculos(linha);
        if (linha.conciliado === 1) {
          this.grupoAtivo = linha.idGrupo; this.linhaAtiva = linha;
          this.trazerGrupo(linha);
        } else {
          this.grupoAtivo = 0; this.linhaAtiva = null;
          this.trazerCandidato(lado, linha.valor, linha);
        }
      },
      alternarModoSugestao() {
        this.modoSugestao = !this.modoSugestao;
        this.limparSelecao();
      },
      onSugestao({ lado, linha }) {
        if (linha.sugestaoGrupo > 0) {
          this.limparSelecao();
          this.esquerda.forEach(e => { if (e.sugestaoGrupo === linha.sugestaoGrupo) e.selecionado = true; });
          this.direita.forEach(d => { if (d.sugestaoGrupo === linha.sugestaoGrupo) d.selecionado = true; });
          this.trazerSelecionados();
          return;
        }

        const extrato = lado === 'direita' ? linha : this.direita.filter(d => d.idExtrato === linha.sugestaoPar)[0];
        const lancamento = lado === 'direita' ? this.esquerda.filter(e => e.idLancamento === linha.sugestaoPar)[0] : linha;

        if (!extrato || !lancamento) {
          toastr.warning('O par sugerido não está visível no período atual.');
          return;
        }

        this.limparSelecao();
        extrato.selecionado = true;
        lancamento.selecionado = true;
        this.trazerSelecionados();
      },
      descartarSugestao() {
        const extrato = this.sugestaoSelecionada;
        if (!extrato) return;

        const idLancamento = extrato.sugestaoPar;

        conciliacaoProjetoServico.responderSugestaoIA(this.idProjetoCript, {
          idExtrato: extrato.idExtrato,
          idLancamento: idLancamento,
          situacao: 2,
        })
          .then(() => {
            this.limparSugestaoLocal(extrato.idExtrato, idLancamento);
            this.limparSelecao();
            toastr.success('Sugestão descartada.');
          })
          .catch(() => toastr.error('Não foi possível descartar a sugestão.'));
      },
      limparSugestaoLocal(idExtrato, idLancamento) {
        const limpar = l => {
          l.sugestao = 0; l.sugestaoPar = 0; l.sugestaoCriterio = ''; l.sugestaoConfianca = null;
          l.sugestaoNivel = ''; l.sugestaoConcorrentes = 0;
        };

        this.direita.filter(d => d.idExtrato === idExtrato).forEach(limpar);

        const restantes = this.direita
          .filter(d => d.sugestao === 1 && d.sugestaoPar === idLancamento)
          .sort((a, b) => (b.sugestaoConfianca || 0) - (a.sugestaoConfianca || 0));

        const lancamentos = this.esquerda.filter(e => e.idLancamento === idLancamento);

        if (restantes.length === 0) {
          lancamentos.forEach(limpar);
        } else {
          const concorrentes = restantes.length > 1 ? restantes.length : 0;

          restantes.forEach(d => { d.sugestaoConcorrentes = concorrentes; });

          lancamentos.forEach(e => {
            e.sugestaoConcorrentes = concorrentes;

            if (e.sugestaoPar !== idExtrato) return;

            e.sugestaoPar = restantes[0].idExtrato;
            e.sugestaoCriterio = restantes[0].sugestaoCriterio;
            e.sugestaoConfianca = restantes[0].sugestaoConfianca;
            e.sugestaoNivel = restantes[0].sugestaoNivel;
          });
        }

        if (this.modoSugestao && this.totalSugestoes === 0) this.modoSugestao = false;
      },
      abrirRegularizarTransitoria(linha) {
        const debito = linha || this.debitoSelecionadoTr;
        if (!debito) return;
        this.debitoParaRegularizar = debito;
      },
      regularizarTransitoriaConcluida() {
        this.debitoParaRegularizar = null;
        this.limparFiltros();
      },
      trazerCandidato(lado, valor, linha) {
        const alvo = lado === 'esquerda' ? this.$refs.gradeDir : this.$refs.gradeEsq;
        const valorAlvo = this.transitoria ? -valor : valor;
        const chaves = linha ? this.chavesCandidatas(lado, [linha], valorAlvo) : [];
        if (alvo) alvo.scrollToValor(valorAlvo, chaves);
      },
      chavesCandidatas(ladoOrigem, linhas, valorAlvo) {
        if (this.modo !== 'banco' || this.transitoria || !valorAlvo) return [];
        let chaves = [];
        if (ladoOrigem === 'esquerda') {
          linhas.map(chaveCambioLinha).filter(c => c).forEach(gdc => {
            this.esquerda
              .filter(s => s.conciliado === 1 && s.idGrupo && chaveCambioLinha(s) === gdc)
              .forEach(s => this.direita
                .filter(d => d.conciliado === 1 && d.idGrupo === s.idGrupo)
                .forEach(d => chaves.push(chaveBlocoExtrato(d))));
          });
        } else {
          linhas.map(chaveBlocoExtrato).filter(c => c).forEach(doc => {
            this.direita
              .filter(d => d.conciliado === 1 && d.idGrupo && chaveBlocoExtrato(d) === doc)
              .forEach(d => this.esquerda
                .filter(e => e.conciliado === 1 && e.idGrupo === d.idGrupo)
                .forEach(e => chaves.push(chaveCambioLinha(e))));
          });
        }
        chaves = chaves.filter((c, i, a) => c && a.indexOf(c) === i);
        if (chaves.length === 0) return [];
        const alvo = ladoOrigem === 'esquerda' ? this.direita : this.esquerda;
        const chaveDoAlvo = ladoOrigem === 'esquerda' ? chaveBlocoExtrato : chaveCambioLinha;
        const existe = alvo.some(l => l.conciliado !== 1 && Math.abs(l.valor - valorAlvo) <= TOLERANCIA && chaves.indexOf(chaveDoAlvo(l)) >= 0);
        return existe ? chaves : [];
      },
      trazerSelecionados() {
        if (this.$refs.gradeEsq) this.$refs.gradeEsq.scrollToSelecionado();
        if (this.$refs.gradeDir) this.$refs.gradeDir.scrollToSelecionado();
      },
      trazerGrupo(linha) {
        if (this.$refs.gradeEsq) this.$refs.gradeEsq.scrollToConciliado(linha.idGrupo, linha.valor);
        if (this.$refs.gradeDir) this.$refs.gradeDir.scrollToConciliado(linha.idGrupo, linha.valor);
      },
      onBuscar({ texto, creditoDebito }) {
        const valor = (texto || '').trim();
        if (!valor) return;
        if (this.lupaAtiva('busca', valor)) { this.desfazerLupa(); return; }
        const anterior = this.estadoAntesDaLupa();
        this.buscaTexto = valor;
        if (this.modo === 'banco' && (this.transitoria ? creditoDebito === 2 : creditoDebito === 1)) this.filtroLancamento = 'cr';
        this.lupa = { tipo: 'busca', texto: valor, lancamento: this.filtroLancamento, anterior };
      },
      onBuscarOutras({ texto }) {
        const valor = (texto || '').trim();
        if (!valor) return;
        if (this.lupaAtiva('outras', valor)) { this.desfazerLupa(); return; }
        const anterior = this.estadoAntesDaLupa();
        clearTimeout(this.timerBusca);
        this.buscaTexto = valor;
        this.buscaAplicada = valor;
        this.filtroLancamento = this.transitoria ? 'cr' : 'cr-outras';
        this.lupa = { tipo: 'outras', texto: valor, lancamento: this.filtroLancamento, anterior };
        this.carregarCROutras(valor);
      },
      lupaAtiva(tipo, texto) { return !!this.lupa && this.lupa.tipo === tipo && this.lupa.texto === texto; },
      estadoAntesDaLupa() {
        if (this.lupa) return this.lupa.anterior;
        return { busca: this.buscaTexto, lancamento: this.filtroLancamento };
      },
      desfazerLupa() {
        if (!this.lupa) return;
        const anterior = this.lupa.anterior;
        this.lupa = null;
        clearTimeout(this.timerBusca);
        this.buscaTexto = anterior.busca || '';
        this.buscaAplicada = (anterior.busca || '').trim();
        this.filtroLancamento = anterior.lancamento;
        if (anterior.lancamento !== 'cr-outras') this.limparCROutras();
      },
      carregarCROutras(busca = '') {
        if (this.modo !== 'banco' || this.carregandoCROutras) return Promise.resolve();
        this.carregandoCROutras = true;
        this.buscaCROutras = busca;
        return conciliacaoProjetoServico.contasReceberOutrasContas(this.idProjetoCript, busca, 0)
          .then(linhas => {
            this.carregandoCROutras = false;
            this.mesclarCROutras(linhas || []);
            if ((linhas || []).length === 0) {
              toastr.info(busca
                ? 'Nenhuma conta a receber em aberto de outras contas para "' + busca + '".'
                : 'Nenhuma conta a receber em aberto em outras contas.');
            }
          })
          .catch(() => {
            this.carregandoCROutras = false;
            toastr.error('Não foi possível buscar as contas a receber de outras contas.');
          });
      },
      carregarCRConta() {
        const pedido = ++this.pedidoCRConta;
        this.carregandoCRConta = true;
        return conciliacaoProjetoServico.contasReceberConta(this.idProjetoCript)
          .then(linhas => {
            if (pedido !== this.pedidoCRConta) return;
            this.carregandoCRConta = false;
            this.crContaCarregado = true;
            const existentes = {};
            this.esquerda.forEach(l => { if (l.idLancamento > 0) existentes[l.idLancamento] = true; });
            const novas = (linhas || [])
              .filter(l => !existentes[l.idLancamento])
              .map(l => Object.assign({ selecionado: false }, l, { _busca: this.textoBusca(l) }));
            if (novas.length) this.esquerda = this.esquerda.concat(novas);
          })
          .catch(() => {
            if (pedido !== this.pedidoCRConta) return;
            this.carregandoCRConta = false;
            toastr.error('Não foi possível carregar as contas a receber desta conta.');
          });
      },
      mesclarCROutras(linhas) {
        const manter = this.transitoria ? this.esquerda.slice() : this.esquerda.filter(l => l.crOutraConta !== 1 || l.selecionado);
        const existentes = {};
        manter.forEach(l => { if (l.idLancamento > 0) existentes[l.idLancamento] = true; });
        const novas = linhas
          .filter(l => !existentes[l.idLancamento])
          .map(l => Object.assign({ selecionado: false }, l, { _busca: this.textoBusca(l) }));
        this.esquerda = manter.concat(novas);
      },
      limparCROutras() {
        this.buscaCROutras = '';
        if (this.transitoria) return;
        const proprias = this.esquerda.filter(l => l.crOutraConta !== 1);
        if (proprias.length !== this.esquerda.length) this.esquerda = proprias;
      },
      onScrollX({ lado, x }) {
        const alvo = lado === 'esquerda' ? this.$refs.gradeDir : this.$refs.gradeEsq;
        if (alvo) alvo.sincronizarX(x);
      },
      onMarcarTodos({ lado, marca }) {
        const lista = lado === 'esquerda' ? this.esquerdaVisivel : this.direitaVisivel;
        lista.forEach(l => {
          if (l.conciliado === 1) return;
          if (lado === 'direita' && this.linhaBloqueada(l)) return;
          l.selecionado = marca;
        });
      },
      linhaBloqueada(l) { return this.exigirConferido && l.conferido !== 1; },
      onBloqueada() {
        toastr.warning('Lançamento ainda não conciliado no banco: dá para justificar ou marcar como pago em outra conta, mas não conciliar.');
      },
      montarPayload(tipo) {
        return {
          tipo: tipo,
          esquerda: this.selEsq.map(l => ({ idLancamento: l.idLancamento, idLancRateio: l.idLancRateio, idExtrato: l.idExtrato, valor: l.valor })),
          direita: this.selDir.map(l => ({ idLancamento: l.idLancamento, idLancRateio: l.idLancRateio, idExtrato: l.idExtrato, valor: l.valor })),
        };
      },
      conciliar() {
        if (!this.podeConciliar) return;
        if (this.transitoria && !this.crAbertoSelecionadoTr) {
          this.gravarConciliacao(this.montarPayload(2));
          return;
        }
        if (this.modo === 'banco') {
          this.baixaData = this.transitoria ? null : (this.dataExtratoSelecionado() || new Date());
          this.baixaErro = false;
          this.baixaObs = '';
          this.baixaComTaxa = false;
          this.mostrarCheckTaxa = false;
          this.mostrarBaixa = true;
          this.carregarCheckTaxa();
          return;
        }
        this.gravarConciliacao(this.montarPayload(2));
      },
      carregarCheckTaxa() {
        const ids = this.selEsq.map(l => l.idLancamento).filter(x => x > 0);
        if (!ids.length) return;

        conciliacaoProjetoServico.checkTaxa(this.idProjetoCript, { idsLancamento: ids })
          .then(r => {
            if (!this.mostrarBaixa) return;
            this.mostrarCheckTaxa = !!(r && r.mostrar);
            this.baixaComTaxa = !!(r && r.marcado);
          })
          .catch(() => { this.mostrarCheckTaxa = false; });
      },
      dataExtratoSelecionado() {
        let escolhida = null;
        this.selDir.forEach(l => {
          if (l.dataInt > 1 && (!escolhida || l.dataInt > escolhida.dataInt)) escolhida = l;
        });
        if (!escolhida) return null;
        const data = moment(escolhida.dataStr, 'DD/MM/YYYY');
        return data.isValid() ? data.toDate() : null;
      },
      confirmarBaixa() {
        if (!this.baixaData) {
          this.baixaErro = true;
          return;
        }

        const payload = this.montarPayload(2);
        payload.dataBaixa = this.baixaData ? moment(this.baixaData).format('YYYY-MM-DD') : '';
        payload.observacao = this.baixaObs || '';
        payload.comTaxa = this.mostrarCheckTaxa && this.baixaComTaxa;
        this.mostrarBaixa = false;
        this.gravarConciliacao(payload);
      },
      gravarConciliacao(payload) {
        const qtdeOutras = this.crOutrasSelecionadas.length;
        this.salvando = true;
        this.api.conciliar(this.idProjetoCript, payload)
          .then(resposta => {
            this.salvando = false;
            if (qtdeOutras > 0) {
              toastr.success('Conciliação registrada. ' + qtdeOutras + ' conta' + (qtdeOutras > 1 ? 's' : '')
                + ' a receber já ' + (qtdeOutras > 1 ? 'foram baixadas' : 'foi baixada') + ' em ' + this.nomeContaPadrao
                + ' — a provisão de repasse é concluída pelo serviço em seguida.');
            } else {
              toastr.success('Conciliação registrada.');
            }
            const revisandoSugestoes = this.modoSugestao;

            if (this.transitoria) {
              this.restaurarFiltrosTela();
              this.processar(true).then(() => {
                if (!revisandoSugestoes) return;
                if (this.totalSugestoes > 0) { this.modoSugestao = true; return; }
                this.modoSugestao = false;
                toastr.info('Todas as sugestões da IA foram revisadas.');
              });
              return;
            }

            this.aplicarConciliacaoLocal(resposta && resposta.grupo ? +resposta.grupo : 0, 2, payload.dataBaixa);
            this.restaurarFiltrosTela();
            if (this.modo === 'banco') {
              this.tesourariaRecemLida = false;
              this.atualizarSaldoTesouraria();
            }

            if (!revisandoSugestoes) return;
            if (this.totalSugestoes > 0) { this.modoSugestao = true; return; }
            this.modoSugestao = false;
            toastr.info('Todas as sugestões da IA foram revisadas.');
          })
          .catch(res => { this.salvando = false; toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível conciliar.'); });
      },
      aplicarConciliacaoLocal(grupo, tipoConc, dataBaixa) {
        const esq = this.selEsq.slice();
        const dir = this.selDir.slice();
        const banco = this.modo === 'banco';
        const baixa = banco ? this.serialDaBaixa(dataBaixa) : null;

        esq.forEach(l => {
          if (banco && l.executado === 1 && l.conferido !== 1) this.conciliadoConta += 1;

          if (banco && l.executado !== 1 && baixa) {
            l.executado = 1;
            l.dataInt = baixa.serial;
            l.dataStr = baixa.texto;
            l.idConta = this.contaPadraoId;
            l.nomeConta = this.nomeContaPadrao;
            l.crOutraConta = 0;
            this.conciliadoConta += 1;
            this.totalConta += 1;
          }

          if (l.executado === 1) l.conferido = 1;
          l.conciliado = 1;
          l.tipoConc = tipoConc;
          l.idGrupo = grupo;
          l.selecionado = false;
        });

        dir.forEach(l => {
          if (banco && l.conciliado !== 1) this.totalConta = Math.max(0, this.totalConta - 1);
          if (!banco && l.executado === 1) l.conferido = 1;
          l.conciliado = 1;
          l.tipoConc = tipoConc;
          l.idGrupo = grupo;
          if (banco && !l.statusConciliacao) l.statusConciliacao = 1;
          l.selecionado = false;
        });

        esq.concat(dir).forEach(l => this.esquecerSugestao(l));

        this.esquerda.forEach(e => {
          if (e.sugestao === 1 && dir.some(d => d.idExtrato === e.sugestaoPar)) this.esquecerSugestao(e);
        });

        this.direita.forEach(d => {
          if (d.sugestao === 1 && esq.some(e => e.idLancamento === d.sugestaoPar)) this.esquecerSugestao(d);
        });

        this.grupoAtivo = 0;
        this.linhaAtiva = null;
        this.filtroValor = null;
        this.limparCROutras();
      },
      serialDaBaixa(dataBaixa) {
        if (!dataBaixa) return null;
        const data = moment(dataBaixa, 'YYYY-MM-DD');
        if (!data.isValid()) return null;
        return { serial: moment.utc(dataBaixa, 'YYYY-MM-DD').diff(moment.utc('1899-12-30', 'YYYY-MM-DD'), 'days'), texto: data.format('DD/MM/YYYY') };
      },
      esquecerSugestao(l) {
        l.sugestao = 0; l.sugestaoPar = 0; l.sugestaoCriterio = ''; l.sugestaoConfianca = null;
        l.sugestaoNivel = ''; l.sugestaoConcorrentes = 0;
        l.sugestaoGrupo = 0; l.sugestaoGrupoDescricao = ''; l.sugestaoGrupoQtdeEsq = 0; l.sugestaoGrupoQtdeDir = 0;
      },
      limparBusca() {
        clearTimeout(this.timerBusca);
        this.buscaTexto = '';
        this.buscaAplicada = '';
      },
      restaurarFiltrosTela() {
        clearTimeout(this.timerBusca);
        this.buscaTexto = '';
        this.buscaAplicada = '';
        this.filtroValor = null;
        this.lupa = null;
        this.filtroForma = '';
        this.filtroSituacao = '';
        this.filtroTipo = '';
        if (!this.transitoria && this.filtroLancamento !== this.lancamentoPadrao) {
          this.filtroLancamento = this.lancamentoPadrao;
          this.limparCROutras();
        }
      },
      confirmarDesfazer(idGrupo) {
        this.$swal({
          title: 'Desfazer conciliação',
          text: 'Deseja realmente desfazer esta conciliação?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, desfazer',
          cancelButtonText: 'Cancelar',
        })
          .then(result => {
            if (!(result === true || (result && result.value))) return;
            this.desfazendo = true;
            this.carregando = true;
            this.api.desfazer(this.idProjetoCript, idGrupo)
              .then(() => {
                toastr.success('Conciliação desfeita. Atualizando a tela...');
                return this.processar();
              })
              .catch(erro => {
                this.carregando = false;
                const msg = erro && erro.body && erro.body.message;
                toastr.error(msg || 'Não foi possível desfazer.');
              })
              .then(() => { this.desfazendo = false; });
          })
          .catch(() => {});
      },
      abrirLog() {
        const a = this.alvoLog;
        if (!a) return;
        this.logConciliacao = { grupo: a.grupo, lancamentos: a.lancamentos.slice(), extratos: a.extratos.slice() };
      },
      desfazerGrupoAtivo() {
        if (!this.podeApagarModulo) { toastr.info('Seu usuário não tem permissão para desfazer conciliações.'); return; }
        if (!this.grupoAtivo || this.grupoAtivo <= 0) return;
        this.confirmarDesfazer(this.grupoAtivo);
      },
      justificarSelecionado() {
        if (!this.podeJustificar) return;
        const lado = this.selEsq.length > 0 ? 'esquerda' : 'direita';
        const linhas = lado === 'esquerda' ? this.selEsq.slice() : this.selDir.slice();
        this.abrirJustificativa(lado, linhas, false);
      },
      onObservar({ lado, linha }) {
        if (this.somenteLeitura) { this.avisarSomenteLeitura(); return; }
        this.abrirJustificativa(lado, [linha], true);
      },
      avisarSomenteLeitura() {
        if (!this.podeAlterarModulo) {
          toastr.info('Seu usuário tem acesso apenas de consulta nesta conciliação.');
          return;
        }
        toastr.info('Projeto encerrado: a conciliação está disponível apenas para consulta.');
      },
      abrirJustificativa(lado, linhas, edicao) {
        const lista = (linhas || []).filter(l => l);
        if (lista.length === 0) return;

        const crOutra = lista.filter(l => l.crOutraConta === 1)[0];
        if (crOutra) {
          toastr.warning('Conta a receber de outra conta não pode ser justificada aqui. Concilie com a linha do extrato ou justifique na conciliação de ' + (crOutra.nomeConta || 'conta de origem') + '.');
          return;
        }

        this.justificativa = { lado, linhas: lista, texto: edicao ? (lista[0].observacao || '') : '', edicao };
        this.validarJustificativa = false;
        this.mostrarJustificativa = true;
        this.$nextTick(() => { if (this.$refs.campoJustificativa) this.$refs.campoJustificativa.focus(); });
      },
      abrirAjuste() {
        if (!this.podeAjustarDiferenca || this.ajuste.carregando) return;
        const payload = this.payloadAjuste();
        this.ajuste = { carregando: true, gravando: false, previa: null, escolhido: 0,
                        idsEsq: payload.idsLancamento, idsDir: payload.idsExtrato };
        conciliacaoProjetoServico.previaAjusteDiferenca(this.idProjetoCript, payload)
          .then(previa => {
            this.ajuste.carregando = false;
            this.ajuste.previa = previa;
            const livre = (previa.lancamentos || []).filter(l => !l.bloqueio)[0];
            this.ajuste.escolhido = livre ? livre.idLancamento : 0;
            this.mostrarAjuste = true;
          })
          .catch(res => {
            this.ajuste.carregando = false;
            toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível preparar o ajuste.');
          });
      },
      payloadAjuste(idLancamento = 0) {
        return {
          idsLancamento: this.selEsq.map(l => l.idLancamento).filter(id => id > 0),
          idsExtrato: this.selDir.map(l => l.idExtrato).filter(id => id > 0),
          idLancamento,
          diferenca: Math.round(this.diferenca * 100) / 100,
        };
      },
      escolherAjuste(l) {
        if (l.bloqueio) return;
        this.ajuste.escolhido = l.idLancamento;
      },
      confirmarAjuste() {
        if (!this.ajusteEscolhido || this.ajuste.gravando) return;
        const payload = {
          idsLancamento: this.ajuste.idsEsq,
          idsExtrato: this.ajuste.idsDir,
          idLancamento: this.ajuste.escolhido,
          idLancRateio: this.ajusteEscolhido.idLancRateio,
          diferenca: this.ajuste.previa.diferenca,
        };
        this.ajuste.gravando = true;
        conciliacaoProjetoServico.ajustarDiferenca(this.idProjetoCript, payload)
          .then(r => {
            const idsEsq = payload.idsLancamento, idsDir = payload.idsExtrato;
            this.ajuste.gravando = false;
            this.mostrarAjuste = false;
            toastr.success('Lançamento ' + r.idLancamento + ' ajustado de R$ ' + this.formatar(r.valor) + ' para R$ ' + this.formatar(r.valorNovo) + '.');
            return this.processar().then(() => this.reselecionar(idsEsq, idsDir));
          })
          .catch(res => {
            this.ajuste.gravando = false;
            toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível ajustar a diferença.');
          });
      },
      reselecionar(idsEsq, idsDir) {
        this.esquerda.forEach(l => { if (l.conciliado !== 1 && idsEsq.indexOf(l.idLancamento) !== -1) l.selecionado = true; });
        this.direita.forEach(l => { if (l.conciliado !== 1 && idsDir.indexOf(l.idExtrato) !== -1) l.selecionado = true; });
      },
      fecharAjuste() {
        if (this.ajuste.gravando) return;
        this.mostrarAjuste = false;
      },
      fecharJustificativa() {
        this.mostrarJustificativa = false;
        this.justificativa = { lado: '', linhas: [], texto: '', edicao: false };
      },
      salvarJustificativa() {
        const texto = (this.justificativa.texto || '').trim();
        const linhas = this.justificativa.linhas || [];
        if (linhas.length === 0) return;
        if (!texto) {
          this.validarJustificativa = true;
          toastr.warning('Informe o motivo.');
          this.$nextTick(() => { if (this.$refs.campoJustificativa) this.$refs.campoJustificativa.focus(); });
          return;
        }

        const itens = linhas.map(l => ({
          idLancamento: l.idLancamento,
          idLancRateio: l.idLancRateio,
          idExtrato: l.idExtrato,
          valor: l.valor,
        }));

        const payload = {
          lado: this.justificativa.lado,
          idLancamento: itens[0].idLancamento,
          idLancRateio: itens[0].idLancRateio,
          idExtrato: itens[0].idExtrato,
          texto,
          itens,
        };

        const edicao = this.justificativa.edicao;
        const qtde = itens.length;

        this.salvandoJustificativa = true;
        this.api.observacao(this.idProjetoCript, payload)
          .then(() => {
            this.salvandoJustificativa = false;
            toastr.success(edicao
              ? 'Observação registrada.'
              : (qtde > 1 ? qtde + ' lançamentos conciliados com justificativa.' : 'Justificativa registrada.'));
            this.fecharJustificativa();
            this.processar();
          })
          .catch(erro => {
            this.salvandoJustificativa = false;
            const msg = erro && erro.body && erro.body.message;
            toastr.error(msg || (edicao ? 'Não foi possível salvar a observação.' : 'Não foi possível justificar.'));
          });
      },
      onDesfazerLinha(linha) {
        if (this.bloqueadoContexto) { this.avisarSomenteLeitura(); return; }
        if (!this.podeApagarModulo) { toastr.info('Seu usuário não tem permissão para desfazer conciliações.'); return; }
        if (!linha.idGrupo || linha.idGrupo <= 0) { toastr.warning('Conciliação automática não pode ser desfeita.'); return; }
        this.confirmarDesfazer(linha.idGrupo);
      },
      onInfo(linha) {
        this.lancamentoInfo = linha;
        this.rateiosInfo = [];
        this.criacaoInfo = null;
        if (linha.idLancamento > 0) {
          conciliacaoProjetoServico.criacaoLancamento(this.idProjetoCript, linha.idLancamento)
            .then(c => { if (this.lancamentoInfo === linha) this.criacaoInfo = c || null; })
            .catch(() => {});
          this.carregandoRateios = true;
          conciliacaoProjetoServico.rateios(this.idProjetoCript, linha.idLancamento)
            .then(r => { this.rateiosInfo = r || []; this.carregandoRateios = false; })
            .catch(() => { this.carregandoRateios = false; });
        }
      },
      fecharInfo() { this.lancamentoInfo = null; this.rateiosInfo = []; this.criacaoInfo = null; },
      onVinculos(linha) {
        this.vinculoLinha = linha;
        this.vinculoDados = null;
        this.vinculoMarcados = [];
        this.carregandoVinculo = true;
        conciliacaoProjetoServico.vinculosTransitoria(this.idProjetoCript, linha.idLancamentoVinculo || linha.idLancamento)
          .then(r => { this.vinculoDados = r; this.carregandoVinculo = false; })
          .catch(() => { this.carregandoVinculo = false; toastr.error('Não foi possível carregar os lançamentos vinculados.'); });
      },
      fecharVinculos() { this.vinculoLinha = null; this.vinculoDados = null; this.vinculoMarcados = []; },
      historicoVinculo(i) { return (i.nomePessoa || '').trim() || i.historico || ''; },
      chaveVinculo(secao, i, n) { return secao + ':' + (i.idLancamento || i.idExtrato || ('n' + n)); },
      marcavel(secao) { return this.itensVinculo(secao).length > 1; },
      vinculoMarcado(secao, i, n) { return this.vinculoMarcados.indexOf(this.chaveVinculo(secao, i, n)) >= 0; },
      alternarVinculoMarcado(secao, i, n) {
        if (!this.marcavel(secao)) return;
        const chave = this.chaveVinculo(secao, i, n);
        const pos = this.vinculoMarcados.indexOf(chave);
        if (pos >= 0) this.vinculoMarcados.splice(pos, 1);
        else this.vinculoMarcados.push(chave);
      },
      todosVinculoMarcados(secao) {
        const itens = this.itensVinculo(secao);
        return itens.length > 0 && itens.every((i, n) => this.vinculoMarcado(secao, i, n));
      },
      alternarTodosVinculo(secao, e) {
        const chaves = this.itensVinculo(secao).map((i, n) => this.chaveVinculo(secao, i, n));
        const outras = this.vinculoMarcados.filter(c => chaves.indexOf(c) < 0);
        this.vinculoMarcados = e.target.checked ? outras.concat(chaves) : outras;
      },
      somaVinculo(secao, soMarcados) {
        const soma = this.itensVinculo(secao)
          .filter((i, n) => !soMarcados || this.vinculoMarcado(secao, i, n))
          .reduce((t, i) => t + (+i.valor || 0), 0);
        return Math.round(soma * 100) / 100;
      },
      qtdeVinculoMarcados(secao) {
        return this.itensVinculo(secao).filter((i, n) => this.vinculoMarcado(secao, i, n)).length;
      },
      extratoParaDiferenca(secao) {
        return secao !== 'extrato' && this.itensVinculo('extrato').length ? this.somaVinculo('extrato', false) : null;
      },
      itensVinculo(secao) {
        return this.vinculoDados ? (this.vinculoDados.itens || []).filter(i => i.secao === secao) : [];
      },
      classeSituacaoVinculo(situacao) {
        if (['Conciliado', 'Conciliada', 'Baixado'].includes(situacao)) return 'vs-ok';
        if (['Cancelado', 'Cancelada', 'Erro na baixa'].includes(situacao)) return 'vs-erro';
        if (situacao === 'Linha do extrato') return 'vs-neutro';
        return 'vs-pend';
      },
      onRegularizarLinha(linha) {
        if (!linha || linha.conciliado === 1) return;
        if (this.transitoria) {
          if (!this.podeAlterarModulo) { this.avisarSomenteLeitura(); return; }
          this.abrirRegularizarTransitoria(linha);
          return;
        }
        this.esquerda.forEach(l => { l.selecionado = false; });
        this.direita.forEach(l => { l.selecionado = false; });
        this.onRegularizar(linha);
      },
      onRegularizar(linhaAlvo) {
        if (!this.podeAlterarModulo) { this.avisarSomenteLeitura(); return; }

        if (!linhaAlvo && this.lancamentosSaldo.length) { this.regularizarSaldo(); return; }
        if (!linhaAlvo && this.complementoRegularizavel) { this.regularizarComplemento(); return; }

        const alvo = linhaAlvo && linhaAlvo.idExtrato > 0 ? linhaAlvo : null;
        const grupo = alvo && alvo.grupoExtrato > 0
          ? this.direita.filter(l => l.grupoExtrato === alvo.grupoExtrato && l.idExtrato > 0 && l.conciliado !== 1)
          : [];
        const linhas = alvo ? (grupo.length > 1 ? grupo : [alvo]) : this.linhasRegularizaveis;
        const linha = linhas[0];
        if (!linha || this.detectando) return;

        this.detectando = true;
        conciliacaoProjetoServico.detectarOperacao(this.idProjetoCript, linha.idExtrato, linhas.map(l => l.idExtrato))
          .then(deteccao => {
            this.detectando = false;
            if (grupo.length > 1) {
              deteccao.operacao = 'movimento';
              deteccao.grupoTarifa = true;
            }
            this.linhaRegularizar = linha;
            this.deteccao = deteccao;
            this.mostrarOperacao = true;
          })
          .catch(res => {
            this.detectando = false;
            toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível identificar a operação desta linha.');
          });
      },
      regularizarComplemento() {
        if (this.detectando) return;
        const idsExtrato = this.selDir.map(l => l.idExtrato);
        const idsLancamento = this.selEsq.map(l => l.idLancamento);
        const linha = this.selDir[0];

        this.detectando = true;
        Promise.all([
          conciliacaoProjetoServico.detectarOperacao(this.idProjetoCript, linha.idExtrato, idsExtrato),
          conciliacaoProjetoServico.previaComplemento(this.idProjetoCript, { idsLancamento, idsExtrato }),
        ])
          .then(([deteccao, previa]) => {
            this.detectando = false;
            deteccao.operacao = 'movimento';
            deteccao.operacaoIdentificada = true;
            deteccao.grupoTarifa = false;
            deteccao.valor = previa.valor;
            deteccao.creditoDebito = previa.creditoDebito;
            deteccao.complemento = previa;
            this.linhaRegularizar = linha;
            this.deteccao = deteccao;
            this.mostrarOperacao = true;
          })
          .catch(res => {
            this.detectando = false;
            toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível preparar o lançamento da diferença.');
          });
      },
      regularizarSaldo() {
        if (this.detectando) return;
        if (Math.abs(this.somaEsq) <= TOLERANCIA) {
          toastr.info('A soma dos lançamentos selecionados dá zero — não há o que transferir. Use Conciliar com justificativa.');
          return;
        }
        if (this.somaEsq < 0) {
          toastr.warning('A soma dos lançamentos selecionados é negativa — só dá para transferir quando sobra dinheiro na conta.');
          return;
        }

        this.detectando = true;
        conciliacaoProjetoServico.detectarSaldo(this.idProjetoCript, this.lancamentosSaldo.map(l => l.idLancamento),
          this.boletosSelecionados.map(l => l.idExtrato))
          .then(deteccao => {
            this.detectando = false;
            this.linhaRegularizar = null;
            if (this.dataConciliadora) deteccao.data = this.dataConciliadora;
            this.deteccao = deteccao;
            this.mostrarOperacao = true;
          })
          .catch(res => {
            this.detectando = false;
            toastr.error(res && res.body && res.body.message ? res.body.message : 'Não foi possível preparar a transferência do saldo.');
          });
      },
      fecharOperacao() { this.mostrarOperacao = false; this.deteccao = null; this.linhaRegularizar = null; },
      operacaoConcluida(resultado) {
        this.mostrarOperacao = false;
        this.deteccao = null;
        this.linhaRegularizar = null;
        toastr.success(((resultado && resultado.mensagens) || ['Operação concluída.']).join(' '));
        this.limparFiltros();
      },
      exportarConferencia() {
        try {
          const banco = this.modo === 'banco';
          const cols = banco
            ? ['Grupo', 'Tipo', 'Situação', 'Data (conta)', 'Documento (conta)', 'Histórico (conta)', 'Valor (conta)', 'Data (extrato)', 'Documento (extrato)', 'Histórico (extrato)', 'Valor (extrato)', 'Diferença']
            : ['Grupo', 'Tipo', 'Situação',
               'Data (projeto)', 'Documento (projeto)', 'Doc. banco (projeto)', 'Histórico (projeto)', 'Valor (projeto)',
               'Data (conta bancária)', 'Documento (conta bancária)', 'Doc. banco (conta bancária)', 'Histórico (conta bancária)', 'Valor (conta bancária)'];
          const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
          const val = v => v == null ? '' : v.toFixed(2).replace('.', ',');
          const histE = r => r.semCorrEsq ? 'sem correspondência no sistema' : r.histEsq;
          const histD = r => r.semCorrDir ? 'sem correspondência ' + (banco ? 'no extrato' : 'na conta bancária') : r.histDir;
          const sit = r => r.justif ? 'Justificado' : (r.divergente === 1 ? 'Divergente' : 'Conferido');
          const linhas = [cols.map(esc).join(';')];
          this.confRows.forEach(r => {
            const celulas = banco
              ? [
                  r.primeira && r.grupo > 0 ? r.grupo : '', r.primeira ? r.tipoDesc : '', r.primeira ? sit(r) : '',
                  r.dataEsq, r.docEsq, histE(r), val(r.valorEsq),
                  r.dataDir, r.docDir, histD(r), val(r.valorDir),
                  r.primeira && r.dif != null ? val(r.dif) : ''
                ]
              : [
                  r.primeira && r.grupo > 0 ? r.grupo : '', r.primeira ? r.tipoDesc : '', r.primeira ? sit(r) : '',
                  r.dataEsq, r.docIntEsq, r.docBancoEsq, histE(r), val(r.valorEsq),
                  r.dataDir, r.docIntDir, r.docBancoDir, histD(r), val(r.valorDir)
                ];
            linhas.push(celulas.map(esc).join(';'));
          });
          const blob = new Blob(['﻿' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'conferencia-' + this.modo + '.csv'; a.click(); URL.revokeObjectURL(url);
        } catch (e) {
          console.error('Excel conferência:', e);
          toastr.error('Erro ao gerar o Excel: ' + (e && e.message ? e.message : e));
        }
      },
    },
  }
</script>

<template>
  <div class="conc-page">
    <div class="conc-card">

      <div class="titlebar">
        <span class="dot"></span><span class="tb-txt">LISTA DE CONCILIAÇÃO</span>
      </div>

      <div class="ctx">
        <div class="ctx-l">
          <div class="ctx-id">
          <div class="ctx-nome">{{ nomeProjeto }} <span class="chip">{{ transitoria ? 'CONTA TRANSITÓRIA' : (modo === 'banco' ? 'CONTA × BANCO' : 'PROJETO') }}</span>
            <span v-if="somenteLeitura" class="chip chip-enc" :title="motivoSomenteLeitura.dica">
              <i class="fa fa-lock"></i> {{ motivoSomenteLeitura.chip }}
            </span>
          </div>
          <div class="ctx-conta" v-if="modo !== 'banco'">Conta padrão <b class="mono">{{ nomeContaPadrao || '---' }}</b></div>
          <div class="ctx-conta" v-else>
            <b v-if="nomeBanco || numeroBanco">{{ numeroBanco }}<span v-if="nomeBanco"> · {{ nomeBanco }}</span></b>
            <span v-if="numeroAgencia"> · Ag. <b class="mono">{{ numeroAgencia }}</b></span>
            <span v-if="numeroConta"> · C/C <b class="mono">{{ numeroConta }}</b></span>
          </div>
          </div>
        <div class="ctx-atu">
          <button type="button" class="btn btn-success ctx-atualizar" :disabled="carregando" @click="atualizar" title="Recarrega os lançamentos, o extrato e os cards do período atual, mantendo a data e os filtros">
            <i class="fa fa-refresh" :class="{ 'fa-spin': carregando }"></i> Atualizar
          </button>
          <span class="ctx-atu-hora" v-if="ultimaAtualizacao">atualizado às {{ ultimaAtualizacao }}</span>
        </div>
        </div>
        <div class="ctx-m" v-if="modo === 'banco' && !transitoria && saldoTesouraria">
          <div class="ctx-tile t-op" :class="saldoTesouraria.divergenciaOperacao ? 't-pend-val' : 't-ok'" :title="tituloOperacaoTesouraria()">
            <span class="lbl">Por operação em {{ dataReferenciaTesouraria }}</span>
            <span class="val mono" :class="{ neg: saldoTesouraria.saldoOperacao < 0 }">{{ moeda(saldoTesouraria.saldoOperacao) }}</span>
            <span class="sub" :class="saldoTesouraria.divergenciaOperacao ? 'sub-pend' : 'sub-ok'">
              <i class="fa" :class="saldoTesouraria.divergenciaOperacao ? 'fa-exclamation-circle' : 'fa-check'"></i> {{ subOperacaoTesouraria() }}
            </span>
          </div>
          <div class="ctx-tile t-saldo" :title="'Saldo dos lançamentos conferidos (conciliados) no Manager até o fim de ' + dataReferenciaTesouraria + ', pela data do lançamento; antes da data de corte da conciliação entram todos (mesmo cálculo da Posição de Tesouraria)'">
            <span class="lbl">Saldo em {{ dataReferenciaTesouraria }}</span>
            <span class="val mono" :class="{ neg: saldoTesouraria.saldoConciliado < 0 }">{{ moeda(saldoTesouraria.saldoConciliado) }}</span>
            <span class="sub">conciliado no Manager</span>
          </div>
          <div class="ctx-tile t-extrato" title="Último saldo lido no extrato do banco até a data de referência">
            <span class="lbl">Extrato em {{ dataReferenciaTesouraria }}</span>
            <template v-if="temBancoTesouraria()">
              <span class="val mono" :class="{ neg: saldoTesouraria.saldoBanco < 0 }">{{ moeda(saldoTesouraria.saldoBanco) }}</span>
              <span class="sub">lido em {{ saldoTesouraria.dataSaldoBancoTexto }}</span>
            </template>
            <template v-else>
              <span class="val mono mudo">—</span>
              <span class="sub">sem saldo no extrato</span>
            </template>
          </div>
          <div class="ctx-tile t-dif" :class="'t-' + classeDiferencaTesouraria()" :title="tituloDiferencaTesouraria()">
            <span class="lbl">Diferença</span>
            <span class="val mono" :class="classeDiferencaTesouraria()">
              <template v-if="!temBancoTesouraria()">—</template>
              <template v-else-if="!saldoTesouraria.divergente && !saldoTesouraria.conciliacaoPendente">R$ 0,00</template>
              <template v-else>{{ moedaSinal(saldoTesouraria.divergencia) }}</template>
            </span>
            <span class="sub" v-if="temBancoTesouraria()">{{ saldoTesouraria.conciliacaoPendente ? 'conciliação pendente' : (saldoTesouraria.divergente ? saldoTesouraria.tipoDivergencia : 'banco e Manager batem') }}</span>
          </div>
        </div>
        <div class="ctx-r">
          <template v-if="processado">
            <div class="ctx-prog-top"><span class="lbl">Conciliado</span> <span class="mono">{{ numeroInt(cardConc) }} / {{ numeroInt(cardTotal) }} · <span class="pend">{{ numeroInt(cardPend) }} pendente<span v-if="cardPend !== 1">s</span></span></span></div>
            <div class="ctx-bar"><span :style="{ width: cardPct + '%' }"></span></div>
            <div class="ctx-pct mono">{{ cardPct }}%</div>
          </template>
        </div>
      </div>

      <div class="filtros">
        <div class="f f-per"><label>Período</label><div class="f-periodo">
          <date-picker v-model="dataIni" format="DD/MM/YYYY" lang="pt-br" input-class="form-control" placeholder="dd/mm/aaaa" :editable="false" :shortcuts="false" :clearable="false" @change="onDataChange"></date-picker>
          <span class="ate">até</span>
          <date-picker v-model="dataFim" format="DD/MM/YYYY" lang="pt-br" input-class="form-control" placeholder="dd/mm/aaaa" :editable="false" :shortcuts="false" :clearable="false" @change="onDataChange"></date-picker>
        </div></div>
        <div class="f"><label>Buscar</label>
          <div class="f-busca-wrap">
            <input type="text" class="form-control f-busca" v-model="buscaTexto" placeholder="Nome, CNPJ, histórico..." title="Busca em histórico, cliente/fornecedor, complemento, documento e CPF/CNPJ (com ou sem máscara)">
            <a v-if="buscaTexto" href="#" class="f-busca-x" title="Limpar busca" @click.prevent="limparBusca"><i class="fa fa-times"></i></a>
          </div>
        </div>
        <div class="f"><label>Forma de pgto</label><select class="form-control" v-model="filtroForma"><option value="">Todas</option><option v-for="fp in formasDisponiveis" :value="fp" :key="fp">{{ fp }}</option></select></div>
        <div class="f"><label>Situação</label><select class="form-control" v-model="filtroSituacao"><option value="">Todas</option><option value="1">Automática</option><option value="2">Manual</option><option value="3">{{ transitoria ? 'CR de outra conta' : 'Outra conta' }}</option><option value="4">Justificada</option></select></div>
        <div class="f"><label>Tipo</label><select class="form-control" v-model="filtroTipo"><option value="">Todos</option><option value="1">Crédito</option><option value="2">Débito</option></select></div>
        <div class="f" v-if="modo === 'banco'"><label>Lançamento</label>
          <select class="form-control f-lanc" v-model="filtroLancamento" :title="transitoria ? 'Os executados obedecem ao período e aparecem sempre. Executados + contas a receber junta as em aberto (todas as datas).' : 'Os executados obedecem ao período e aparecem sempre. Executados + CR desta conta junta as contas a receber em aberto desta conta; Executados + CR de outras contas junta as em aberto das demais contas.'">
            <option value="exec">Executados</option>
            <option value="cr">{{ transitoria ? 'Executados + contas a receber' : 'Executados + CR desta conta' }}</option>
            <option v-if="!transitoria" value="cr-outras">Executados + CR de outras contas</option>
          </select>
        </div>
        <div class="f" v-else-if="modo !== 'banco'"><label>Lançamento</label><select class="form-control f-lanc" v-model="filtroLancamento" title="Filtra os lançamentos do projeto pela conciliação no banco"><option value="">Todos</option><option value="conf">Conciliados no banco</option><option value="naoconf">Não conciliados no banco</option><option value="aberto">Em aberto</option></select></div>
        <div class="f"><label>Exibir</label><div class="f-toggles">
          <label class="tgl" title="Este filtro fica mantido quando você limpa os demais."><input type="checkbox" v-model="soNaoConciliados"><span>Só não conciliados</span></label>
          <label class="tgl"><input type="checkbox" v-model="ocultarAplicacao"><span>Ocultar aplicação</span></label>
        </div></div>
        <div class="f" v-if="modo === 'banco' && !transitoria && (totalSugestoes > 0 || modoSugestao)"><label>IA</label>
          <button type="button" class="f-sug" :class="{ ativo: modoSugestao }" @click="alternarModoSugestao"
                  title="Mostra só os pares que a IA apontou — contas a receber e executados juntos. Clicar no ícone da linha marca o par; nada é conciliado sem você confirmar.">
            <i class="fa fa-magic"></i> Sugestões <span class="f-sug-qt">{{ totalSugestoes }}</span>
          </button>
        </div>
        <div class="f f-sug-fim" v-if="modoSugestao && totalSugestoes === 0">
          Nenhuma sugestão pendente. <a href="#" @click.prevent="alternarModoSugestao">Voltar para a grade completa</a>
        </div>
        <div class="f-acoes">
          <span v-if="filtroValor !== null" class="f-valor-chip" :title="dicaChipValor">
            <i class="fa fa-filter"></i> Filtrado por valor <b class="mono">R$ {{ formatar(filtroValorEsq) }}</b>
            <template v-if="transitoria"> / <b class="mono">R$ {{ formatar(filtroValorDir) }}</b></template>
            <a href="#" class="f-valor-x" title="Remover o filtro de valor" @click.prevent="limparFiltroValor"><i class="fa fa-times"></i></a>
          </span>
          <span v-if="lupa" class="f-valor-chip f-lupa-chip" :class="{ mais: lupa.tipo === 'outras' }" :title="dicaChipLupa">
            <i class="fa" :class="lupa.tipo === 'outras' ? 'fa-search-plus' : 'fa-search'"></i>
            {{ lupa.tipo === 'outras' ? 'CR de outras contas' : 'Filtrado por' }} <b>{{ lupa.texto }}</b>
            <a href="#" class="f-valor-x" title="Desfazer o filtro da lupa" @click.prevent="desfazerLupa"><i class="fa fa-times"></i></a>
          </span>
          <button type="button" class="btn-link" :class="{ 'limpar-ativo': temFiltroTela }" @click="limparFiltros">Limpar filtros</button>
          <span v-if="carregando || carregandoCROutras || carregandoCRConta" class="proc-load"><i class="fa fa-spinner fa-pulse"></i> Processando...</span>
        </div>
      </div>

      <div v-if="truncado" class="aviso-teto">
        <i class="fa fa-exclamation-triangle"></i>
        <span>
          Período muito grande — foram carregados
          <b v-if="cortouEsquerda">{{ linhasEsquerdaCarregadas.toLocaleString('pt-BR') }} de {{ totalLinhasEsquerda.toLocaleString('pt-BR') }} lançamentos</b>
          <template v-if="cortouEsquerda && cortouDireita"> e </template>
          <b v-if="cortouDireita">{{ direita.length.toLocaleString('pt-BR') }} de {{ totalLinhasDireita.toLocaleString('pt-BR') }} {{ transitoria ? 'lançamentos da transitória' : 'linhas do extrato' }}</b>,
          priorizando as não conciliadas mais recentes. A conciliação e os totais consideram o período inteiro.
        </span>
        <button type="button" class="aviso-mais" :disabled="carregando" @click="carregarMais">
          <i class="fa" :class="carregando ? 'fa-spinner fa-pulse' : 'fa-plus'"></i> Carregar mais 5.000
        </button>
      </div>

      <div v-if="modo === 'banco' && !transitoria && retroativosForaPeriodo > 0" class="aviso-teto aviso-retro">
        <i class="fa fa-history"></i>
        <span>
          <b>{{ retroativosForaPeriodo === 1 ? '1 lançamento pendente do extrato deste período está' : retroativosForaPeriodo + ' lançamentos pendentes do extrato deste período estão' }} com data anterior (desde {{ retroativoDataInicial }}).</b>
          O banco lançou de forma retroativa: aparecem no PDF deste período, mas com a data de balancete de um dia anterior.
        </span>
        <button type="button" class="aviso-mais" :disabled="carregando" @click="incluirRetroativos">
          <i class="fa fa-calendar"></i> Incluir desde {{ retroativoDataInicial }}
        </button>
      </div>

      <div class="legenda">
        <span class="lg-tit">LEGENDA DAS CORES</span>
        <span class="lg-chip" title="Marcado por você agora, aguardando conciliar."><i class="sw" style="background:#bfd9f7;border-color:#2d6cb3"></i>Selecionado</span>
        <span class="lg-chip" title="Par com valor igual ao que está selecionado do outro lado."><i class="sw" style="background:#fdf0c4;border-color:#d4ab2c"></i>Candidato / vínculo</span>
        <span class="lg-chip" v-if="modo === 'banco' && !transitoria" title="Não é conciliação — a IA só apontou o par. Clique no ícone da linha para marcar os dois lados; nada é gravado até você conciliar."><i class="fa fa-magic lg-ic" style="color:#7c4dbd"></i>Sugestão da IA</span>
        <span class="lg-chip" v-if="!transitoria" title="Casada pelo sistema por data e valor, sem intervenção."><i class="sw" style="background:#e4f2f6;border-color:#8fc9d6"></i>Conciliação automática</span>
        <span class="lg-chip" title="Casada por uma pessoa, inclusive em lote (1:N e N:N)."><i class="sw" style="background:#e5f2ea;border-color:#a9d4b6"></i>Conciliação manual</span>
        <span class="lg-chip" v-if="modo !== 'banco'" title="Já conciliado no banco — a dica aparece na coluna Doc. banco."><i class="fa fa-university lg-ic"></i>Já conciliado no banco</span>
        <template v-if="modo === 'banco'">
          <span class="lg-chip" title="Lançamento baixado, posicionado pela data de movimento."><i class="sit-mk sit-exec lg-sit"></i>Baixado</span>
          <span class="lg-chip" title="Conta a receber em aberto, posicionada pela data de vencimento."><i class="sit-mk sit-aberto lg-sit"></i>Em aberto</span>
          <span class="lg-chip" title="Conta a receber em aberto cadastrada em outra conta bancária. Ao conciliar, a baixa transfere o lançamento para esta conta."><i class="sw" style="background:#fdf3e7;border-color:#d97e26"></i>Conta a receber de outra conta</span>
        </template>
        <template v-else>
          <span class="lg-chip" title="Lançamento baixado e já conciliado no banco (conferido)."><i class="sit-mk sit-conf lg-sit"></i>Conciliado no banco</span>
          <span class="lg-chip" title="Lançamento baixado, mas ainda não conciliado no banco. O par do lado do banco só aparece depois da conciliação bancária."><i class="sit-mk sit-naoconf lg-sit"></i>Não conciliado no banco</span>
          <span class="lg-chip" title="Conta a pagar ou receber em aberto, posicionada pela data de vencimento."><i class="sit-mk sit-aberto lg-sit"></i>Em aberto</span>
        </template>
      </div>

      <div v-if="carregando || !processado" class="carregando">
        <template v-if="carregando"><i class="fa fa-spinner fa-pulse fa-2x"></i><div>{{ desfazendo ? 'Desfazendo a conciliação e atualizando a tela...' : 'Carregando lançamentos...' }}</div></template>
        <template v-else><i class="fa fa-exclamation-triangle fa-2x"></i><div>Não foi possível carregar os lançamentos.</div></template>
      </div>

      <div v-show="processado && !carregando" class="area">
        <grade-conciliacao ref="gradeEsq" :lupa="lupa" :linhas="esquerdaVisivel" lado="esquerda" :titulo="tituloEsq" :candidato-valor="candidatoEsq" :candidato-chaves="candidatoChavesEsq" :vinculos-ativos="vinculoIds" :grupo-ativo="grupoAtivo" :mostrar-projeto="mostrarProjetoEsq" :historico-com-complemento="true" :exigir-conferido="exigirConferido" :permitir-busca-outras="modo === 'banco'" :filtro-valor="filtroValorEsq"
          :restante-servidor="restanteEsquerda" :carregando-servidor="carregando"
          @toggle="onToggle" @marcar-todos="onMarcarTodos" @ativar="onAtivar" @info="onInfo" @vinculos="onVinculos" @desfazer="onDesfazerLinha" @observar="onObservar" @scroll-x="onScrollX" @buscar="onBuscar" @buscar-outras="onBuscarOutras" @sugestao="onSugestao" @bloqueada="onBloqueada" @filtrar-valor="onFiltrarValor" @carregar-servidor="carregarMais"></grade-conciliacao>

        <div class="central">
          <div class="ce-tit">SELEÇÃO ATUAL</div>
          <div class="ce-card">
            <div class="ce-row"><span>{{ rotuloEsq }}</span><b class="mono">R$ {{ formatar(somaEsq) }}</b></div>
            <div class="ce-row"><span>{{ rotuloDir }}</span><b class="mono">R$ {{ formatar(somaDir) }}</b></div>
            <div class="ce-row ce-dif"><span>Diferença</span><b class="mono">R$ {{ formatar(diferenca) }}</b></div>
          </div>
          <div class="ce-cont mono">{{ selEsq.length }} de {{ pendEsq }} pend. | {{ selDir.length }} de {{ pendDir }} pend.</div>

          <template v-if="podeDesfazer">
            <div class="ce-info">Grupo <b>{{ descTipo(linhaAtiva.tipoConc) }}</b> selecionado.</div>
            <button class="ce-btn ce-desfazer" :disabled="desfazendo" @click="desfazerGrupoAtivo"><i class="fa fa-undo"></i> Desfazer conciliação</button>
          </template>
          <div v-else-if="somenteLeitura" class="ce-encerrado">
            <i class="fa fa-lock"></i> {{ motivoSomenteLeitura.titulo }}
            <span class="ce-encerrado-aj">{{ motivoSomenteLeitura.dica }}</span>
          </div>
          <template v-else>
            <div v-if="grupoSugeridoSelecionado" class="ce-sug">
              <i class="fa fa-magic"></i> Grupo sugerido
              <em>{{ grupoSugeridoSelecionado.sugestaoGrupoDescricao }}</em>
              <span class="ce-sug-aj">
                {{ grupoSugeridoSelecionado.sugestaoGrupoQtdeEsq }} lançamento(s) da conta ×
                {{ grupoSugeridoSelecionado.sugestaoGrupoQtdeDir }} linha(s) do extrato. Confira antes de conciliar.
              </span>
            </div>
            <div v-else-if="sugestaoSelecionada" class="ce-sug">
              <i class="fa fa-magic"></i> Par sugerido pela IA
              <em>{{ { alta: 'confiança alta', media: 'confiança média', baixa: 'confiança baixa' }[sugestaoSelecionada.sugestaoNivel] || 'confiança baixa' }}</em>
              <span v-if="sugestaoSelecionada.sugestaoConcorrentes > 1" class="ce-sug-aj ce-sug-disputa">
                {{ sugestaoSelecionada.sugestaoConcorrentes }} linhas do extrato apontam este mesmo lançamento — só uma pode conciliar.
              </span>
              <span v-if="sugestaoSelecionada.sugestaoCriterio === 'equivalente'" class="ce-sug-aj">
                A IA apontou um lançamento já conciliado — este é o único pendente com mesma data e valor.
              </span>
              <span class="ce-sug-aj">Confira antes de conciliar — a IA só aponta.</span>
            </div>
            <div v-if="podeConciliar" class="ce-ok"><span class="ce-ok-dot">✓</span> Valores conferem nos dois lados. Pronto para conciliar.</div>
            <div v-else class="ce-info">{{ statusMsg }}</div>
            <button v-if="podeAjustarDiferenca" class="ce-btn ce-ajuste" :disabled="ajuste.carregando" @click="abrirAjuste"
                    title="Corrige o valor de um lançamento selecionado (e do primeiro rateio) para zerar a diferença">
              <i class="fa" :class="ajuste.carregando ? 'fa-spinner fa-pulse' : 'fa-sliders'"></i> Ajustar diferença de R$ {{ formatar(Math.abs(diferenca)) }}
            </button>
            <button class="ce-btn ce-conciliar" :disabled="!podeConciliar || salvando" @click="conciliar"><i class="fa" :class="salvando ? 'fa-spinner fa-pulse' : 'fa-check'"></i> {{ crAbertoSelecionadoTr ? 'Baixar contas a receber' : 'Conciliar' }}</button>
<button class="ce-btn ce-sec" :disabled="!podeJustificar" @click="justificarSelecionado"><i class="fa fa-file-text-o"></i> Conciliar com justificativa{{ qtdeJustificar > 1 ? ' (' + qtdeJustificar + ')' : '' }}</button>
            <button v-if="sugestaoSelecionada" class="ce-btn ce-sec ce-descartar" @click="descartarSugestao"
                    title="Some com esta sugestão. Não mexe nos lançamentos."><i class="fa fa-ban"></i> Descartar sugestão</button>
            <button v-if="modo === 'banco' && !transitoria" class="ce-btn ce-sec ce-cred" :disabled="!podeRegularizar || detectando" @click="onRegularizar()"
                    :title="dicaBotaoRegularizar"><i class="fa" :class="detectando ? 'fa-spinner fa-pulse' : 'fa-random'"></i> Regularizar</button>
            <button v-if="transitoria && podeAlterarModulo" class="ce-btn ce-sec ce-cred" :disabled="!debitoSelecionadoTr" @click="abrirRegularizarTransitoria()"
                    title="Selecione um débito da transitória — cria o contas a receber, a transferência para a conta certa ou os dois"><i class="fa fa-random"></i> Regularizar</button>
          </template>

          <button v-if="alvoLog" class="ce-btn ce-sec ce-log" @click="abrirLog"
                  title="Mostra o que o log da conciliação Web gravou sobre este grupo ou sobre os lançamentos e linhas do extrato selecionados"><i class="fa fa-history"></i> {{ rotuloLog }}</button>

          <div class="ce-nota">Marque lançamentos de um lado — eles ficam <b>azuis</b>. Quando o valor casa, o par fica <b>amarelo</b> nos dois lados. Dê <b>duplo clique</b> para selecionar; use <b>Conciliar</b> para gravar.</div>
        </div>

        <grade-conciliacao ref="gradeDir" :lupa="lupa" :linhas="direitaVisivel" lado="direita" :titulo="tituloDir" :candidato-valor="candidatoDir" :candidato-chaves="candidatoChavesDir" :vinculos-ativos="vinculoIds" :grupo-ativo="grupoAtivo" :mostrar-projeto="mostrarProjetoDir" :historico-com-complemento="transitoria" :exigir-conferido="exigirConferido" :permitir-busca-outras="modo === 'banco' && !transitoria" :permitir-regularizar="modo === 'banco' && podeAlterarModulo && !transitoria" :regularizar-debito="transitoria && podeAlterarModulo && !somenteLeitura" :filtro-valor="filtroValorDir"
          @toggle="onToggle" @marcar-todos="onMarcarTodos" @ativar="onAtivar" @info="onInfo" @vinculos="onVinculos" @desfazer="onDesfazerLinha" @observar="onObservar" @scroll-x="onScrollX" @buscar="onBuscar" @buscar-outras="onBuscarOutras" @sugestao="onSugestao" @bloqueada="onBloqueada" @filtrar-valor="onFiltrarValor" @regularizar="onRegularizarLinha"></grade-conciliacao>
      </div>

      <div class="rodape">
        <div class="rd-dica">Conciliação em lote disponível: marque vários de cada lado (1:N e N:N).</div>
        <div class="rd-btns">
          <button v-if="modo === 'banco' && !transitoria" class="rd-btn rd-azul" @click="mostrarExtratosConta = true"><i class="fa fa-file-pdf-o"></i> Extratos da conta</button>
          <button class="rd-btn" @click="abrirConferencia"><i class="fa fa-check-square-o"></i> Conferir</button>
          <button class="rd-btn" @click="exportarConferencia"><i class="fa fa-table"></i> Exportar Excel</button>
          <button class="rd-btn rd-sair" @click="voltar" :title="veioDaTesouraria ? 'Volta para a Posição de Tesouraria' : ''"><i class="fa fa-sign-out"></i> Sair</button>
        </div>
      </div>

    </div>

    <div v-if="mostrarConferencia" class="cf-ov">
      <div class="cf-modal" :class="{ 'cf-semdif': modo !== 'banco' }" role="dialog" aria-modal="true" aria-label="Conferência da conciliação">
        <div class="cf-head">
          <div class="cf-head-l">
            <h4>Conferência da conciliação</h4>
            <span v-if="numeroConta" class="cf-chip">C/C {{ numeroConta }}</span>
            <span class="cf-sub">{{ numeroBanco }}<template v-if="nomeBanco"> · {{ nomeBanco }}</template><template v-if="confPeriodoLabel"> · {{ confPeriodoLabel }}</template></span>
          </div>
        </div>

        <div class="cf-cards">
          <div class="cf-card"><div class="cf-clb">{{ modo === 'banco' ? 'Total conta' : 'Total projeto' }}</div><div class="cf-cvl">R$ {{ formatar(conferencia.totEsq) }}</div></div>
          <div class="cf-card"><div class="cf-clb">{{ modo === 'banco' ? 'Total extrato' : 'Total conta bancária' }}</div><div class="cf-cvl">R$ {{ formatar(conferencia.totDir) }}</div></div>
          <div class="cf-card" :class="temDif ? 'cf-card-dif' : 'cf-card-ok'">
            <div class="cf-clb">Diferença</div>
            <div class="cf-cvl">R$ {{ formatar(Math.abs(conferencia.diferenca)) }}<span class="cf-caux">{{ temDif ? ('em ' + conferencia.semPar + ' lançamento' + (conferencia.semPar === 1 ? '' : 's')) : 'todos os grupos conferem' }}</span></div>
          </div>
          <div class="cf-card">
            <div class="cf-crow"><div class="cf-clb">Conferido</div><div class="cf-cmeta">{{ conferencia.qtdConf }} / {{ conferencia.qtdGrupos }} grupos · <span class="cf-semp">{{ conferencia.semPar }} sem par</span></div></div>
            <div class="cf-bar"><span :style="{ width: confPctConf + '%' }"></span></div>
          </div>
        </div>

        <div class="cf-view">
          <div class="cf-seg">
            <button :class="{ ativo: modoConf === 'todos' }" @click="setModoConf('todos')">Todos</button>
            <button :class="{ ativo: modoConf === 'div' }" @click="setModoConf('div')">Só divergências</button>
            <button :class="{ ativo: modoConf === 'ok' }" @click="setModoConf('ok')">Só conferidos</button>
          </div>
          <div class="cf-busca"><i class="fa fa-search"></i><input v-model="buscaConf" placeholder="Buscar histórico, documento ou grupo"></div>
          <span class="cf-cont">{{ confGruposVisiveis }} de {{ confGruposTotal }} grupos</span>
        </div>

        <div class="cf-scroll">
          <div class="cf-thead">
            <div class="cf-hgrp">
              <div style="grid-column: span 2"></div>
              <div class="cf-hg" style="grid-column: span 4">{{ modo === 'banco' ? 'Lado conta · Sistema' : 'Lado projeto · Sistema' }}</div>
              <div class="cf-hg cf-hb" style="grid-column: span 4">{{ modo === 'banco' ? 'Lado extrato · Banco' : 'Lado conta bancária do projeto' }}</div>
              <div v-if="modo === 'banco'"></div>
            </div>
            <div class="cf-hcol">
              <div>Grupo</div><div>Tipo</div><div>Data</div><div>Documento</div><div>Histórico</div><div class="tr">Valor</div>
              <div class="cf-hb">Data</div><div>Documento</div><div>Histórico</div><div class="tr">Valor</div><div v-if="modo === 'banco'" class="tr">Dif.</div>
            </div>
          </div>
          <div v-for="(r, idx) in confRows" :key="idx" class="cf-row" :class="{ 'cf-rdiv': r.divergente === 1 }">
            <div class="cf-mono cf-mut">{{ r.primeira && r.grupo > 0 ? r.grupo : '' }}</div>
            <div class="cf-nw">{{ r.primeira ? r.tipoDesc : '' }}</div>
            <div class="cf-mono cf-nw">{{ r.dataEsq }}</div>
            <div class="cf-mono">{{ r.docEsq }}</div>
            <div class="cf-hist">
              <div v-if="r.semCorrEsq" class="cf-semcor">sem correspondência no sistema</div>
              <div v-else :title="r.histEsq" class="cf-clamp">{{ r.histEsq }}</div>
            </div>
            <div class="cf-mono tr cf-val" :class="valCls(r.valorEsq)">{{ r.semCorrEsq ? '—' : (r.valorEsq !== null ? 'R$ ' + formatar(r.valorEsq) : '') }}</div>
            <div class="cf-mono cf-nw cf-hb">{{ r.dataDir }}</div>
            <div class="cf-mono">{{ r.docDir }}</div>
            <div class="cf-hist">
              <div v-if="r.semCorrDir" class="cf-semcor">sem correspondência {{ modo === 'banco' ? 'no extrato' : 'na conta bancária' }}</div>
              <div v-else :title="r.histDir" class="cf-clamp">{{ r.histDir }}</div>
            </div>
            <div class="cf-mono tr cf-val" :class="valCls(r.valorDir)">{{ r.semCorrDir ? '—' : (r.valorDir !== null ? 'R$ ' + formatar(r.valorDir) : '') }}</div>
            <div v-if="modo === 'banco'" class="cf-mono tr cf-dif" :class="{ 'cf-difzero': !r.divergente }">{{ r.primeira ? (r.divergente ? ('R$ ' + formatar(r.dif)) : '0,00') : '' }}</div>
          </div>
          <div v-if="confRows.length === 0" class="cf-vazio">Nenhum grupo corresponde aos filtros.</div>
        </div>

        <div class="cf-foot">
          <div class="cf-leg">
            <span class="cf-legi"><span class="cf-sw cf-swdiv"></span> Sem correspondência</span>
            <span class="cf-legi"><span class="cf-sw cf-swok"></span> Conferido · diferença R$ 0,00</span>
          </div>
          <div class="cf-foot-r">
            <div class="cf-footr">Soma das divergências exibidas: <b>R$ {{ formatar(confSomaVisiveis) }}</b></div>
            <button class="cf-fechar" @click="exportarConferencia"><i class="fa fa-table"></i> Excel</button>
            <button class="rd-btn rd-sair" @click="fecharConferencia"><i class="fa fa-sign-out"></i> Sair</button>
          </div>
        </div>
      </div>
    </div>

    <log-conciliacao-modal v-if="logConciliacao" :id-conta-cript="idProjetoCript" :nome-conta="nomeContaPadrao"
                           :grupo="logConciliacao.grupo" :lancamentos="logConciliacao.lancamentos" :extratos="logConciliacao.extratos"
                           @fechar="logConciliacao = null"></log-conciliacao-modal>
    <extratos-conta-modal v-if="mostrarExtratosConta" :id-conta="contaPadraoId" :nome-conta="nomeContaPadrao" :data-ini="dataIni" :data-fim="dataFim"
                          @fechar="mostrarExtratosConta = false"></extratos-conta-modal>

    <div v-if="lancamentoInfo" class="ov">
      <div class="ov-modal" style="max-width:560px">
        <div class="ov-head"><h4>Informações do lançamento</h4></div>
        <div class="ov-corpo" style="padding:16px">
          <table class="info-tab">
            <tr><th>ID</th><td class="mono">{{ lancamentoInfo.idExtrato > 0 ? lancamentoInfo.idExtrato : lancamentoInfo.idLancamento }}</td></tr>
            <tr><th>Data</th><td class="mono">{{ lancamentoInfo.dataStr }}</td></tr>
            <tr><th>Conta</th><td>{{ lancamentoInfo.nomeConta }}</td></tr>
            <tr><th>Forma pgto banco</th><td>{{ lancamentoInfo.formaPagBanco }}</td></tr>
            <tr><th>Documento</th><td class="mono">{{ lancamentoInfo.documento }}</td></tr>
            <tr v-if="lancamentoInfo.documentoReceberPagar"><th>Doc. a receber/pagar</th><td class="mono">{{ lancamentoInfo.documentoReceberPagar }}</td></tr>
            <tr v-if="lancamentoInfo.notaFiscal"><th>Nota fiscal</th><td class="mono">{{ lancamentoInfo.notaFiscal }}</td></tr>
            <tr><th>Doc. banco</th><td class="mono">{{ lancamentoInfo.documentoBanco }}</td></tr>
            <tr><th>Histórico</th><td>{{ lancamentoInfo.historico }}</td></tr>
            <tr><th>Complemento</th><td>{{ lancamentoInfo.complemento }}</td></tr>
            <tr v-if="lancamentoInfo.idLancamento > 0"><th>Categoria financeira</th><td>{{ categoriaInfo }}</td></tr>
            <tr><th>CPF/CNPJ</th><td class="mono">{{ lancamentoInfo.cpfcnpj }}</td></tr>
            <tr><th>Valor</th><td class="mono" :style="{ color: lancamentoInfo.valor < 0 ? '#a13c3c' : '#0a6d4d' }"><b>R$ {{ formatar(lancamentoInfo.valor) }}</b></td></tr>
            <tr><th>Situação</th><td>{{ lancamentoInfo.conciliado === 1 ? descTipo(lancamentoInfo.tipoConc) : 'Não conciliado' }}</td></tr>
            <tr v-if="criacaoInfo && (criacaoInfo.usuario || criacaoInfo.dataHora)"><th>Incluído por</th>
              <td>{{ criacaoInfo.usuario || 'Usuário não informado' }}<span v-if="criacaoInfo.dataHora" class="mono muted"> · {{ criacaoInfo.dataHora }}</span></td></tr>
            <tr v-if="lancamentoInfo.observacao"><th>Observação</th><td>{{ lancamentoInfo.observacao }}</td></tr>
          </table>

          <div class="info-rateios">
            <div class="info-rt-tit">Rateios</div>
            <div v-if="carregandoRateios" class="muted"><i class="fa fa-spinner fa-pulse"></i> Carregando rateios...</div>
            <table v-else-if="rateiosInfo.length > 0" class="rt-tab">
              <thead><tr><th>Projeto</th><th>Rubrica / Subrubrica</th><th class="tr">Valor</th></tr></thead>
              <tbody>
                <tr v-for="(rt, i) in rateiosInfo" :key="i">
                  <td>{{ rt.nomeProjeto }}</td>
                  <td>{{ rt.nomeRubrica }}<span v-if="rt.nomeSubrubrica"> · {{ rt.nomeSubrubrica }}</span></td>
                  <td class="tr mono" :style="{ color: rt.valor < 0 ? '#a13c3c' : '#0a6d4d' }">R$ {{ formatar(rt.valor) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="muted">Este lançamento não possui rateios.</div>
          </div>
        </div>
        <div class="ov-head" style="border-top:1px solid #dfe3e8;border-bottom:none;justify-content:flex-end">
          <button class="rd-btn rd-sair" @click="fecharInfo"><i class="fa fa-sign-out"></i> Sair</button>
        </div>
      </div>
    </div>

    <div v-if="vinculoLinha" class="ov">
      <div class="ov-modal" style="max-width:1120px">
        <div class="ov-head">
          <h4><i class="fa fa-usd" style="color:#2d6cb3"></i> Lançamentos vinculados</h4>
          <span v-if="vinculoDados" class="vinc-selo" :class="vinculoDados.situacao === 1 ? 'vinc-selo-trans' : 'vinc-selo-conc'">
            {{ vinculoDados.situacao === 1 ? 'Em transição' : 'Conciliado' }}<template v-if="vinculoDados.grupos && vinculoDados.grupos.length"> · grupo {{ vinculoDados.grupos.join(', ') }}</template>
          </span>
        </div>
        <div class="ov-corpo vinc-corpo">
          <div v-if="carregandoVinculo" class="muted"><i class="fa fa-spinner fa-pulse"></i> Carregando lançamentos vinculados...</div>
          <template v-else-if="vinculoDados">
            <div class="vinc-sec">
              <div class="info-rt-tit">Extrato de origem</div>
              <table v-if="itensVinculo('extrato').length" class="rt-tab">
                <thead><tr><th class="vinc-ck"><input type="checkbox" :disabled="!marcavel('extrato')" :checked="todosVinculoMarcados('extrato')" title="Marcar todos" @change="alternarTodosVinculo('extrato', $event)"></th><th>ID</th><th>Data</th><th>Conta</th><th>Histórico</th><th>Documento</th><th class="tr">Valor</th><th>Situação</th></tr></thead>
                <tbody>
                  <tr v-for="(i, n) in itensVinculo('extrato')" :key="'e' + i.idExtrato"
                      :class="[{ 'vinc-atual': vinculoLinha.idExtrato > 0 && i.idExtrato === vinculoLinha.idExtrato }, { 'vinc-marcavel': marcavel('extrato'), 'vinc-marcado': vinculoMarcado('extrato', i, n) }]"
                      @click="alternarVinculoMarcado('extrato', i, n)">
                    <td class="vinc-ck"><input type="checkbox" :disabled="!marcavel('extrato')" :checked="vinculoMarcado('extrato', i, n)" @click.stop="alternarVinculoMarcado('extrato', i, n)"></td>
                    <td class="mono">{{ i.idExtrato }}</td><td class="mono">{{ i.dataStr }}</td><td>{{ i.nomeConta }}</td>
                    <td>{{ i.historico }}</td><td class="mono">{{ i.documento }}</td>
                    <td class="tr mono" :class="i.valor < 0 ? 'neg' : 'pos'">R$ {{ formatar(i.valor) }}</td>
                    <td><span class="vinc-sit" :class="classeSituacaoVinculo(i.situacao)">{{ i.situacao }}</span></td>
                  </tr>
                </tbody>
                <rodape-vinculo :qtde="itensVinculo('extrato').length" :total="somaVinculo('extrato', false)" :qtde-marcados="qtdeVinculoMarcados('extrato')" :soma-marcados="somaVinculo('extrato', true)" :extrato="extratoParaDiferenca('extrato')" :colunas-antes="6" :colunas-depois="1"></rodape-vinculo>
              </table>
              <div v-else class="muted">Nenhuma linha de extrato ligada a este grupo.</div>
            </div>

            <div v-if="itensVinculo('grupo').length" class="vinc-sec">
              <div class="info-rt-tit">Lançamentos conciliados no grupo</div>
              <table class="rt-tab">
                <thead><tr><th class="vinc-ck"><input type="checkbox" :disabled="!marcavel('grupo')" :checked="todosVinculoMarcados('grupo')" title="Marcar todos" @change="alternarTodosVinculo('grupo', $event)"></th><th>ID</th><th>Data</th><th>Conta</th><th>Histórico</th><th>Documento</th><th class="tr">Valor</th><th>Situação</th></tr></thead>
                <tbody>
                  <tr v-for="(i, n) in itensVinculo('grupo')" :key="'g' + i.idLancamento"
                      :class="[{ 'vinc-atual': i.idLancamento === vinculoLinha.idLancamento }, { 'vinc-marcavel': marcavel('grupo'), 'vinc-marcado': vinculoMarcado('grupo', i, n) }]"
                      @click="alternarVinculoMarcado('grupo', i, n)">
                    <td class="vinc-ck"><input type="checkbox" :disabled="!marcavel('grupo')" :checked="vinculoMarcado('grupo', i, n)" @click.stop="alternarVinculoMarcado('grupo', i, n)"></td>
                    <td class="mono">{{ i.idLancamento }}</td><td class="mono">{{ i.dataStr }}</td><td>{{ i.nomeConta }}</td>
                    <td>{{ historicoVinculo(i) }}</td><td class="mono">{{ i.documento }}</td>
                    <td class="tr mono" :class="i.valor < 0 ? 'neg' : 'pos'">R$ {{ formatar(i.valor) }}</td>
                    <td><span class="vinc-sit" :class="classeSituacaoVinculo(i.situacao)">{{ i.situacao }}</span></td>
                  </tr>
                </tbody>
                <rodape-vinculo :qtde="itensVinculo('grupo').length" :total="somaVinculo('grupo', false)" :qtde-marcados="qtdeVinculoMarcados('grupo')" :soma-marcados="somaVinculo('grupo', true)" :extrato="extratoParaDiferenca('grupo')" :colunas-antes="6" :colunas-depois="1"></rodape-vinculo>
              </table>
            </div>

            <div v-if="vinculoDados.transitoria" class="vinc-sec">
              <div class="info-rt-tit">Transferência com a transitória</div>
              <table v-if="itensVinculo('par').length" class="rt-tab">
                <thead><tr><th class="vinc-ck"><input type="checkbox" :disabled="!marcavel('par')" :checked="todosVinculoMarcados('par')" title="Marcar todos" @change="alternarTodosVinculo('par', $event)"></th><th>ID</th><th>Data</th><th>Conta</th><th>Histórico</th><th>Documento</th><th class="tr">Valor</th><th>Situação</th></tr></thead>
                <tbody>
                  <tr v-for="(i, n) in itensVinculo('par')" :key="'p' + i.idLancamento"
                      :class="[{ 'vinc-atual': i.idLancamento === vinculoLinha.idLancamento }, { 'vinc-marcavel': marcavel('par'), 'vinc-marcado': vinculoMarcado('par', i, n) }]"
                      @click="alternarVinculoMarcado('par', i, n)">
                    <td class="vinc-ck"><input type="checkbox" :disabled="!marcavel('par')" :checked="vinculoMarcado('par', i, n)" @click.stop="alternarVinculoMarcado('par', i, n)"></td>
                    <td class="mono">{{ i.idLancamento }}</td><td class="mono">{{ i.dataStr }}</td><td>{{ i.nomeConta }}</td>
                    <td>{{ historicoVinculo(i) }}</td><td class="mono">{{ i.documento }}</td>
                    <td class="tr mono" :class="i.valor < 0 ? 'neg' : 'pos'">R$ {{ formatar(i.valor) }}</td>
                    <td><span class="vinc-sit" :class="classeSituacaoVinculo(i.situacao)">{{ i.situacao }}</span></td>
                  </tr>
                </tbody>
                <rodape-vinculo :qtde="itensVinculo('par').length" :total="somaVinculo('par', false)" :qtde-marcados="qtdeVinculoMarcados('par')" :soma-marcados="somaVinculo('par', true)" :extrato="extratoParaDiferenca('par')" :colunas-antes="6" :colunas-depois="1"></rodape-vinculo>
              </table>
              <div v-else class="muted">Nenhum débito na transitória.</div>
            </div>

            <div v-if="itensVinculo('transferencia').length" class="vinc-sec">
              <div class="info-rt-tit">Transferências / solicitações aos destinos</div>
              <table class="rt-tab">
                <thead><tr><th class="vinc-ck"><input type="checkbox" :disabled="!marcavel('transferencia')" :checked="todosVinculoMarcados('transferencia')" title="Marcar todos" @change="alternarTodosVinculo('transferencia', $event)"></th><th>ID</th><th>Data</th><th>Conta</th><th>Histórico</th><th>Documento</th><th class="tr">Valor</th><th>Situação</th></tr></thead>
                <tbody>
                  <tr v-for="(i, n) in itensVinculo('transferencia')" :key="'t' + n"
                      :class="{ 'vinc-marcavel': marcavel('transferencia'), 'vinc-marcado': vinculoMarcado('transferencia', i, n) }"
                      @click="alternarVinculoMarcado('transferencia', i, n)">
                    <td class="vinc-ck"><input type="checkbox" :disabled="!marcavel('transferencia')" :checked="vinculoMarcado('transferencia', i, n)" @click.stop="alternarVinculoMarcado('transferencia', i, n)"></td>
                    <td class="mono">{{ i.idLancamento > 0 ? i.idLancamento : '' }}<span v-if="i.idSolicitacao > 0" class="muted"> sol. {{ i.idSolicitacao }}</span></td>
                    <td class="mono">{{ i.dataStr }}</td><td>{{ i.nomeConta }}</td><td><span class="muted">Para</span> {{ i.nomeContaDestino }}</td><td class="mono">{{ i.documento }}</td>
                    <td class="tr mono">R$ {{ formatar(i.valor) }}</td>
                    <td><span class="vinc-sit" :class="classeSituacaoVinculo(i.situacao)">{{ i.situacao }}</span></td>
                  </tr>
                </tbody>
                <rodape-vinculo :qtde="itensVinculo('transferencia').length" :total="somaVinculo('transferencia', false)" :qtde-marcados="qtdeVinculoMarcados('transferencia')" :soma-marcados="somaVinculo('transferencia', true)" :extrato="extratoParaDiferenca('transferencia')" :colunas-antes="6" :colunas-depois="1"></rodape-vinculo>
              </table>
            </div>

            <div v-if="vinculoDados.transitoria || itensVinculo('cr').length" class="vinc-sec">
              <div class="info-rt-tit">Contas a receber</div>
              <table v-if="itensVinculo('cr').length" class="rt-tab">
                <thead><tr><th class="vinc-ck"><input type="checkbox" :disabled="!marcavel('cr')" :checked="todosVinculoMarcados('cr')" title="Marcar todos" @change="alternarTodosVinculo('cr', $event)"></th><th>ID</th><th>Data</th><th>Conta</th><th>Histórico</th><th>Documento</th><th class="tr">Valor</th><th>Situação</th></tr></thead>
                <tbody>
                  <tr v-for="(i, n) in itensVinculo('cr')" :key="'c' + i.idLancamento"
                      :class="[{ 'vinc-atual': i.idLancamento === vinculoLinha.idLancamento }, { 'vinc-marcavel': marcavel('cr'), 'vinc-marcado': vinculoMarcado('cr', i, n) }]"
                      @click="alternarVinculoMarcado('cr', i, n)">
                    <td class="vinc-ck"><input type="checkbox" :disabled="!marcavel('cr')" :checked="vinculoMarcado('cr', i, n)" @click.stop="alternarVinculoMarcado('cr', i, n)"></td>
                    <td class="mono">{{ i.idLancamento }}</td><td class="mono">{{ i.dataStr }}</td><td>{{ i.nomeConta }}</td>
                    <td>{{ historicoVinculo(i) }}</td>
                    <td class="mono">
                      <div v-if="i.documento">{{ i.documento }}</div>
                      <template v-if="i.notaFiscal">
                        <div>NF {{ i.notaFiscal }}</div>
                        <div v-if="i.notaFiscalEmissao || i.notaFiscalRPS" class="muted" style="font-size:11px">
                          <template v-if="i.notaFiscalEmissao">Emissão {{ i.notaFiscalEmissao }}</template><template v-if="i.notaFiscalEmissao && i.notaFiscalRPS"> · </template><template v-if="i.notaFiscalRPS">RPS {{ i.notaFiscalRPS }}</template>
                        </div>
                      </template>
                      <span v-if="!i.documento && !i.notaFiscal" class="muted">—</span>
                    </td>
                    <td class="tr mono pos">R$ {{ formatar(i.valor) }}</td>
                    <td><span class="vinc-sit" :class="classeSituacaoVinculo(i.situacao)" :title="i.detalhe">{{ i.situacao }}</span></td>
                  </tr>
                </tbody>
                <rodape-vinculo :qtde="itensVinculo('cr').length" :total="somaVinculo('cr', false)" :qtde-marcados="qtdeVinculoMarcados('cr')" :soma-marcados="somaVinculo('cr', true)" :extrato="extratoParaDiferenca('cr')" :colunas-antes="6" :colunas-depois="1"></rodape-vinculo>
              </table>
              <div v-else class="muted">Nenhum contas a receber vinculado.</div>
            </div>
          </template>
        </div>
        <div class="ov-head" style="border-top:1px solid #dfe3e8;border-bottom:none;justify-content:flex-end">
          <button class="rd-btn rd-sair" @click="fecharVinculos"><i class="fa fa-sign-out"></i> Sair</button>
        </div>
      </div>
    </div>

    <div v-if="mostrarAjuste && ajuste.previa" class="ov">
      <div class="ov-modal just-modal aj-modal">
        <div class="env-head">
          <div>
            <h4>Ajustar diferença</h4>
            <small class="env-sub">
              Os lançamentos selecionados somam <b>R$ {{ formatar(Math.abs(ajuste.previa.diferenca)) }}</b>
              {{ ajuste.previa.diferenca > 0 ? 'a mais' : 'a menos' }} que o extrato. Escolha o lançamento que absorve a diferença:
              o valor dele e o do primeiro rateio são corrigidos, e o lançamento continua batendo com o rateio.
            </small>
          </div>
        </div>

        <div class="ov-corpo just-corpo">
          <div class="just-lista aj-lista">
            <div v-for="l in ajuste.previa.lancamentos" :key="'aj-' + l.idLancamento" class="just-linha aj-linha"
                 :class="{ 'aj-ativo': ajuste.escolhido === l.idLancamento, 'aj-bloq': !!l.bloqueio }"
                 @click="escolherAjuste(l)">
              <input type="radio" :checked="ajuste.escolhido === l.idLancamento" :disabled="!!l.bloqueio">
              <span class="mono muted">{{ l.idLancamento }}</span>
              <span class="mono">{{ l.data }}</span>
              <span class="aj-cd" :class="l.creditoDebito === 2 ? 'neg' : ''">{{ l.creditoDebito === 2 ? 'D' : 'C' }}</span>
              <span class="just-hist" :title="l.historico">{{ l.historico }}</span>
              <span class="just-valor mono">R$ {{ formatar(l.valor) }}</span>
              <span v-if="l.bloqueio" class="aj-motivo">{{ l.bloqueio }}</span>
              <span v-else class="aj-novo mono"><i class="fa fa-long-arrow-right"></i> R$ {{ formatar(l.valorNovo) }}</span>
            </div>
          </div>

          <div v-if="ajusteEscolhido" class="aj-detalhe">
            <div class="aj-det-linha">
              <span class="just-lbl">Lançamento {{ ajusteEscolhido.idLancamento }}</span>
              <span class="mono">R$ {{ formatar(ajusteEscolhido.valor) }} <i class="fa fa-long-arrow-right"></i> <b>R$ {{ formatar(ajusteEscolhido.valorNovo) }}</b></span>
            </div>
            <div class="aj-det-linha">
              <span class="just-lbl">Rateio {{ ajusteEscolhido.quantidadeRateios > 1 ? '(sorteado entre ' + ajusteEscolhido.quantidadeRateios + ')' : '' }}</span>
              <span class="mono">R$ {{ formatar(ajusteEscolhido.valorRateio) }} <i class="fa fa-long-arrow-right"></i> <b>R$ {{ formatar(ajusteEscolhido.valorRateioNovo) }}</b></span>
            </div>
            <div class="aj-det-proj">{{ ajusteEscolhido.projetoRateio }}<template v-if="ajusteEscolhido.rubricaRateio"> · {{ ajusteEscolhido.rubricaRateio }}</template></div>
          </div>
          <div v-else class="ce-info">Nenhum lançamento selecionado pode receber o ajuste.</div>
        </div>

        <div class="env-foot">
          <div class="aj-rodape-aj">Depois do ajuste a tela recarrega com a mesma seleção — é só conciliar.</div>
          <div class="env-foot-r">
            <button class="btn-primary" :disabled="!ajusteEscolhido || ajuste.gravando" @click="confirmarAjuste">
              <i class="fa" :class="ajuste.gravando ? 'fa-spinner fa-pulse' : 'fa-check'"></i> Ajustar
            </button>
            <button class="rd-btn rd-sair" :disabled="ajuste.gravando" @click="fecharAjuste"><i class="fa fa-sign-out"></i> Sair</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="mostrarJustificativa" class="ov">
      <div class="ov-modal just-modal">
        <div class="env-head">
          <div>
            <h4>{{ justificativa.edicao ? 'Observação do lançamento' : 'Conciliar com justificativa' }}</h4>
            <small class="env-sub">
              {{ justificativa.edicao
                 ? 'Fica registrada na linha e aparece no relatório de conferência.'
                 : (justificativa.linhas.length > 1
                    ? 'Use quando não há correspondente do outro lado — por exemplo, sem extrato do período. Os ' + justificativa.linhas.length + ' lançamentos abaixo ficam conciliados no mesmo grupo, com o motivo registrado.'
                    : 'Use quando não há correspondente do outro lado — por exemplo, sem extrato do período. O lançamento fica conciliado com o motivo registrado.') }}
            </small>
          </div>
        </div>

        <div class="ov-corpo just-corpo">
          <div v-if="justificativa.linhas.length" class="just-lista">
            <div v-for="l in justificativa.linhas" class="just-linha"
                 :key="'j-' + l.idLancamento + '-' + l.idLancRateio + '-' + l.idExtrato">
              <span class="mono muted">{{ l.idExtrato > 0 ? l.idExtrato : l.idLancamento }}</span>
              <span class="mono">{{ l.dataStr }}</span>
              <span class="just-valor mono" :class="l.valor < 0 ? 'neg' : ''">R$ {{ formatar(l.valor) }}</span>
              <span class="just-hist" :title="l.historico">{{ l.historico }}</span>
            </div>
          </div>
          <div v-if="justificativa.linhas.length > 1" class="just-tot">
            <span>{{ justificativa.linhas.length }} lançamentos selecionados</span>
            <b class="mono">R$ {{ formatar(totalJustificativa) }}</b>
          </div>

          <div :class="{ 'just-invalido': validarJustificativa && !(justificativa.texto || '').trim() }">
            <label class="just-lbl">Motivo <span class="just-obr">*</span></label>
            <textarea ref="campoJustificativa" class="form-control just-txt" rows="4" maxlength="300"
                      v-model="justificativa.texto" placeholder="Descreva como o lançamento foi conferido e por que ficou sem par..."></textarea>
            <span v-if="validarJustificativa && !(justificativa.texto || '').trim()" class="just-msg">Campo obrigatório</span>
          </div>
          <div class="just-cont">{{ (justificativa.texto || '').length }} / 300</div>
        </div>

        <div class="env-foot">
          <div></div>
          <div class="env-foot-r">
            <button class="btn-primary" :disabled="salvandoJustificativa" @click="salvarJustificativa">
              <i class="fa" :class="salvandoJustificativa ? 'fa-spinner fa-pulse' : 'fa-check'"></i> Gravar
            </button>
            <button class="rd-btn rd-sair" @click="fecharJustificativa"><i class="fa fa-sign-out"></i> Sair</button>
          </div>
        </div>
      </div>
    </div>

    <regularizar-modal v-if="mostrarOperacao && deteccao" :id-conta-cript="idProjetoCript" :deteccao="deteccao"
                       @fechar="fecharOperacao" @concluido="operacaoConcluida"></regularizar-modal>

    <regularizar-transitoria-modal v-if="debitoParaRegularizar" :id-conta-cript="idProjetoCript" :debito="debitoParaRegularizar"
                                   :nome-conta-transitoria="nomeContaPadrao"
                                   @fechar="debitoParaRegularizar = null" @concluido="regularizarTransitoriaConcluida"></regularizar-transitoria-modal>

    <div v-if="mostrarBaixa" class="ov">
      <div class="ov-modal" style="max-width:440px">
        <div class="ov-head"><h4>{{ transitoria ? 'Baixar contas a receber' : 'Conciliar — baixa' }}</h4></div>
        <div class="ov-corpo" style="padding:16px">
          <div v-if="crOutrasSelecionadas.length > 0" class="baixa-aviso">
            <i class="fa fa-exchange"></i>
            <div>
              <b>{{ crOutrasSelecionadas.length }} conta{{ crOutrasSelecionadas.length > 1 ? 's' : '' }} a receber de outra conta</b>
              <span>Na baixa, {{ crOutrasSelecionadas.length > 1 ? 'os lançamentos são transferidos' : 'o lançamento é transferido' }} para {{ nomeContaPadrao }}.</span>
              <ul>
                <li v-for="l in crOutrasSelecionadas" :key="l.idLancamento">{{ l.nomeConta }} · R$ {{ formatar(l.valor) }}</li>
              </ul>
            </div>
          </div>
          <div class="baixa-campo">
            <label>Data de baixa (compensação) <span class="baixa-obr">*</span></label>
            <date-picker v-model="baixaData" format="DD/MM/YYYY" lang="pt-br"
                         :input-class="baixaErro ? 'form-control baixa-invalido' : 'form-control'"
                         :editable="false" :shortcuts="false" :clearable="false"
                         :append-to-body="true" :popup-style="{ zIndex: 3200 }"
                         placeholder="Informe a data" style="width:100%"
                         @change="baixaErro = false"></date-picker>
            <span v-if="baixaErro" class="baixa-erro">Campo obrigatório</span>
            <span v-else-if="transitoria" class="muted">Não precisa ser a data do débito da transitória.</span>
          </div>
          <div class="baixa-campo">
            <label>Observação <span class="muted">(opcional — grava no complemento)</span></label>
            <textarea v-model="baixaObs" class="form-control" rows="3" placeholder="Deixe em branco para conciliar sem observação"></textarea>
          </div>
          <label v-if="mostrarCheckTaxa" class="baixa-check">
            <input type="checkbox" v-model="baixaComTaxa">
            <span>Taxa <span class="muted">— marcado quando o projeto tem taxa cadastrada</span></span>
          </label>
        </div>
        <div class="ov-head" style="border-top:1px solid #dfe3e8;border-bottom:none;justify-content:flex-end">
          <button class="btn-primary" :disabled="salvando" @click="confirmarBaixa"><i class="fa" :class="salvando ? 'fa-spinner fa-pulse' : 'fa-check'"></i> {{ transitoria ? 'Baixar' : 'Conciliar' }}</button>
          <button class="rd-btn rd-sair" style="margin-left:8px" @click="mostrarBaixa = false"><i class="fa fa-sign-out"></i> Sair</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .conc-page { box-sizing: border-box; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #eceef1; padding: 12px 14px; overflow: auto; font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #1c2024; }
  .conc-card { min-width: 1360px; height: 100%; display: flex; flex-direction: column; background: #fff; border: 1px solid #d3d8de; border-radius: 8px; box-shadow: 0 1px 2px rgba(16,24,40,.06); overflow: hidden; }

  .titlebar { flex: 0 0 auto; display: flex; align-items: center; gap: 8px; background: #f4f6f8; border-bottom: 1px solid #dfe3e8; padding: 8px 14px; }
  .titlebar .dot { width: 7px; height: 7px; border-radius: 50%; background: #0f8a5f; }
  .tb-txt { font-size: 12.5px; font-weight: 600; letter-spacing: .02em; color: #5b6570; }

  .ctx { flex: 0 0 auto; display: flex; justify-content: space-between; align-items: flex-start; padding: 15px 18px 13px; }
  .ctx-nome { font-size: 21px; font-weight: 600; letter-spacing: -.01em; }
  .ctx-nome .chip { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 11px; font-weight: 500; padding: 2px 6px; border-radius: 4px; background: #eef1f4; color: #5b6570; vertical-align: 3px; margin-left: 4px; }
  .ctx-conta { font-size: 12.5px; color: #6b747e; margin-top: 4px; }
  .ctx-conta b { color: #3b444d; font-weight: 500; }
  .ctx-l { display: flex; align-items: stretch; }
  .ctx-atu { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; margin-left: 16px; padding-left: 16px; border-left: 1px solid #e6e9ed; align-self: stretch; justify-content: center; }
  .ctx-atualizar { height: 34px; padding: 0 14px; font-size: 13px; font-weight: 600; }
  .ctx-atu-hora { font-size: 11.5px; color: #8a939c; white-space: nowrap; }
  .ctx-m { display: flex; gap: 10px; margin: 0 18px; flex: 0 0 auto; }
  .ctx-tile { min-width: 150px; padding: 7px 12px; border: 1px solid #e6e9ed; border-radius: 6px; background: #fbfcfd; display: flex; flex-direction: column; align-items: flex-end; }
  .ctx-tile .lbl { font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; color: #7b848e; font-weight: 600; }
  .ctx-tile .val { font-size: 16px; font-weight: 600; color: #1a2035; margin-top: 2px; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .ctx-tile .sub { font-size: 11px; color: #8a939c; margin-top: 1px; white-space: nowrap; }
  .ctx-tile .val.ok { color: #0a6d4d; }
  .ctx-tile .val.neg { color: #c0392b; }
  .ctx-tile .val.pend-val { color: #b06a15; }
  .ctx-tile .val.mudo { color: #b3b9c0; }
  .ctx-tile.t-saldo { border-top: 3px solid #3b6fb6; }
  .ctx-tile.t-op.t-ok { border-top: 3px solid #0a6d4d; }
  .ctx-tile.t-op.t-pend-val { border-top: 3px solid #e0a03a; background: #fff8ec; }
  .ctx-tile .sub.sub-ok { color: #0a6d4d; }
  .ctx-tile .sub.sub-pend { color: #b06a15; font-weight: 600; }
  .ctx-tile.t-extrato { border-top: 3px solid #1ab394; }
  .ctx-tile.t-dif.t-ok { border-top: 3px solid #0a6d4d; background: #effaf5; }
  .ctx-tile.t-dif.t-neg { border-top: 3px solid #c0392b; background: #fdf1f0; }
  .ctx-tile.t-dif.t-pend-val { border-top: 3px solid #e0a03a; background: #fff8ec; }
  .ctx-tile.t-dif.t-mudo { border-top: 3px solid #d5dae0; }
  .ctx-r { width: 330px; }
  .ctx-prog-top { display: flex; justify-content: space-between; font-size: 12.5px; }
  .ctx-prog-top .lbl { color: #5b6570; }
  .ctx-bar { height: 8px; border-radius: 99px; background: #e6e9ed; overflow: hidden; margin: 6px 0 3px; }
  .ctx-bar > span { display: block; height: 100%; background: #0f8a5f; border-radius: 99px; transition: width .3s; }
  .ctx-pct { text-align: right; font-size: 20px; font-weight: 600; color: #0f8a5f; }
  .pend { color: #c98a00; }

  .filtros { flex: 0 0 auto; display: flex; flex-wrap: nowrap; align-items: flex-end; gap: 14px; padding: 0 18px 14px; }
  .filtros .f { display: flex; flex-direction: column; gap: 5px; }
  .filtros label { font-size: 12px; font-weight: 600; color: #5b6570; }
  .filtros .form-control { height: 34px; font-size: 13px; box-shadow: none; }
  .filtros select.form-control { width: 118px; }
  .filtros select.f-lanc { width: 200px; }
  .f-sug { height: 34px; padding: 0 12px; border-radius: 5px; border: 1px solid #ddd0ee; background: #faf8fd;
           color: #5b3494; font-size: 12.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
  .f-sug:hover { border-color: #b9a5d4; }
  .f-sug.ativo { background: #7c4dbd; border-color: #6b3fa8; color: #fff; }
  .f-sug-fim { align-self: flex-end; padding-bottom: 8px; font-size: 12px; color: #6b6480; }
  .f-sug-fim a { color: #5b3494; font-weight: 600; }
  .f-sug-qt { display: inline-block; padding: 0 6px; border-radius: 99px; background: #ece3f7; color: #5b3494; font-size: 11px; font-weight: 700; }
  .f-sug.ativo .f-sug-qt { background: rgba(255,255,255,.22); color: #fff; }
  .filtros input.f-busca { width: 170px; padding-right: 26px; }
  .f-busca-wrap { position: relative; display: inline-block; }
  .f-busca-x { position: absolute; right: 9px; top: 50%; transform: translateY(-50%); color: #a4acb4; font-size: 12px; line-height: 1; }
  .f-busca-x:hover { color: #5b6570; }
  .filtros .f-per { flex: 0 0 auto; }
  .f-periodo { display: flex; align-items: center; gap: 8px; }
  .f-periodo .mx-datepicker { flex: 0 0 124px; min-width: 124px; }
  .f-periodo .ate { font-size: 12.5px; color: #8a939c; }
  .f-toggles { display: flex; gap: 12px; height: 34px; align-items: center; }
  .tgl { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: normal; margin: 0; cursor: pointer; }
  .tgl input { width: 14px; height: 14px; }
  .f-acoes { margin-left: auto; flex: 0 1 auto; min-width: 0; display: flex; align-items: center; gap: 12px; }
  .f-valor-chip { display: inline-flex; align-items: center; gap: 7px; background: #fdf3e7; border: 1px solid #e8b477; color: #a75f14;
                  border-radius: 5px; padding: 5px 10px; font-size: 12px; font-weight: 600; white-space: nowrap; }
  .f-valor-chip b { color: #8a4c0f; }
  .f-valor-chip .fa-filter { font-size: 11px; }
  .f-valor-x { color: #a75f14; opacity: .7; }
  .f-valor-x:hover { color: #7a430c; opacity: 1; }
  .f-lupa-chip { background: #e8f5ef; border-color: #7fc9a8; color: #0a6d4d; }
  .f-lupa-chip { min-width: 0; }
  .f-lupa-chip b { display: inline-block; vertical-align: bottom; color: #075a3f; max-width: 150px; overflow: hidden; text-overflow: ellipsis; }
  .f-lupa-chip .f-valor-x { color: #0a6d4d; }
  .f-lupa-chip .f-valor-x:hover { color: #075a3f; }
  .f-lupa-chip.mais { background: #fbf1e4; border-color: #e0b47e; color: #8a5a1c; }
  .f-lupa-chip.mais b { color: #6e4612; }
  .f-lupa-chip.mais .f-valor-x { color: #8a5a1c; }
  .f-lupa-chip.mais .f-valor-x:hover { color: #6e4612; }
  .btn-link.limpar-ativo { color: #fff; background: #f8ac59; border: 1px solid #f7a54a; border-radius: 4px; padding: 5px 12px; font-weight: 600; text-decoration: none; }
  .btn-link.limpar-ativo:hover { background: #f7a54a; }
  .btn-link { height: 34px; border: 1px solid #d3d8de; border-radius: 4px; background: #fff; color: #6b747e; font-size: 13px; padding: 0 14px; font-weight: 600; white-space: nowrap; }
  .btn-link:hover { background: #f4f6f8; text-decoration: none; }
  .btn-link:disabled { color: #b6bdc5; }
  .proc-load { color: #1ab394; font-size: 13px; font-weight: 600; white-space: nowrap; }
  .btn-primary { height: 34px; padding: 0 20px; background: #0f8a5f; border: 1px solid #0d7a54; color: #fff; font-size: 13px; font-weight: 600; border-radius: 5px; box-shadow: 0 1px 2px rgba(15,138,95,.3); }
  .btn-primary:hover { background: #0d7a54; }
  .btn-primary:disabled { opacity: .6; }

  .legenda { flex: 0 0 auto; display: flex; align-items: center; flex-wrap: wrap; gap: 10px; background: #f7f9fa; border-top: 1px solid #e6e9ed; border-bottom: 1px solid #dfe3e8; padding: 9px 18px; }
  .lg-tit { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #7b848e; }
  .lg-chip { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e2e6ea; border-radius: 99px; padding: 4px 9px 4px 6px; font-size: 12px; color: #3b444d; cursor: help; }
  .lg-chip:hover { border-color: #c6ccd4; background: #fbfcfd; }
  .lg-chip em { color: #8a939c; font-style: normal; }
  .lg-chip .sw { width: 12px; height: 12px; border-radius: 3px; border: 1px solid; display: inline-block; }
  .lg-chip .lg-ic { color: #2f7d4f; font-size: 12px; }
  .lg-ocultar { margin-left: auto; font-size: 12px; text-decoration: underline; color: #6b747e; }
  .legenda-off { justify-content: flex-start; }
  .legenda-off a { font-size: 12px; text-decoration: underline; color: #6b747e; }

  .carregando { flex: 1 1 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #8a939c; gap: 10px; }

  .area { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: 1fr 236px 1fr; gap: 1px; background: #dfe3e8; }

  .central { background: #f7f9fa; padding: 14px 14px 16px; display: flex; flex-direction: column; gap: 12px; min-height: 0; overflow-y: auto; -webkit-user-select: none; -ms-user-select: none; user-select: none; }
  .central .ce-row b { -webkit-user-select: text; -ms-user-select: text; user-select: text; cursor: text; }
  .ce-tit { text-align: center; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #7b848e; }
  .ce-card { background: #fff; border: 1px solid #dfe3e8; border-radius: 6px; }
  .ce-row { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; padding: 9px 12px; }
  .ce-row + .ce-row { border-top: 1px solid #eef1f4; }
  .ce-row b { font-weight: 600; }
  .ce-dif { background: #f4faf7; border-top: 1px solid #eef1f4; }
  .ce-dif span, .ce-dif b { color: #0a6d4d; }
  .ce-dif b { font-weight: 700; }
  .ce-cont { text-align: center; font-size: 11.5px; color: #6b747e; }
  .ce-ok { display: flex; align-items: flex-start; gap: 7px; background: #f4faf7; border: 1px solid #b7e0cb; border-radius: 6px; padding: 9px 10px; font-size: 12px; color: #0a6d4d; }
  .ce-ok-dot { flex: 0 0 auto; width: 15px; height: 15px; border-radius: 50%; background: #0f8a5f; color: #fff; font-size: 10px; text-align: center; line-height: 15px; }
  .ce-info { text-align: center; font-size: 12px; color: #8a6d3b; background: #fdf6e3; border: 1px solid #f0e2b8; border-radius: 6px; padding: 9px 10px; }
  .ce-btn { flex: 0 0 auto; width: 100%; border-radius: 5px; padding: 10px 12px; font-size: 13px; font-weight: 600; border: 1px solid transparent; }
  .ce-conciliar { background: #0f8a5f; border-color: #0d7a54; color: #fff; box-shadow: 0 1px 2px rgba(15,138,95,.3); }
  .ce-conciliar:disabled { opacity: .5; }
  .ce-sec { background: #fff; border-color: #dfe3e8; color: #3b444d; font-weight: 500; }
  .ce-sec:disabled { color: #b6bdc5; }
  .ce-cred:not(:disabled) { border-color: #b7e0cb; color: #0a6d4d; background: #f4faf7; }
  .ce-cred:not(:disabled):hover { border-color: #0f8a5f; }
  .ce-ajuste { margin-top: 8px; background: #eaf2fb; border-color: #a9c8ec; color: #1f5f9e; }
  .ce-ajuste:not(:disabled):hover { background: #dceafa; border-color: #2f6fb2; }
  .ce-sug { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; background: #f6f2fc; border: 1px solid #ddd0ee;
            border-radius: 6px; padding: 9px 10px; font-size: 12px; font-weight: 600; color: #5b3494; }
  .ce-sug em { font-style: normal; font-weight: 500; color: #7c4dbd; }
  .ce-sug-aj { flex: 1 0 100%; font-size: 11.5px; font-weight: 400; color: #6b6480; }
  .ce-sug-disputa { color: #a94442; font-weight: 600; }
  .ce-descartar { border-color: #ddd0ee; color: #5b3494; background: #faf8fd; }
  .ce-descartar:hover { border-color: #b9a5d4; }
  .ce-desfazer { background: #fff; border-color: #e4d7d7; color: #a13c3c; }
  .ce-desfazer:hover:not(:disabled) { border-color: #c98080; }
  .ce-desfazer:disabled { color: #c7ccd2; border-color: #e9edf1; }
  .ce-nota { flex: 0 0 auto; margin-top: auto; border-top: 1px solid #e6e9ed; padding-top: 10px; font-size: 11.5px; color: #8a939c; line-height: 1.45; }
  .ce-encerrado { background: #fdf1f1; border: 1px solid #e4d7d7; border-radius: 6px; padding: 10px 12px; font-size: 12.5px; font-weight: 600; color: #a13c3c; }
  .ce-encerrado-aj { display: block; margin-top: 5px; font-size: 11.5px; font-weight: normal; color: #8a6a6a; line-height: 1.45; }
  .chip-enc { background: #fdecec !important; border-color: #f5c6c6 !important; color: #c0392b !important; }

  .rodape { flex: 0 0 auto; display: flex; justify-content: space-between; align-items: center; background: #f7f9fa; border-top: 1px solid #dfe3e8; padding: 12px 16px; }
  .rd-dica { font-size: 12px; color: #8a939c; }
  .rd-btns { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
  .rd-btn { display: inline-flex; align-items: center; gap: 7px; background: #fff; border: 1px solid #c6ccd4; border-radius: 5px; padding: 8px 14px; font-size: 12.5px; color: #3b444d; }
  .rd-btn:hover { background: #f4f6f8; }
  .rd-sair { background: #fdf1f1; border-color: #e4d7d7; color: #a13c3c; font-weight: 600; padding: 8px 18px; }
  .rd-sair:hover { background: #fbe7e7; border-color: #d9c2c2; }
  .rd-azul { background: #2b6cb0; border-color: #2b6cb0; color: #fff; font-weight: 600; }
  .rd-azul:hover { background: #245a94; border-color: #245a94; color: #fff; }

  .ov { position: fixed; inset: 0; background: rgba(16,24,40,.55); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px; }

  .env-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 14px 18px; border-bottom: 1px solid #eceef1; }
  .env-head h4 { margin: 0; font-size: 16px; font-weight: 600; }
  .env-sub { display: block; color: #8a939c; font-size: 12px; margin-top: 3px; }
  .env-foot { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-top: 1px solid #eceef1; }
  .env-foot-r { display: flex; gap: 8px; }
  .rd-sair:hover { background: #fbe7e7; border-color: #d9c2c2; }

  .ov { position: fixed; inset: 0; background: rgba(16,24,40,.55); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px; }


  .ov-modal.just-modal { width: 560px; max-width: 96vw; max-height: none; box-shadow: 0 16px 48px rgba(16,24,40,.35); }
  .just-corpo { padding: 14px 18px; }
  .just-lista { max-height: 230px; overflow-y: auto; margin-bottom: 14px; }
  .just-linha { display: flex; align-items: baseline; gap: 10px; padding: 9px 11px; background: #f7f9fa; border: 1px solid #eceef1; border-radius: 6px; font-size: 12.5px; color: #3b444d; }
  .just-linha + .just-linha { margin-top: 5px; }
  .just-tot { display: flex; justify-content: space-between; align-items: baseline; margin: -6px 0 14px; padding: 0 11px; font-size: 12.5px; color: #6b747e; }
  .just-tot b { color: #1c2024; }
  .just-valor { font-weight: 600; color: #1c2024; min-width: 96px; text-align: right; }
  .just-valor.neg { color: #b3261e; }
  .just-hist { color: #6b747e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .just-lbl { display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #7b848e; margin-bottom: 5px; }
  .just-txt { resize: vertical; font-size: 13px; }
  .just-obr { color: #ED5565; }
  .just-invalido .just-lbl { color: #ED5565; }
  .just-invalido .just-txt { border-color: #ED5565 !important; background: #fff8f8; box-shadow: 0 0 0 3px rgba(237,85,101,.2) !important; }
  .just-msg { display: block; margin-top: 4px; font-size: 12px; color: #ED5565; }
  .just-cont { text-align: right; font-size: 11.5px; color: #8a939c; margin-top: 5px; }
  .ov-modal.aj-modal { width: 720px; }
  .aj-lista { max-height: 300px; }
  .aj-linha { align-items: center; cursor: pointer; }
  .aj-linha input { margin: 0; flex: none; }
  .aj-linha .just-hist { flex: 1 1 auto; min-width: 0; }
  .aj-linha.aj-ativo { background: #eaf2fb; border-color: #a9c8ec; }
  .aj-linha.aj-bloq { cursor: default; opacity: .7; }
  .aj-cd { flex: none; font-size: 10.5px; font-weight: 700; color: #0a6d4d; }
  .aj-cd.neg { color: #b3261e; }
  .aj-novo { flex: none; min-width: 110px; text-align: right; color: #1f5f9e; font-weight: 600; }
  .aj-motivo { flex: none; max-width: 220px; font-size: 11.5px; color: #8a6d3b; text-align: right; }
  .aj-detalhe { border: 1px solid #a9c8ec; background: #f6f9fd; border-radius: 6px; padding: 10px 12px; }
  .aj-det-linha { display: flex; justify-content: space-between; align-items: baseline; font-size: 12.5px; }
  .aj-det-linha + .aj-det-linha { margin-top: 4px; }
  .aj-det-linha .just-lbl { margin: 0; }
  .aj-det-proj { margin-top: 6px; font-size: 12px; color: #6b747e; }
  .aj-rodape-aj { font-size: 11.5px; color: #8a939c; }
  .ov-modal { background: #fff; border-radius: 8px; width: 96vw; max-width: 1200px; max-height: 92vh; display: flex; flex-direction: column; box-shadow: 0 10px 40px rgba(16,24,40,.2); }
  .ov-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #dfe3e8; }
  .ov-head h4 { margin: 0; font-size: 15px; }
  .ov-corpo { overflow: auto; padding: 0 8px 8px; }

  .cf-ov { position: fixed; inset: 0; background: rgba(16,24,40,.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 22px; }
  .cf-modal { width: 100%; max-width: 1360px; min-width: 1180px; max-height: 94vh; background: #fff; border: 1px solid #d3d8de; border-radius: 10px; box-shadow: 0 8px 28px rgba(16,24,40,.14); overflow: hidden; display: flex; flex-direction: column; }
  .cf-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; background: #f4f6f8; border-bottom: 1px solid #dfe3e8; }
  .cf-head-l { display: flex; gap: 10px; align-items: center; min-width: 0; }
  .cf-head-l h4 { margin: 0; font-size: 16.5px; font-weight: 600; letter-spacing: -.01em; color: #1c2024; }
  .cf-chip { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 11.5px; padding: 3px 7px; border-radius: 4px; background: #eef1f4; color: #5b6570; white-space: nowrap; }
  .cf-sub { font-size: 12.5px; color: #6b747e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cf-head-r { display: flex; gap: 8px; flex: none; }
  .cf-fechar { height: 32px; padding: 0 14px; border: 1px solid #c6ccd4; border-radius: 5px; background: #eef1f4; font-size: 12.5px; font-weight: 600; color: #3b444d; cursor: pointer; }
  .cf-fechar:hover { background: #e4e8ec; }
  .cf-cards { display: grid; grid-template-columns: 1fr 1fr 1fr 1.4fr; gap: 1px; background: #e6e9ed; border-bottom: 1px solid #dfe3e8; }
  .cf-card { background: #fff; padding: 12px 18px; }
  .cf-clb { font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #7b848e; }
  .cf-cvl { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 17px; font-weight: 600; color: #1c2024; margin-top: 4px; font-variant-numeric: tabular-nums; }
  .cf-caux { font-size: 11.5px; font-weight: 400; margin-left: 8px; }
  .cf-card-dif { background: #fdf7f7; border-left: 2px solid #c98080; }
  .cf-card-dif .cf-clb, .cf-card-dif .cf-cvl, .cf-card-dif .cf-caux { color: #a13c3c; }
  .cf-card-dif .cf-cvl { font-weight: 700; }
  .cf-card-ok { background: #f4faf7; border-left: 2px solid #0f8a5f; }
  .cf-card-ok .cf-clb, .cf-card-ok .cf-cvl, .cf-card-ok .cf-caux { color: #0a6d4d; }
  .cf-crow { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .cf-cmeta { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 12px; color: #6b747e; white-space: nowrap; }
  .cf-semp { color: #c98a00; }
  .cf-bar { height: 8px; border-radius: 99px; background: #e6e9ed; margin-top: 8px; overflow: hidden; }
  .cf-bar > span { display: block; height: 100%; background: #0f8a5f; border-radius: 99px; }
  .cf-view { display: flex; align-items: center; gap: 12px; padding: 11px 18px; background: #f7f9fa; border-bottom: 1px solid #dfe3e8; }
  .cf-seg { display: flex; gap: 2px; padding: 2px; border: 1px solid #c6ccd4; border-radius: 6px; background: #fff; }
  .cf-seg button { height: 26px; padding: 0 11px; border: none; border-radius: 4px; font-size: 12px; font-weight: 600; background: transparent; color: #5b6570; cursor: pointer; }
  .cf-seg button.ativo { background: #1c2024; color: #fff; }
  .cf-busca { position: relative; }
  .cf-busca .fa { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 13px; color: #8a939c; }
  .cf-busca input { width: 280px; height: 30px; border: 1px solid #c6ccd4; border-radius: 5px; padding: 0 10px 0 28px; font-size: 12.5px; }
  .cf-cont { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 12px; color: #6b747e; white-space: nowrap; margin-left: auto; }
  .cf-scroll { height: 520px; overflow: auto; background: #fff; }
  .cf-thead { position: sticky; top: 0; z-index: 3; }
  .cf-hgrp, .cf-hcol, .cf-row { display: grid; grid-template-columns: 64px 88px 84px 84px 1.2fr 112px 98px 84px 1.2fr 112px 88px; column-gap: 16px; padding-inline: 16px; min-width: 1320px; }
  .cf-semdif .cf-hgrp, .cf-semdif .cf-hcol, .cf-semdif .cf-row { grid-template-columns: 64px 88px 84px 84px 1.2fr 112px 98px 84px 1.2fr 112px; min-width: 1232px; }
  .cf-hgrp { background: #eef1f4; border-bottom: 1px solid #e6e9ed; }
  .cf-hgrp > div { padding: 7px 0 6px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #5b6570; }
  .cf-hcol { background: #f7f9fa; border-bottom: 1px solid #dfe3e8; }
  .cf-hcol > div { padding: 8px 0; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #7b848e; }
  .cf-hb { border-left: 1px solid #e6e9ed; padding-left: 10px; }
  .cf-hgrp .cf-hb { border-left-color: #d3d8de; }
  .cf-row { border-bottom: 1px solid #eef1f4; }
  .cf-row > div { padding: 10px 0; font-size: 12.5px; color: #3b444d; align-self: start; }
  .cf-row.cf-rdiv { background: #fdf6f6; box-shadow: inset 3px 0 0 #c05252; }
  .cf-mono { font-family: 'IBM Plex Mono', Consolas, monospace; font-variant-numeric: tabular-nums; }
  .cf-mut { color: #6b747e; }
  .cf-nw { white-space: nowrap; }
  .cf-hist { padding-right: 10px; }
  .cf-clamp { line-height: 1.35; max-height: 2.7em; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .cf-semcor { font-style: italic; color: #b08686; }
  .cf-val { text-align: right; font-weight: 600; padding-right: 8px; white-space: nowrap; }
  .cf-neg { color: #a13c3c; }
  .cf-pos { color: #0a6d4d; }
  .cf-dif { text-align: right; font-weight: 700; color: #a13c3c; }
  .cf-difzero { color: #a9b2bb; font-weight: 400; }
  .cf-vazio { padding: 60px 20px; text-align: center; font-size: 13px; color: #8a939c; }
  .cf-foot { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; background: #f7f9fa; border-top: 1px solid #dfe3e8; }
  .cf-leg { display: flex; gap: 16px; font-size: 12px; color: #8a939c; }
  .cf-legi { display: flex; align-items: center; gap: 6px; }
  .cf-sw { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
  .cf-swdiv { background: #fdf3f3; border: 1px solid #e4b9b9; box-shadow: inset 3px 0 0 #c05252; }
  .cf-swok { background: #fff; border: 1px solid #dfe3e8; }
  .cf-foot-r { display: flex; align-items: center; gap: 10px; }
  .cf-footr { font-size: 12px; color: #6b747e; }
  .cf-footr b { font-family: 'IBM Plex Mono', Consolas, monospace; font-weight: 600; color: #a13c3c; }
  .info-tab { width: 100%; font-size: 13px; border-collapse: collapse; }
  .info-tab th { text-align: left; width: 150px; color: #6b747e; font-weight: 500; padding: 6px 10px 6px 0; vertical-align: top; border-bottom: 1px solid #eef1f4; }
  .info-tab td { padding: 6px 0; color: #1c2024; border-bottom: 1px solid #eef1f4; }
  .baixa-aviso { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 14px; padding: 10px 12px;
                 background: #fdf3e7; border: 1px solid #f0d5b4; border-radius: 6px; font-size: 12.5px; color: #6b4a1c; }
  .baixa-aviso .fa { color: #d97e26; margin-top: 2px; }
  .baixa-aviso b { display: block; color: #a75f14; }
  .baixa-aviso span { display: block; margin-top: 2px; }
  .baixa-aviso ul { margin: 6px 0 0; padding-left: 16px; }
  .baixa-obr { color: #c0392b; }
  .baixa-erro { display: block; margin-top: 4px; font-size: 12px; color: #c0392b; }
  .baixa-campo { margin-bottom: 14px; }
  .baixa-check { display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: #5b6570; cursor: pointer; margin: 0 0 4px; }
  .baixa-check input { margin: 0; }
  .baixa-check .muted { font-weight: 400; }
  .baixa-campo label { display: block; font-size: 12.5px; font-weight: 600; color: #5b6570; margin-bottom: 5px; }
  .baixa-campo label .muted { font-weight: 400; }
  .info-rateios { margin-top: 16px; }
  .info-rt-tit { font-size: 12.5px; font-weight: 600; color: #5b6570; margin-bottom: 8px; }
  .rt-tab { width: 100%; font-size: 12.5px; border-collapse: collapse; }
  .rt-tab th { text-align: left; background: #f7f9fa; border-bottom: 1px solid #dfe3e8; padding: 6px 8px; color: #7b848e; font-weight: 600; font-size: 11px; }
  .rt-tab td { padding: 6px 8px; border-bottom: 1px solid #eef1f4; vertical-align: top; }
  .rt-tab .tr { text-align: right; white-space: nowrap; }
  .muted { color: #8a939c; }
  .text-center { text-align: center; }
</style>

<style>
  .conc-page .mx-datepicker { width: 124px; }
  .conc-page .mx-datepicker input { height: 34px; padding: 0 28px 0 10px; border: 1px solid #c6ccd4; border-radius: 5px; box-shadow: none; font-size: 13px; font-family: 'IBM Plex Mono', Consolas, monospace; }
  .baixa-invalido, .mx-datepicker input.baixa-invalido { border-color: #c0392b; background: #fdf6f5; }
  .mono { font-family: 'IBM Plex Mono', Consolas, 'Courier New', monospace; font-variant-numeric: tabular-nums; }

  .painel { min-width: 0; min-height: 0; background: #fff; display: flex; flex-direction: column; }
  .painel-head { flex: 0 0 auto; display: flex; justify-content: space-between; align-items: flex-start; padding: 11px 14px 9px; }
  .painel-tit { font-size: 13.5px; font-weight: 600; color: #1c2024; }
  .painel-res { font-family: 'IBM Plex Mono', Consolas, monospace; font-size: 12px; color: #6b747e; margin-top: 2px; }
  .painel-res .pend { color: #c98a00; }
  .sel-todos { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; color: #3b444d; margin: 0; cursor: pointer; padding: 5px 10px; border: 1px solid #dfe3e8; border-radius: 5px; }
  .sel-todos input { width: 13px; height: 13px; }
  .painel-scroll { position: relative; flex: 1 1 auto; min-height: 0; overflow: auto; border-top: 1px solid #e6e9ed; }

  .grid-conc { display: grid; grid-template-columns: 28px 100px 30px 66px 86px 168px 176px 208px 228px 60px 70px 94px 94px 180px 150px 150px 150px 210px; min-width: 2250px; padding: 0; align-items: stretch; user-select: none; -webkit-user-select: none; -ms-user-select: none; }
  .grid-conc > div { border-right: 1px solid #edf0f3; min-width: 0; overflow: hidden; }
  .grid-conc > div:first-child, .grid-conc > div:nth-child(2), .grid-conc > div:nth-child(3), .grid-conc > div:last-child { border-right: none; }
  .grid-conc > div:last-child { padding-right: 22px; }
  .c-ind, .c-info, .c-sit { padding: 0 !important; justify-content: center; }
  .c-info { display: flex; align-items: center; justify-content: center; gap: 3px; }
  .c-info .ic { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 26px;
                border-radius: 4px; color: #9aa3ac; font-size: 16px; transition: background .12s; }
  .c-info .ic:hover { color: #2d6cb3; background: #eaf1f9; }
  .c-info .ic-vinc { color: #2d6cb3; }
  .c-info .ic-vinc:hover { color: #1f4f86; background: #eaf1f9; }
  .vinc-corpo { padding: 14px 16px !important; }
  .vinc-sec { margin-bottom: 16px; }
  .vinc-sec:last-child { margin-bottom: 0; }
  .vinc-sec .rt-tab .neg { color: #a13c3c; }
  .vinc-sec .rt-tab .pos { color: #0a6d4d; }
  .vinc-sec .rt-tab tr.vinc-atual td { background: #f5fcfa; border-top: 2px solid #8fd9c8; border-bottom: 2px solid #8fd9c8; }
  .vinc-sec .rt-tab tr.vinc-atual td:first-child { border-left: 2px solid #8fd9c8; }
  .vinc-sec .rt-tab tr.vinc-atual td:last-child { border-right: 2px solid #8fd9c8; }
  .vinc-sec .rt-tab { table-layout: fixed; }
  .vinc-sec .rt-tab td { overflow-wrap: anywhere; }
  .vinc-sec .rt-tab thead th:nth-child(1) { width: 30px; }
  .vinc-sec .rt-tab thead th:nth-child(2) { width: 74px; }
  .vinc-sec .rt-tab thead th:nth-child(3) { width: 88px; }
  .vinc-sec .rt-tab thead th:nth-child(4) { width: 24%; }
  .vinc-sec .rt-tab thead th:nth-child(6) { width: 15%; }
  .vinc-sec .rt-tab thead th:nth-child(7) { width: 128px; }
  .vinc-sec .rt-tab thead th:nth-child(8) { width: 104px; }
  .vinc-sec .rt-tab .vinc-ck { width: 30px; text-align: center; }
  .vinc-sec .rt-tab .vinc-ck input { margin: 0; cursor: pointer; }
  .vinc-sec .rt-tab .vinc-ck input:disabled { cursor: default; opacity: .45; }
  .vinc-sec .rt-tab tr.vinc-marcavel { cursor: pointer; }
  .vinc-sec .rt-tab tr.vinc-marcado td { background: #fff8e1; }
  .vinc-sec .rt-tab .vinc-calc td { border-bottom: none; padding: 5px 8px; font-weight: 600; color: #3b444d; }
  .vinc-sec .rt-tab .vinc-calc tr:first-child td { border-top: 2px solid #dfe3e8; }
  .vinc-sec .rt-tab .vinc-calc .vinc-calc-dica { font-weight: 400; text-align: right; font-size: 11.5px; }
  .vinc-sec .rt-tab .vinc-calc-marc td { background: #fff8e1; }
  .vinc-sec .rt-tab .vinc-calc-dif td { background: #f7f9fa; }
  .vinc-sec .rt-tab tr.vinc-marcavel:hover td { background: #f3f7fc; }
  .vinc-sec .rt-tab tr.vinc-marcado:hover td { background: #fdf1cc; }
  .vinc-sec .rt-tab .vinc-calc td.tr { text-align: right; white-space: nowrap; }
  .vinc-sec .rt-tab .vinc-calc td.neg { color: #a13c3c; }
  .vinc-sec .rt-tab .vinc-calc td.pos { color: #0a6d4d; }
  .vinc-selo { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 10px; }
  .vinc-selo-trans { background: #fff4e0; color: #9a5b00; }
  .vinc-selo-conc { background: #e3f4ec; color: #0a6d4d; }
  .vinc-sit { display: inline-block; font-size: 11.5px; font-weight: 600; padding: 1px 8px; border-radius: 9px; white-space: nowrap; }
  .vinc-sit.vs-ok { background: #e3f4ec; color: #0a6d4d; }
  .vinc-sit.vs-pend { background: #fff4e0; color: #9a5b00; }
  .vinc-sit.vs-erro { background: #fbe9e9; color: #a13c3c; }
  .vinc-sit.vs-neutro { background: #eef1f4; color: #5b6570; }
  .grid-conc .ic:focus, .grid-conc .ic-busca:focus { outline: none; box-shadow: none; }
  .ic-sug { font-size: 16px !important; }
  .c-info .ic-sug:hover { background: #f0eaf9; }
  .ic-sug.sug-alta { color: #7c4dbd !important; }
  .ic-sug.sug-media { color: #9a76c9 !important; }
  .ic-sug.sug-baixa { color: #b9a5d4 !important; }
  .ic-sug:hover { color: #5b3494 !important; }
  .c-sit { display: flex; align-items: center; justify-content: center; }
  .sit-mk { width: 9px; height: 9px; border-radius: 50%; box-sizing: border-box; }
  .sit-exec { background: #5b6570; border: 1px solid #5b6570; }
  .sit-aberto { background: transparent; border: 1.5px solid #9aa3ac; }
  .sit-conf { background: #18a689; border: 1px solid #18a689; }
  .sit-naoconf { background: #f8ac59; border: 1px solid #d68a00; }
  .sit-outra { background: #d97e26; border: 1.5px solid #a75f14; }
  .grid-row.row-bloq { background: #fbfbfc; color: #98a0a9; }
  .grid-row.row-bloq:hover { background: #f5f6f8; }
  .lg-sit { display: inline-block; vertical-align: middle; margin-right: 6px; }
  .grid-conc.grid-proj { grid-template-columns: 28px 100px 30px 66px 86px 168px 176px 208px 228px 60px 70px 94px 94px 180px 150px 150px 150px 160px 210px; min-width: 2410px; }

  .grid-conc > div:nth-child(-n+7) { position: sticky; background-color: inherit; z-index: 1; }
  .grid-conc > div:nth-child(1) { left: 0; }
  .grid-conc > div:nth-child(2) { left: 28px; }
  .grid-conc > div:nth-child(3) { left: 128px; }
  .grid-conc > div:nth-child(4) { left: 158px; }
  .grid-conc > div:nth-child(5) { left: 224px; }
  .grid-conc > div:nth-child(6) { left: 310px; }
  .grid-conc > div:nth-child(7) { left: 478px; box-shadow: 6px 0 6px -4px rgba(16,24,40,.14); }
  .grid-head > div:nth-child(-n+7) { z-index: 3; }
  .grid-row { background: #fff; }
  .grid-head { position: sticky; top: 0; z-index: 2; background: #f7f9fa; border-bottom: 1px solid #dfe3e8; }
  .grid-head > div { display: flex; align-items: center; padding: 8px 9px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #7b848e; white-space: nowrap; overflow: hidden; }
  .grid-head .tr { justify-content: flex-end; }
  .grid-head .col-ord { cursor: pointer; user-select: none; }
  .grid-head .col-ord:hover { color: #2b6cb0; }
  .grid-head .col-ord .fa { margin-left: 4px; color: #2b6cb0; }
  .grid-head .col-ord .ord-inativo { color: #c5ccd3; }
  .grid-row { border-bottom: 1px solid #eef1f4; cursor: pointer; }
  .grid-row:hover { filter: brightness(.98); }
  .grid-row > div { display: flex; align-items: center; padding: 9px; font-size: 12.5px; min-height: 42px; }
  .grid-row .muted { color: #6b747e; }
  .grid-row .nowrap { white-space: nowrap; }
  .grid-row .valor { justify-content: flex-end; text-align: right; font-weight: 600; white-space: nowrap; }
  .grid-row .valor.neg { color: #a13c3c; }
  .grid-row .valor.pos { color: #0a6d4d; }
  .grid-row .tr { justify-content: flex-end; text-align: right; }
  .clamp2 { align-items: center; }
  .hist-compl { color: #8a939c; }
  .tag-grupo-ext { display: inline-flex; align-items: center; gap: 3px; margin-right: 5px; padding: 0 6px; font-size: 10.5px; font-weight: 700; line-height: 16px; border: 1px solid; border-radius: 8px; vertical-align: 1px; white-space: nowrap; }
  .tag-grupo-ext .fa { font-size: 10px; }
  .c-info .ic-reg.ic-reg-grupo { color: #c48a12 !important; }
  .clamp2-in { line-height: 1.35; max-height: 2.7em; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; }
  .obs-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .obs-txt { flex: 1 1 auto; min-width: 0; color: #6b747e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .obs-cell .ic { flex: 0 0 auto; color: #9aa3ac; font-size: 14px; }
  .obs-cell .ic:hover { color: #5b6570; }
  .aviso-teto { display: flex; align-items: flex-start; gap: 9px; background: #fdf6e3; border-bottom: 1px solid #f0e2b8; padding: 9px 16px; font-size: 12.5px; color: #8a6d3b; line-height: 1.45; }
  .aviso-teto .fa { margin-top: 2px; flex: 0 0 auto; }
  .aviso-teto b { color: #6f5620; }
  .aviso-teto span { flex: 1 1 auto; }
  .aviso-mais { flex: 0 0 auto; background: #fff; border: 1px solid #e0cf9a; color: #6f5620; border-radius: 5px; padding: 4px 11px; font-size: 12px; font-weight: 600; }
  .aviso-mais:hover:not(:disabled) { background: #fbf1d8; }
  .aviso-mais:disabled { opacity: .55; }
  .aviso-teto.aviso-retro { background: #eef5fc; border-bottom-color: #cfe0f2; color: #2c5578; align-items: center; }
  .aviso-teto.aviso-retro .fa { margin-top: 0; }
  .aviso-retro b { color: #1f4466; }
  .aviso-retro .aviso-mais { border-color: #b9d1ea; color: #1f4466; }
  .aviso-retro .aviso-mais:hover:not(:disabled) { background: #e2eefa; }
  .data-retro { margin-left: 5px; color: #2d6cb3; font-size: 11px; cursor: help; }
  .c-info .ic-reg { font-size: 16px !important; color: #b6bdc5 !important; }
  .c-info .ic-reg:hover { color: #0f8a5f !important; background: #e8f6ef; }
  .cel-busca { gap: 4px; min-width: 0; }
  .cel-busca .cel-txt { flex: 0 0 auto; white-space: nowrap; }
  .cel-busca .clamp2-in { flex: 1 1 auto; min-width: 0; }
  .cel-busca .ic-busca:first-of-type { margin-left: auto; }
  .cel-busca-ini .ic-busca:first-of-type { margin-left: 0; }
  .cel-busca-ini .cel-txt { margin-left: 2px; }
  .cel-busca-ini .clamp2-in { margin-left: 2px; }
  .cel-busca-ini .ic-busca { opacity: 1; }
  .ic-busca { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 4px;
              font-size: 13px; color: #9aa3ac; background: transparent; border: 1px solid transparent; opacity: 0;
              transition: opacity .12s, background .12s, color .12s, border-color .12s; }
  .grid-row:hover .ic-busca { opacity: 1; }
  .ic-busca:hover { color: #fff; background: #0f8a5f; border-color: #0b6c4a; }
  .ic-busca-mais { color: #c98a3c; }
  .ic-busca-mais:hover { color: #fff; background: #c98a3c; border-color: #a75f14; }
  .ic-busca.ativo { opacity: 1; color: #fff; background: #0f8a5f; border-color: #0b6c4a; box-shadow: 0 0 0 2px rgba(15,138,95,.22); }
  .ic-busca.ativo:hover { background: #0b6c4a; }
  .ic-busca-mais.ativo { background: #c98a3c; border-color: #a75f14; box-shadow: 0 0 0 2px rgba(201,138,60,.22); }
  .ic-busca-mais.ativo:hover { background: #a75f14; }
  .cel-valor { gap: 7px; }
  .cel-valor .cel-txt { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
  .ic-valor { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 19px; height: 19px; border-radius: 4px;
              font-size: 11px; color: #9aa3ac; background: transparent; border: 1px solid transparent; opacity: 1; transition: opacity .12s, background .12s, color .12s; }
  .grid-row:hover .ic-valor { opacity: 1; }
  .ic-valor:hover { color: #fff; background: #d97e26; border-color: #b96613; }
  .ic-valor.ativo { opacity: 1; color: #fff; background: #d97e26; border-color: #a75f14; box-shadow: 0 0 0 2px rgba(217,126,38,.22); }
  .ic-valor.ativo:hover { background: #a75f14; }
  .grid-row .cel-valor-on { background: #fdf3e7; }
  .doc-banco { gap: 6px; min-width: 0; }
  .doc-banco > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dica-banco { flex: 0 0 auto; font-size: 11px; color: #2f7d4f; cursor: help; }
  .dica-banco.dica-parcial { color: #c07d17; }
  .grid-vazio { padding: 40px; text-align: center; color: #8a939c; font-size: 13px; }
  .grid-mais { flex: none; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 10px;
               padding: 10px; font-size: 12px; color: #6b747e; background: #f7f9fa; border-top: 1px solid #eef1f4; }
  .grid-mais button { border: 1px solid #c6ccd4; background: #fff; color: #3b444d; border-radius: 5px; padding: 4px 12px; font-size: 12px; }
  .grid-mais button:hover { background: #eef1f4; }
  .grid-mais .btn-servidor { border-color: #b9d3c6; background: #f2f9f5; color: #0a6d4d; font-weight: 600; }
  .grid-mais .btn-servidor:hover:not(:disabled) { background: #e4f2ea; }
  .grid-mais .btn-servidor:disabled { opacity: .55; }

  .c-ind { display: flex; align-items: center; }
  .circ { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid #a9b2bb; background: #fff; display: inline-flex; align-items: center; justify-content: center; }
  .circ-sel { border-color: #1f5fa8; background: #2d6cb3; }
  .circ-cand { border-color: #d4ab2c; background: #e0b429; }
  .circ-sug { border-color: #b9a5d4; border-style: dashed; background: #fff; }
  .circ-auto { border-color: #6cb7c9; background: #159bb3; }
  .circ-manual { border-color: #93c7a2; background: #0f8a5f; }
  .circ-vinculo { border-color: #d4ab2c; background: #e0b429; }
  .circ-just { border-color: #93c7a2; background: #0f8a5f; }
  .circ-pend { border-color: #a9b2bb; background: #fff; }

  .grid-row.row-cr-outra { background: #fdf3e7; box-shadow: inset 3px 0 0 #d97e26; }
  .grid-row.st-sel { background: #bfd9f7; box-shadow: inset 4px 0 0 #2d6cb3; }
  .grid-row.st-cand { background: #fdf0c4; }
  .grid-row.st-sug { background: #f6f2fc; box-shadow: inset 3px 0 0 #b9a5d4; }
  .grid-row.st-vinculo { background: #fdf0c4; }
  .grid-row.st-auto { background: #e4f2f6; }
  .grid-row.st-manual { background: #e5f2ea; }
  .grid-row.st-just { background: #e5f2ea; }
  .grid-row.st-auto, .grid-row.st-manual, .grid-row.st-just, .grid-row.st-vinculo { cursor: default; }
</style>



// WEBPACK FOOTER //
// FrontEnd/modulos/conciliacao-projeto/componentes/ConciliacaoTela.vue